import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { servicioActualizacionDiaria } from "./servicios/actualizacionDiaria";
import { actualizarPresupuestoTotalInicial, actualizarPresupuestoTotalActualizado, recalcularPresupuestosInicialesActualizados, sincronizarDashboard, actualizarYSincronizarProyecto, dispararActualizacionEnSegundoPlano } from "./servicios/actualizarPresupuestosTotales";
import {
  sincronizarPresupuestosProyecto,
  sincronizarTodosLosProyectosConstructora,
} from "./servicios/actualizacionPresupuestos";
import {
  obtenerPermisosCliente,
  puedeAccederAVista,
  obtenerMensajeRestriccion,
} from "./permisos";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Wallet,
  Building2,
  Users,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  DollarSign,
  FileText,
  User,
  Clock,
  MapPin,
  Target,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Info,
  Layers,
  ChevronDown,
  ChevronRight,
  Settings,
  Receipt,
  Package,
  Briefcase,
  Calculator,
  PieChart,
  Activity,
  Zap,
  ArrowUpDown,
  Link,
  Unlink,
  MoreHorizontal,
  ChevronUp,
  Check,
  ArrowUp,
  ArrowDown,
  TrendingUpIcon,
  Award,
  Tag,
} from "lucide-react";

// Configuración anti-zoom
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
}

// Colores por etapa mejorados
const ETAPA_COLORS = [
  {
    gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    solid: "#2563eb",
    light: "#e0e7ff",
    subEtapa: "#dbeafe",
    hover: "#2563eb",
  },
  {
    gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    solid: "#10b981",
    light: "#ecfdf5",
    subEtapa: "#d1fae5",
    hover: "#059669",
  },
  {
    gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    solid: "#f59e0b",
    light: "#fffbeb",
    subEtapa: "#fef3c7",
    hover: "#d97706",
  },
  {
    gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    solid: "#ef4444",
    light: "#fef2f2",
    subEtapa: "#fecaca",
    hover: "#dc2626",
  },
  {
    gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
    solid: "#8b5cf6",
    light: "#f3e8ff",
    subEtapa: "#e9d5ff",
    hover: "#7c3aed",
  },
  {
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    solid: "#06b6d4",
    light: "#ecfeff",
    subEtapa: "#cffafe",
    hover: "#0891b2",
  },
  {
    gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)",
    solid: "#ec4899",
    light: "#fdf2f8",
    subEtapa: "#fbcfe8",
    hover: "#db2777",
  },
  {
    gradient: "linear-gradient(135deg, #84cc16 0%, #65a30d 100%)",
    solid: "#84cc16",
    light: "#f7fee7",
    subEtapa: "#ecfccb",
    hover: "#65a30d",
  },
];

// Colores especiales para honorarios y MO
const SPECIAL_COLORS = {
  honorarios: {
    gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    solid: "#2563eb",
    light: "#e0e7ff",
    subEtapa: "#dbeafe",
    hover: "#2563eb",
  },
  mano_obra: {
    gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    solid: "#06b6d4",
    light: "#ecfeff",
    subEtapa: "#cffafe",
    hover: "#0891b2",
  },
};

// Colores por vista
const VISTA_COLORS = {
  "presupuestos-iniciales": {
    gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
    solid: "#2563eb",
    light: "#2563eb10",
    description: "Presupuesto inicial de obra",
  },
};

const getVistaColor = (vistaId) => {
  return VISTA_COLORS[vistaId] || VISTA_COLORS["presupuestos-iniciales"];
};

// CustomDropdown mejorado con búsqueda y z-index fijo
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
  onSearch,
  canCreate = false,
  onCreateNew,
  createText = "Crear nuevo",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const filtered = options.filter((option) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredOptions(filtered);
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "14px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onFocus={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#2563eb";
            e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
          }
        }}
        onBlur={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#f1f5f9";
            e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
          }
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 9999,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 20px 50px -10px rgba(0, 0, 0, 0.25)",
            marginTop: "4px",
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              padding: "8px",
              borderBottom: "1px solid #f1f5f9",
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 10000,
            }}
          >
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                touchAction: "manipulation",
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  canCreate &&
                  filteredOptions.length === 0 &&
                  searchTerm.trim()
                ) {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }
              }}
            />
          </div>

          <div style={{ maxHeight: "240px", overflowY: "auto" }}>
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                  setSearchTerm("");
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "none",
                  background:
                    option.value === value ? "#eff6ff" : "transparent",
                  color: option.value === value ? "#2563eb" : "#374151",
                  fontSize: "16px",
                  fontWeight: option.value === value ? "600" : "400",
                  textAlign: "left",
                  cursor: "pointer",
                  transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onMouseEnter={(e) => {
                  if (option.value !== value) {
                    e.target.style.backgroundColor = "#f8fafc";
                  }
                }}
                onMouseLeave={(e) => {
                  if (option.value !== value) {
                    e.target.style.backgroundColor = "transparent";
                  }
                }}
              >
                {option.label}
              </button>
            ))}

            {canCreate && searchTerm.trim() && filteredOptions.length === 0 && (
              <button
                onClick={() => {
                  onCreateNew?.(searchTerm.trim());
                  setSearchTerm("");
                  setIsOpen(false);
                }}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "none",
                  background: "#f0fdf4",
                  color: "#166534",
                  fontSize: "14px",
                  fontWeight: "600",
                  textAlign: "left",
                  cursor: "pointer",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  touchAction: "manipulation",
                }}
              >
                <Plus size={14} />
                {createText}: "{searchTerm}"
              </button>
            )}

            {!canCreate && filteredOptions.length === 0 && searchTerm && (
              <div
                style={{
                  padding: "16px",
                  textAlign: "center",
                  color: "#64748b",
                  fontSize: "14px",
                }}
              >
                No se encontraron resultados
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Modal personalizado para alertas/confirmaciones
function CustomModal({
  isOpen,
  onClose,
  type = "info",
  title,
  message,
  onConfirm,
  confirmText = "Aceptar",
  cancelText = "Cancelar",
  isMobile,
}) {
  if (!isOpen) return null;

  const getColors = () => {
    switch (type) {
      case "confirm":
        return {
          bg: "#fef3cd",
          border: "#f59e0b",
          text: "#92400e",
          button: "#f59e0b",
        };
      case "error":
        return {
          bg: "#fef2f2",
          border: "#ef4444",
          text: "#dc2626",
          button: "#ef4444",
        };
      case "success":
        return {
          bg: "#f0fdf4",
          border: "#22c55e",
          text: "#166534",
          button: "#22c55e",
        };
      default:
        return {
          bg: "#f0f9ff",
          border: "#2563eb",
          text: "#1d4ed8",
          button: "#2563eb",
        };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          width: "100%",
          maxWidth: isMobile ? "90%" : "400px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          border: `2px solid ${colors.border}`,
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 16px",
              borderRadius: "32px",
              background: colors.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {type === "confirm" && (
              <AlertCircle size={32} color={colors.text} />
            )}
            {type === "error" && <XCircle size={32} color={colors.text} />}
            {type === "success" && (
              <CheckCircle2 size={32} color={colors.text} />
            )}
            {type === "info" && <Info size={32} color={colors.text} />}
          </div>
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: "600",
              color: colors.text,
            }}
          >
            {title}
          </h3>
          <p
            style={{
              margin: 0,
              fontSize: "14px",
              color: "#64748b",
              lineHeight: "1.4",
            }}
          >
            {message}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: type === "confirm" ? "space-between" : "center",
          }}
        >
          {type === "confirm" && (
            <button
              onClick={onClose}
              style={{
                flex: 1,
                padding: "12px 20px",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                background: "white",
                color: "#64748b",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              className="button-hover"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={() => {
              if (type === "confirm" && onConfirm) {
                onConfirm();
              }
              onClose();
            }}
            style={{
              flex: type === "confirm" ? 1 : "auto",
              padding: "12px 20px",
              border: "none",
              borderRadius: "8px",
              background: colors.button,
              color: "white",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              minWidth: type !== "confirm" ? "120px" : "auto",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            className="button-hover"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Tooltip personalizado mejorado
const CustomTooltip = ({ isVisible, x, y, content }) => {
  if (!isVisible || !content) return null;

  return (
    <div
      style={{
        position: "fixed",
        left: x + 10,
        top: y - 10,
        zIndex: 9999,
        background: "rgba(0, 0, 0, 0.95)",
        color: "white",
        padding: "16px 20px",
        borderRadius: "12px",
        fontSize: "12px",
        lineHeight: "1.5",
        maxWidth: "320px",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.4)",
        pointerEvents: "none",
        transform: "translateY(-100%)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <div style={{ fontWeight: "700", marginBottom: "8px", fontSize: "13px" }}>
        {content.nombre}
      </div>

      {content.descripcion && (
        <div
          style={{ marginBottom: "8px", color: "#e2e8f0", fontSize: "11px" }}
        >
          {content.descripcion}
        </div>
      )}

      <div style={{ display: "grid", gap: "4px", fontSize: "11px" }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#a3a3a3" }}>Presupuestado:</span>
          <span style={{ color: "#10b981", fontWeight: "600" }}>
            {content.presupuestado}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#a3a3a3" }}>Gastado:</span>
          <span style={{ color: "#ef4444", fontWeight: "600" }}>
            {content.gastado}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#a3a3a3" }}>Progreso:</span>
          <span style={{ color: "#2563eb", fontWeight: "600" }}>
            {content.porcentaje}%
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ color: "#a3a3a3" }}>Saldo:</span>
          <span
            style={{
              color:
                parseFloat(content.saldo.replace(/[^0-9.-]/g, "")) >= 0
                  ? "#f59e0b"
                  : "#ef4444",
              fontWeight: "600",
            }}
          >
            {content.saldo}
          </span>
        </div>

        {content.estado && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "4px",
              paddingTop: "4px",
              borderTop: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <span style={{ color: "#a3a3a3" }}>Estado:</span>
            <span
              style={{
                color: content.cerrado ? "#10b981" : "#f59e0b",
                fontWeight: "600",
              }}
            >
              {content.estado}
            </span>
          </div>
        )}

        {content.fecha && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#a3a3a3" }}>Creado:</span>
            <span style={{ color: "#d1d5db" }}>{content.fecha}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Funciones helper para manejar fechas
const formatDateForInput = (dateString) => {
  if (!dateString) return new Date().toISOString().split("T")[0];
  if (dateString.includes("T")) {
    return dateString.split("T")[0];
  }
  return dateString;
};

const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};
// Modal para ver información del presupuesto (para clientes)
// NUEVA BARRA DE PROGRESO MINIMALISTA REDISEÑADA
const BarraProgresoMinimalista = ({
  item,
  tipo,
  color,
  isEtapa = false,
  onToggleEstado,
  onEdit,
  onDelete,
  onRestore,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  formatearMoneda,
  isMobile,
  permisosCliente,
  monedaVista,
  convertirMonto,
  onViewInfo,
  cotizacionDolar, // NUEVO PARÁMETRO
  filterVigencia,
}) => {
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    content: null,
  });
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = (e) => {
    if (isMobile) return; // No tooltips en móvil

    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      content: {
        nombre: item.nombre,
        descripcion: item.descripcion,
        presupuestado: formatearMoneda(
          convertirMonto(
            item.monto_actualizado ||
              item.monto_presupuestado ||
              item.monto_inicial,
            item.moneda,
            monedaVista,
            cotizacionDolar
          ),
          monedaVista
        ),
        gastado: formatearMoneda(
          convertirMonto(
            item.total_gastado || item.total_pagado,
            item.moneda,
            monedaVista,
            cotizacionDolar
          ),
          monedaVista
        ),
        porcentaje: (
          item.porcentaje_ejecutado ||
          item.porcentaje_pagado ||
          0
        ).toFixed(1),
        saldo: formatearMoneda(
          convertirMonto(
            item.saldo_actualizado || item.saldo_pendiente,
            item.moneda,
            monedaVista,
            cotizacionDolar
          ),
          monedaVista
        ),
        estado: item.estado === "cerrado" ? "Cerrado" : "Abierto",
        fecha: item.created_at
          ? new Date(item.created_at).toLocaleDateString()
          : null,
        cerrado: item.estado === "cerrado",
      },
    });
  };

  const handleMouseMove = (e) => {
    if (tooltip.visible && !isMobile) {
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX,
        y: e.clientY,
      }));
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setTooltip({ visible: false, x: 0, y: 0, content: null });
  };

  const handleClick = () => {
    if (onViewInfo) {
      onViewInfo(item);
    } else if (!isEtapa && !permisosCliente.esCliente && onEdit) {
      onEdit(item);
    }
  };

  const porcentaje = item.porcentaje_ejecutado || item.porcentaje_pagado || 0;
  const estaCerrado = item.estado === "cerrado";

  // Usar valores actualizados si están disponibles
  const montoPresupuestado =
    item.monto_actualizado ||
    item.monto_presupuestado ||
    item.monto_inicial ||
    0;
  const totalGastado = item.total_gastado || item.total_pagado || 0;
  const saldoPendiente = item.saldo_actualizado || item.saldo_pendiente || 0;

  // Cálculo de ahorro/sobregasto usando valores actualizados
  const sobregasto =
    totalGastado > montoPresupuestado ? totalGastado - montoPresupuestado : 0;
  const ahorro =
    estaCerrado && montoPresupuestado > totalGastado
      ? montoPresupuestado - totalGastado
      : 0;

  // Determinar color de la barra
  let barColor = color.solid;
  if (sobregasto > 0) {
    barColor = "#ef4444"; // Rojo para sobregasto
  } else if (ahorro > 0) {
    barColor = "#10b981"; // Verde para ahorro
  } else if (porcentaje >= 90) {
    barColor = "#f59e0b"; // Naranja si está cerca del límite
  }

  if (isEtapa) {
    // RENDERIZADO PARA ETAPAS (headers principales)
    return (
      <>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            background: "#f8fafc",
            borderRadius: "16px",
            padding: isMobile ? "20px" : "24px",
            marginBottom: "16px",
            border: "1px solid #e2e8f0",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor: "default",
          }}
        >
          {/* Header de la etapa */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              {/* Ícono de la etapa */}
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  background: color.gradient,
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `0 4px 16px ${color.light}`,
                }}
              >
                <Layers size={18} color="white" />
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "18px" : "20px",
                    fontWeight: "700",
                    color: "#0f172a",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.nombre}
                  {item.presupuestos && ` (${item.presupuestos.length})`}
                </h3>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Totales de la etapa */}
              <div style={{ textAlign: "right", marginRight: "16px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    marginBottom: "4px",
                  }}
                >
                  {formatearMoneda(
                    convertirMonto(
                      totalGastado,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}{" "}
                  /{" "}
                  {formatearMoneda(
                    convertirMonto(
                      montoPresupuestado,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
                <div
                  style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    color: saldoPendiente >= 0 ? "#10b981" : "#ef4444",
                  }}
                >
                  {saldoPendiente >= 0 ? "+" : ""}
                  {formatearMoneda(
                    convertirMonto(
                      saldoPendiente,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              </div>

              {/* Controles de orden - Solo si NO es cliente */}
              {tipo === "etapa" && !isMobile && !permisosCliente.esCliente && (
                <div style={{ display: "flex", gap: "4px" }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveUp();
                    }}
                    disabled={!canMoveUp}
                    style={{
                      background: canMoveUp
                        ? "rgba(241, 245, 249, 0.8)"
                        : "rgba(248, 250, 252, 0.5)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px",
                      cursor: canMoveUp ? "pointer" : "not-allowed",
                      opacity: canMoveUp ? 1 : 0.5,
                      transition: "all 0.2s ease",
                    }}
                    title="Subir"
                  >
                    <ArrowUp size={14} color="#64748b" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onMoveDown();
                    }}
                    disabled={!canMoveDown}
                    style={{
                      background: canMoveDown
                        ? "rgba(241, 245, 249, 0.8)"
                        : "rgba(248, 250, 252, 0.5)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "6px",
                      cursor: canMoveDown ? "pointer" : "not-allowed",
                      opacity: canMoveDown ? 1 : 0.5,
                      transition: "all 0.2s ease",
                    }}
                    title="Bajar"
                  >
                    <ArrowDown size={14} color="#64748b" />
                  </button>
                </div>
              )}

              {/* Porcentaje destacado */}
              <div
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  textAlign: "center",
                  minWidth: "80px",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                  border: "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: barColor,
                    marginBottom: "2px",
                  }}
                >
                  {porcentaje.toFixed(1)}%
                </div>
                <div
                  style={{
                    fontSize: "10px",
                    color: "#64748b",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Progreso
                </div>
              </div>
            </div>
          </div>

          {/* Barra de progreso de la etapa */}
          <div
            style={{
              background: "#e2e8f0",
              height: "12px",
              borderRadius: "6px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: "100%",
                background: color.gradient,
                width: `${Math.min(porcentaje, 100)}%`,
                borderRadius: "6px",
                transition: "width 1s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
                boxShadow:
                  porcentaje > 0 ? `0 0 12px ${color.solid}40` : "none",
              }}
            >
              {/* Efecto de brillo */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, transparent 100%)",
                  borderRadius: "6px 6px 0 0",
                }}
              />
            </div>
          </div>

          {/* Indicadores de ahorro/sobregasto */}
          {(ahorro > 0 || sobregasto > 0) && (
            <div
              style={{
                marginTop: "12px",
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {ahorro > 0 && (
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    fontSize: "11px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Award size={12} />
                  Ahorro:{" "}
                  {formatearMoneda(
                    convertirMonto(
                      ahorro,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              )}
              {sobregasto > 0 && (
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    color: "white",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    fontSize: "11px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <AlertCircle size={12} />
                  Sobregasto:{" "}
                  {formatearMoneda(
                    convertirMonto(
                      sobregasto,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <CustomTooltip {...tooltip} />
      </>
    );
  } else {
    // RENDERIZADO PARA SUBETAPAS (barras minimalistas)
    return (
      <>
        <div
          onMouseEnter={handleMouseEnter}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          style={{
            background: "white",
            borderRadius: "12px",
            padding: isMobile ? "16px" : "18px",
            marginBottom: "8px",
            border: `1px solid ${isHovered ? color.solid : "#f1f5f9"}`,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            cursor:
              permisosCliente.esCliente || !permisosCliente.esCliente
                ? "pointer"
                : "default",
            boxShadow: isHovered
              ? `0 4px 20px ${color.light}`
              : "0 2px 8px rgba(0,0,0,0.04)",
            transform: isHovered ? "translateY(-1px)" : "translateY(0)",
          }}
        >
          {/* Header de la subetapa */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flex: 1,
              }}
            >
              {/* Indicador de estado */}
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  background: estaCerrado ? "#10b981" : color.solid,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {estaCerrado ? (
                  <Check size={12} color="white" />
                ) : (
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      background: "white",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <h4
                  style={{
                    margin: 0,
                    fontSize: isMobile ? "15px" : "16px",
                    fontWeight: "600",
                    color: "#1e293b",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.nombre}
                </h4>
                
                {/* Info adicional: Etapa → Subetapa */}
                {item.etapas_obra?.nombre && item.subetapas_obra?.nombre && (
                  <div style={{ 
                    fontSize: "12px", 
                    color: "#64748b", 
                    marginTop: "2px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}>
                    {item.etapas_obra?.nombre} → {item.subetapas_obra?.nombre}
                  </div>
                )}

                {/* NUEVO: Mostrar original vs actualizado */}
                {item.monto_actualizado &&
                  Math.abs(item.monto_actualizado - item.monto_presupuestado) >
                    0.01 && (
                    <p
                      style={{
                        margin: "4px 0 0 0",
                        fontSize: "12px",
                        color: "#10b981",
                        fontWeight: "500",
                      }}
                    >
                      Original:{" "}
                      {formatearMoneda(item.monto_presupuestado, item.moneda)} →
                      Actualizado CAC:{" "}
                      {formatearMoneda(item.monto_actualizado, item.moneda)}
                    </p>
                  )}
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Montos */}
              <div
                style={{
                  textAlign: "right",
                  fontSize: "12px",
                  color: "#64748b",
                }}
              >
                <div style={{ marginBottom: "2px" }}>
                  <span style={{ color: "#ef4444", fontWeight: "600" }}>
                    {formatearMoneda(
                      convertirMonto(
                        totalGastado,
                        item.moneda,
                        monedaVista,
                        cotizacionDolar
                      ),
                      monedaVista
                    )}
                  </span>
                  <span style={{ margin: "0 4px" }}>/</span>
                  <span style={{ color: "#10b981", fontWeight: "600" }}>
                    {formatearMoneda(
                      convertirMonto(
                        montoPresupuestado,
                        item.moneda,
                        monedaVista,
                        cotizacionDolar
                      ),
                      monedaVista
                    )}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: "11px",
                    color: saldoPendiente >= 0 ? "#10b981" : "#ef4444",
                    fontWeight: "600",
                  }}
                >
                  Saldo: {saldoPendiente >= 0 ? "+" : ""}
                  {formatearMoneda(
                    convertirMonto(
                      saldoPendiente,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              </div>

              {/* Controles - Solo si NO es cliente */}
              {!permisosCliente.esCliente && (
                <div style={{ display: "flex", gap: "4px" }}>
                  {/* Toggle de estado */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleEstado(item);
                    }}
                    style={{
                      background: estaCerrado
                        ? "#10b981"
                        : "rgba(241, 245, 249, 0.8)",
                      border: estaCerrado ? "none" : "1px solid #e2e8f0",
                      borderRadius: "6px",
                      padding: "4px 8px",
                      cursor: "pointer",
                      color: estaCerrado ? "white" : "#64748b",
                      fontSize: "10px",
                      fontWeight: "600",
                      transition: "all 0.2s ease",
                    }}
                    title={
                      estaCerrado ? "Reabrir presupuesto" : "Cerrar presupuesto"
                    }
                  >
                    {estaCerrado ? "Cerrado" : "Cerrar"}
                  </button>

                  {/* Ver información */}
                  {onViewInfo && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewInfo(item);
                      }}
                      style={{
                        background: "rgba(16, 185, 129, 0.1)",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 6px",
                        cursor: "pointer",
                        color: "#10b981",
                        transition: "all 0.2s ease",
                      }}
                      title="Ver información"
                    >
                      <Eye size={12} />
                    </button>
                  )}

                  {/* Editar: solo si filtro está en activos */}
                  {onEdit && filterVigencia === 'activos' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(item);
                      }}
                      style={{
                        background: "rgba(59, 130, 246, 0.1)",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 6px",
                        cursor: "pointer",
                        color: "#3b82f6",
                        transition: "all 0.2s ease",
                      }}
                      title="Editar"
                    >
                      <Edit size={12} />
                    </button>
                  )}

                  {/* Eliminar/Restaurar según filtro */}
                  {onDelete && filterVigencia === 'activos' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(item.id);
                      }}
                      style={{
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 6px",
                        cursor: "pointer",
                        color: "#ef4444",
                        transition: "all 0.2s ease",
                      }}
                      title="Eliminar"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                  {filterVigencia === 'eliminados' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRestore(item.id);
                      }}
                      style={{
                        background: "rgba(34, 197, 94, 0.1)",
                        border: "none",
                        borderRadius: "6px",
                        padding: "4px 6px",
                        cursor: "pointer",
                        color: "#16a34a",
                        transition: "all 0.2s ease",
                      }}
                      title="Restaurar"
                    >
                      <Package size={12} />
                    </button>
                  )}
                </div>
              )}

              {/* Porcentaje */}
              <div
                style={{
                  background: "white",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  textAlign: "center",
                  minWidth: "50px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  border: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: barColor,
                  }}
                >
                  {porcentaje.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Barra de progreso minimalista */}
          <div
            style={{
              background: "#f1f5f9",
              height: "8px",
              borderRadius: "4px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: "100%",
                background: `linear-gradient(135deg, ${barColor} 0%, ${barColor}dd 100%)`,
                width: `${Math.min(porcentaje, 100)}%`,
                borderRadius: "4px",
                transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                position: "relative",
              }}
            >
              {/* Efecto de brillo sutil */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "50%",
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)",
                  borderRadius: "4px 4px 0 0",
                }}
              />
            </div>

            {/* Indicador de sobregasto en la barra */}
            {porcentaje > 100 && (
              <div
                style={{
                  position: "absolute",
                  right: "4px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "8px",
                  color: "white",
                  fontWeight: "700",
                }}
              >
                +{(porcentaje - 100).toFixed(1)}%
              </div>
            )}
          </div>

          {/* Indicadores de ahorro/sobregasto */}
          {(ahorro > 0 || sobregasto > 0) && (
            <div
              style={{
                marginTop: "8px",
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
              }}
            >
              {ahorro > 0 && (
                <div
                  style={{
                    background: "#f0fdf4",
                    color: "#059669",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "3px",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <Award size={10} />
                  Ahorro:{" "}
                  {formatearMoneda(
                    convertirMonto(
                      ahorro,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              )}
              {sobregasto > 0 && (
                <div
                  style={{
                    background: "#fef2f2",
                    color: "#dc2626",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "600",
                    display: "flex",
                    alignItems: "center",
                    gap: "3px",
                    border: "1px solid #fecaca",
                  }}
                >
                  <AlertCircle size={10} />+
                  {formatearMoneda(
                    convertirMonto(
                      sobregasto,
                      item.moneda,
                      monedaVista,
                      cotizacionDolar
                    ),
                    monedaVista
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <CustomTooltip {...tooltip} />
      </>
    );
  }
};

// COMPONENTE DE ETAPA COLAPSABLE
const EtapaColapsable = ({
  etapa,
  index,
  totalEtapas,
  onMoveEtapa,
  onToggleEstado,
  onEdit,
  onDelete,
  onRestore,
  onViewInfo,
  formatearMoneda,
  isMobile,
  permisosCliente,
  monedaVista,
  convertirMonto,
  cotizacionDolar, // NUEVO PARÁMETRO
  filterVigencia,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div
      style={{
        marginBottom: index < totalEtapas - 1 ? "24px" : "0",
        animation: `slideInRight 0.${5 + index}s cubic-bezier(0.4, 0, 0.2, 1)`,
      }}
    >
      {/* Header de la etapa con botón de colapso */}
      <div
        onClick={toggleExpanded}
        style={{
          display: "flex",
          alignItems: "center",
          background: "#f8fafc",
          borderRadius: "16px",
          padding: isMobile ? "20px" : "24px",
          marginBottom: isExpanded ? "12px" : "0",
          border: "1px solid #e2e8f0",
          cursor: "pointer",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
        }}
        onMouseEnter={(e) => {
          e.target.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
        }}
        onMouseLeave={(e) => {
          e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
        }}
      >
        {/* Botón de colapso */}
        <div
          style={{
            width: "32px",
            height: "32px",
            background: etapa.color.gradient,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: "16px",
            transition: "transform 0.3s ease",
            transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
          }}
        >
          <ChevronDown size={16} color="white" />
        </div>

        {/* Información de la etapa */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "4px",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
                color: "#0f172a",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {etapa.nombre}
            </h3>
            <span
              style={{
                background: etapa.color.light,
                color: etapa.color.solid,
                padding: "2px 8px",
                borderRadius: "6px",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {etapa.presupuestos?.length || 0} items
            </span>
          </div>

          {/* Línea de totales con valores actualizados */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              fontSize: "13px",
            }}
          >
            <span style={{ color: "#64748b" }}>
              <span style={{ fontWeight: "600", color: "#10b981" }}>
                {formatearMoneda(
                  convertirMonto(
                    etapa.monto_total_actualizado || etapa.monto_total,
                    etapa.moneda || "ARS",
                    monedaVista,
                    cotizacionDolar
                  ),
                  monedaVista
                )}
              </span>
              {" presupuestado"}
            </span>
            <span style={{ color: "#64748b" }}>
              <span style={{ fontWeight: "600", color: "#ef4444" }}>
                {formatearMoneda(
                  convertirMonto(
                    etapa.total_gastado,
                    etapa.moneda || "ARS",
                    monedaVista,
                    cotizacionDolar
                  ),
                  monedaVista
                )}
              </span>
              {" gastado"}
            </span>
            <span style={{ color: "#64748b" }}>
              <span
                style={{
                  fontWeight: "600",
                  color:
                    (etapa.saldo_actualizado || etapa.saldo_pendiente) >= 0
                      ? "#f59e0b"
                      : "#ef4444",
                }}
              >
                {(etapa.saldo_actualizado || etapa.saldo_pendiente) >= 0
                  ? "+"
                  : ""}
                {formatearMoneda(
                  convertirMonto(
                    etapa.saldo_actualizado || etapa.saldo_pendiente,
                    etapa.moneda || "ARS",
                    monedaVista,
                    cotizacionDolar
                  ),
                  monedaVista
                )}
              </span>
              {" saldo"}
            </span>
          </div>
        </div>

        {/* Controles de la etapa */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {/* Controles de orden - Solo si NO es cliente */}
          {!isMobile && !permisosCliente.esCliente && (
            <div style={{ display: "flex", gap: "4px" }}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveEtapa(etapa.id, "up");
                }}
                disabled={index === 0}
                style={{
                  background:
                    index > 0
                      ? "rgba(241, 245, 249, 0.8)"
                      : "rgba(248, 250, 252, 0.5)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px",
                  cursor: index > 0 ? "pointer" : "not-allowed",
                  opacity: index > 0 ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
                title="Subir"
              >
                <ArrowUp size={14} color="#64748b" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveEtapa(etapa.id, "down");
                }}
                disabled={index === totalEtapas - 1}
                style={{
                  background:
                    index < totalEtapas - 1
                      ? "rgba(241, 245, 249, 0.8)"
                      : "rgba(248, 250, 252, 0.5)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px",
                  cursor: index < totalEtapas - 1 ? "pointer" : "not-allowed",
                  opacity: index < totalEtapas - 1 ? 1 : 0.5,
                  transition: "all 0.2s ease",
                }}
                title="Bajar"
              >
                <ArrowDown size={14} color="#64748b" />
              </button>
            </div>
          )}

          {/* Barra de progreso pequeña */}
          <div style={{ width: "80px", textAlign: "center" }}>
            <div
              style={{
                fontSize: "16px",
                fontWeight: "700",
                color: etapa.color.solid,
                marginBottom: "4px",
              }}
            >
              {etapa.porcentaje_ejecutado?.toFixed(1) || "0.0"}%
            </div>
            <div
              style={{
                background: "#e2e8f0",
                height: "6px",
                borderRadius: "3px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  background: etapa.color.gradient,
                  width: `${Math.min(etapa.porcentaje_ejecutado || 0, 100)}%`,
                  borderRadius: "3px",
                  transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Subetapas colapsables */}
      <div
        style={{
          overflow: "hidden",
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          maxHeight: isExpanded ? "2000px" : "0",
          opacity: isExpanded ? 1 : 0,
          transform: isExpanded ? "translateY(0)" : "translateY(-10px)",
        }}
      >
        {etapa.presupuestos && etapa.presupuestos.length > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "16px" : "20px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "1px solid #f1f5f9",
            }}
          >
              {etapa.presupuestos.map((presupuesto, subIndex) => (
                <BarraProgresoMinimalista
                  key={presupuesto.id}
                  item={presupuesto}
                  tipo="subetapa"
                  color={etapa.color}
                  isEtapa={false}
                  onToggleEstado={onToggleEstado}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  onRestore={onRestore}
                  onViewInfo={onViewInfo}
                  formatearMoneda={formatearMoneda}
                  isMobile={isMobile}
                  permisosCliente={permisosCliente}
                  monedaVista={monedaVista}
                  convertirMonto={convertirMonto}
                  cotizacionDolar={cotizacionDolar} // NUEVO PARÁMETRO
                  filterVigencia={filterVigencia}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};
function PresupuestosIniciales({ constructora, usuario }) {
  const [proyectos, setProyectos] = useState([]);
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState(() => {
    // Intentar restaurar el último proyecto seleccionado desde localStorage
    try {
      const saved = localStorage.getItem('presupuestosIniciales_proyectoSeleccionado');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error restaurando proyecto seleccionado:', error);
      return null;
    }
  });
  const [presupuestosIniciales, setPresupuestosIniciales] = useState([]);
  const [filterVigencia, setFilterVigencia] = useState('activos');
  // Nuevo: filtro por concepto
  const [filterConceptoId, setFilterConceptoId] = useState('todos');
  // Buscador
  const [searchTerm, setSearchTerm] = useState('');
  const [etapas, setEtapas] = useState([]);
  const [subetapas, setSubetapas] = useState([]);
  const [conceptosSalida, setConceptosSalida] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Estados para permisos
  const [permisosCliente, setPermisosCliente] = useState({ esCliente: null });

  // Estados para modales
  const [showModal, setShowModal] = useState(false);
  const [editingPresupuesto, setEditingPresupuesto] = useState(null);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedPresupuesto, setSelectedPresupuesto] = useState(null);
  const [gastosNoAsignados, setGastosNoAsignados] = useState([]);
  const [presupuestosLoadingIds, setPresupuestosLoadingIds] = useState(new Set());
  const [gastosAsignados, setGastosAsignados] = useState([]);

  // Estados para toggle moneda
  const [monedaVista, setMonedaVista] = useState("ARS");
  const [cotizacionDolar, setCotizacionDolar] = useState(null);

  // Estados para datos de honorarios/MO (reutilizando lógica existente)
  const [datosHonorarios, setDatosHonorarios] = useState(null);
  const [datosManoObra, setDatosManoObra] = useState(null);
  const [datosLlaveEnMano, setDatosLlaveEnMano] = useState(null);
  const [indicesCAC, setIndicesCAC] = useState([]);
  
  // NUEVO: Estado para total optimizado
  const [totalOptimizado, setTotalOptimizado] = useState(null);
  
  // NUEVOS: Estados para datos optimizados de honorarios y mano de obra
  const [datosHonorariosOptimizados, setDatosHonorariosOptimizados] = useState(null);
  const [datosManoObraOptimizados, setDatosManoObraOptimizados] = useState(null);

  // NUEVA FUNCIÓN: Actualizar total optimizado desde tabla (async)
  const actualizarTotalOptimizado = async () => {
    if (!proyectoSeleccionado?.id) {
      console.log('🔍 No hay proyecto seleccionado, usando función legacy');
      setTotalOptimizado(null);
      return;
    }

    try {
      console.log('🔍 DEBUG actualizarTotalOptimizado:');
      console.log('  monedaVista:', monedaVista);
      console.log('  proyecto_id:', proyectoSeleccionado.id);

      // Cargar TODOS los tipos desde la tabla optimizada
      const { data: todosLosTipos, error } = await supabase
        .from("presupuestos_iniciales_actualizados")
        .select("tipo, nombre_presupuesto, total_actualizado_ars, total_actualizado_usd, activo, pagado_ars, pagado_usd, saldo_ars, saldo_usd, moneda_original")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("activo", true);

      if (error) {
        console.error('❌ Error cargando tabla optimizada:', error);
        setTotalOptimizado(null);
        setDatosHonorariosOptimizados(null);
        setDatosManoObraOptimizados(null);
        return;
      }

      if (!todosLosTipos || todosLosTipos.length === 0) {
        console.log('🔍 No hay datos en tabla optimizada');
        setTotalOptimizado(null);
        setDatosHonorariosOptimizados(null);
        setDatosManoObraOptimizados(null);
        return;
      }

      console.log('📊 Datos optimizados encontrados:', todosLosTipos.length, 'items');

      // Separar por tipos
      const honorarios = todosLosTipos.find(item => item.tipo === 'honorarios');
      const manoObra = todosLosTipos.find(item => item.tipo === 'mano_obra');

      // Actualizar estados específicos
      if (honorarios) {
        // Usar moneda original del presupuesto, no monedaVista
        const usarUSD = honorarios.moneda_original === 'USD';
        const totalPagado = usarUSD ? honorarios.pagado_usd : honorarios.pagado_ars;
        const saldoPendiente = usarUSD ? honorarios.saldo_usd : honorarios.saldo_ars;
        const totalActualizado = usarUSD ? honorarios.total_actualizado_usd : honorarios.total_actualizado_ars;
        
        // Calcular porcentaje pagado correctamente
        const porcentajePagado = totalActualizado > 0 ? (totalPagado / totalActualizado) * 100 : 0;
        
        setDatosHonorariosOptimizados({
          nombre: honorarios.nombre_presupuesto || 'Honorarios Profesionales',
          moneda: honorarios.moneda_original,
          total_pagado: totalPagado,
          saldo_pendiente: saldoPendiente,
          porcentaje_pagado: porcentajePagado,
          // Agregar campos adicionales para compatibilidad
          estado: 'abierto'
        });
        console.log('✅ Honorarios optimizados cargados:', {
          moneda: honorarios.moneda_original,
          pagado: totalPagado,
          saldo: saldoPendiente,
          total: totalActualizado,
          porcentaje: porcentajePagado.toFixed(1) + '%'
        });
      } else {
        setDatosHonorariosOptimizados(null);
      }

      if (manoObra) {
        // Usar moneda original del presupuesto, no monedaVista
        const usarUSD = manoObra.moneda_original === 'USD';
        const totalPagado = usarUSD ? manoObra.pagado_usd : manoObra.pagado_ars;
        const saldoPendiente = usarUSD ? manoObra.saldo_usd : manoObra.saldo_ars;
        const totalActualizado = usarUSD ? manoObra.total_actualizado_usd : manoObra.total_actualizado_ars;
        
        // Calcular porcentaje pagado correctamente
        const porcentajePagado = totalActualizado > 0 ? (totalPagado / totalActualizado) * 100 : 0;
        
        setDatosManoObraOptimizados({
          nombre: manoObra.nombre_presupuesto || 'Mano de Obra',
          moneda: manoObra.moneda_original,
          total_pagado: totalPagado,
          saldo_pendiente: saldoPendiente,
          porcentaje_pagado: porcentajePagado,
          // Agregar campos adicionales para compatibilidad
          estado: 'abierto'
        });
        console.log('✅ Mano de obra optimizada cargada:', {
          moneda: manoObra.moneda_original,
          pagado: totalPagado,
          saldo: saldoPendiente,
          total: totalActualizado,
          porcentaje: porcentajePagado.toFixed(1) + '%'
        });
      } else {
        setDatosManoObraOptimizados(null);
      }

      // Usar directamente los totales de la tabla optimizada
      const total = todosLosTipos.reduce((sum, item) => {
        const campo = monedaVista === 'USD' ? 'total_actualizado_usd' : 'total_actualizado_ars';
        const valor = item[campo] || 0;
        console.log(`  ${item.tipo} (${item.nombre_presupuesto}): ${valor} ${monedaVista}`);
        return sum + valor;
      }, 0);

      console.log('  TOTAL FINAL OPTIMIZADO:', total);
      setTotalOptimizado(total);
    } catch (error) {
      console.error('❌ Error en función optimizada:', error);
      setTotalOptimizado(null);
    }
  };

  // EFFECT: Actualizar total optimizado cuando cambie proyecto o moneda
  useEffect(() => {
    if (proyectoSeleccionado?.id) {
      actualizarTotalOptimizado();
    }
  }, [proyectoSeleccionado?.id, monedaVista]);

  // EFFECT: Actualizar datos honorarios/MO cuando cambien los optimizados
  useEffect(() => {
    if (datosHonorariosOptimizados || datosManoObraOptimizados) {
      console.log('🔄 Datos optimizados detectados, actualizando cargarDatosHonorariosMO...');
      cargarDatosHonorariosMO();
    }
  }, [datosHonorariosOptimizados, datosManoObraOptimizados]);

  // Estados para alertas personalizadas
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Estados para colapso de tarjetas (sincronizado)
  const [tarjetasColapsadas, setTarjetasColapsadas] = useState(true);

  // Estados para agrupación
  const [etapasAgrupadas, setEtapasAgrupadas] = useState([]);

  // NUEVOS ESTADOS PARA MODALES DE INFORMACIÓN
  const [showInfoModalPresupuestoInicial, setShowInfoModalPresupuestoInicial] =
    useState(false);
  const [
    showInfoModalPresupuestoActualizado,
    setShowInfoModalPresupuestoActualizado,
  ] = useState(false);

  // Recargar presupuestos cuando cambia la vigencia, proyecto, concepto o búsqueda
  useEffect(() => {
    if (!proyectoSeleccionado) return;
    cargarPresupuestosIniciales();
  }, [filterVigencia, proyectoSeleccionado, filterConceptoId, searchTerm]);

  // Función para guardar el proyecto seleccionado en localStorage
  const guardarProyectoSeleccionado = (proyecto) => {
    try {
      localStorage.setItem('presupuestosIniciales_proyectoSeleccionado', JSON.stringify(proyecto));
    } catch (error) {
      console.error('Error guardando proyecto seleccionado:', error);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  // ESC handler obligatorio
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        setShowAsignarModal(false);
        setShowInfoModal(false);
        setShowInfoModalPresupuestoInicial(false);
        setShowInfoModalPresupuestoActualizado(false);
        setCustomModal((prev) => ({ ...prev, isOpen: false }));
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Bloquear scroll del body cuando hay modales abiertos
  useEffect(() => {
    if (
      showModal ||
      showAsignarModal ||
      showInfoModal ||
      showInfoModalPresupuestoInicial ||
      showInfoModalPresupuestoActualizado ||
      customModal.isOpen
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    // Limpiar al desmontar
    return () => {
      document.body.style.overflow = "";
    };
  }, [
    showModal,
    showAsignarModal,
    showInfoModal,
    showInfoModalPresupuestoInicial,
    showInfoModalPresupuestoActualizado,
    customModal.isOpen,
  ]);

  // Scroll reset al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cargar permisos de cliente al inicio
  useEffect(() => {
    if (constructora?.id && usuario) {
      cargarPermisosCliente();
    }
  }, [constructora, usuario]);

  useEffect(() => {
    if (constructora?.id && Object.keys(permisosCliente).length > 0) {
      cargarProyectos();
      obtenerCotizacionDolar();
      cargarIndicesCAC();
    }
  }, [constructora, permisosCliente]);

  useEffect(() => {
    if (proyectoSeleccionado) {
      cargarDatosProyecto();
    }
  }, [proyectoSeleccionado]);

  // Función para cargar permisos de cliente
  const cargarPermisosCliente = async () => {
    try {
      const permisos = await obtenerPermisosCliente(usuario, constructora);
      setPermisosCliente(permisos);
    } catch (error) {
      console.error("Error cargando permisos:", error);
      setPermisosCliente({ esCliente: false });
    }
  };

  const cargarProyectos = async () => {
    try {
      let data = [];
      let error = null;

      if (filterVigencia === 'eliminados') {
        const { data: idsData, error: idsError } = await supabase
          .from('presupuestos_iniciales')
          .select('proyecto_id')
          .eq('constructora_id', constructora.id)
          .or('activo.eq.false,deleted_at.not.is.null');
        if (idsError) {
          error = idsError;
        } else {
          const ids = Array.from(new Set((idsData || []).map(r => r.proyecto_id).filter(Boolean)));
          if (ids.length > 0) {
            let q = supabase
              .from('proyectos')
              .select('*')
              .eq('constructora_id', constructora.id)
              .in('id', ids)
              .order('codigo');
            const res = await q;
            data = res.data || [];
            error = res.error || null;
          } else {
            data = [];
          }
        }
      } else {
        let q = supabase
          .from('proyectos')
          .select('*')
          .eq('constructora_id', constructora.id)
          .eq('activo', true)
          .is('deleted_at', null);

        if (permisosCliente.esCliente && permisosCliente.proyectoId) {
          q = q.eq('id', permisosCliente.proyectoId);
        } else {
          q = q.order('obra_finalizada', { ascending: true }).order('codigo');
        }

        const res = await q;
        data = res.data || [];
        error = res.error || null;
      }

      if (error) {
        console.error("Error cargando proyectos:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al cargar proyectos: " + error.message
        );
      } else {
        setProyectos(data || []);

        if (data?.length > 0) {
          if (permisosCliente.esCliente) {
            const proyectoCliente = data.find(
              (p) => p.id === permisosCliente.proyectoId
            );
            if (proyectoCliente) {
              setProyectoSeleccionado(proyectoCliente);
              guardarProyectoSeleccionado(proyectoCliente);
            }
          } else {
            // Verificar si el proyecto guardado sigue existiendo
            if (proyectoSeleccionado) {
              const proyectoExiste = data.find(p => p.id === proyectoSeleccionado.id);
              if (!proyectoExiste) {
                // El proyecto guardado ya no existe, establecer uno por defecto
                const primerActivo = data.find((p) => !p.obra_finalizada);
                const proyectoInicial = primerActivo || data[0];
                setProyectoSeleccionado(proyectoInicial);
                guardarProyectoSeleccionado(proyectoInicial);
              }
            } else {
              // No hay proyecto guardado, establecer uno por defecto
              const primerActivo = data.find((p) => !p.obra_finalizada);
              const proyectoInicial = primerActivo || data[0];
              setProyectoSeleccionado(proyectoInicial);
              guardarProyectoSeleccionado(proyectoInicial);
            }
          }
        }
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setLoading(false);
  };

  const obtenerCotizacionDolar = async () => {
    try {
      const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
      const data = await response.json();
      setCotizacionDolar(data.venta);
    } catch (err) {
      console.error("Error obteniendo cotización:", err);
      setCotizacionDolar(1000);
    }
  };

  // NUEVA FUNCIÓN: Actualizar presupuesto con CAC
  const actualizarPresupuestoConCAC = async (presupuesto, gastosAsignados) => {
    console.log(`🔄 ACTUALIZANDO CON CAC: ${presupuesto.nombre}`, {
      moneda: presupuesto.moneda,
      estado: presupuesto.estado,
      fechaCreacion: presupuesto.created_at,
      gastosCount: gastosAsignados?.length || 0,
    });

    // Solo aplicar CAC a presupuestos en ARS
    if (presupuesto.moneda !== "ARS") {
      console.log(
        `⚠️ PRESUPUESTO EN USD - Sin actualización CAC: ${presupuesto.nombre}`
      );
      return {
        ...presupuesto,
        monto_actualizado: presupuesto.monto_presupuestado,
        saldo_actualizado: presupuesto.saldo_pendiente,
        usa_cac: false,
        observaciones_cac: "Presupuesto en USD - no aplica CAC",
      };
    }

    try {
      // Fecha de referencia para actualización CAC
      const fechaReferencia = formatearFechaSinTimezone(
        presupuesto.fecha || presupuesto.created_at
      );
      const fechaReferenciaDate = new Date(fechaReferencia + "T00:00:00");
      const mesReferencia = formatearFechaParaIndice(fechaReferenciaDate);

      console.log(
        `📅 FECHA REFERENCIA CAC: ${fechaReferencia} → ${mesReferencia}`
      );

      // Obtener CAC del mes de referencia
      const cacCreacion = await obtenerIndicePorFecha(
        mesReferencia,
        "CAC_GENERAL",
        "presupuesto"
      );

      if (!cacCreacion) {
        console.log(`❌ NO HAY CAC PARA MES DE REFERENCIA: ${mesReferencia}`);
        return {
          ...presupuesto,
          monto_actualizado: presupuesto.monto_presupuestado,
          saldo_actualizado: presupuesto.saldo_pendiente,
          usa_cac: false,
          observaciones_cac: `Sin CAC disponible para ${mesReferencia}`,
        };
      }

      console.log(
        `📊 CAC CREACIÓN (${mesReferencia}): ${cacCreacion.toFixed(2)}`
      );

      // Calcular unidades base del presupuesto
      const unidadesBase =
        parseFloat(presupuesto.monto_presupuestado) / cacCreacion;
      console.log(`🔢 UNIDADES BASE: ${unidadesBase.toFixed(2)} unidades`);

      // Procesar gastos mes a mes para calcular unidades gastadas
      let unidadesGastadas = 0;
      let fechaUltimoGasto = null;

      if (gastosAsignados && gastosAsignados.length > 0) {
        console.log(`💰 PROCESANDO ${gastosAsignados.length} GASTOS:`);

        for (const gasto of gastosAsignados) {
          const fechaGasto = formatearFechaSinTimezone(gasto.fecha);
          const fechaGastoDate = new Date(fechaGasto + "T00:00:00");
          const mesGasto = formatearFechaParaIndice(fechaGastoDate);

          // Obtener CAC del mes del gasto
          const cacGasto = await obtenerIndicePorFecha(
            mesGasto,
            "CAC_GENERAL",
            "presupuesto"
          );

          if (cacGasto) {
            // Convertir monto del gasto a unidades usando CAC del mes del gasto
            const montoGastoARS = convertirMonto(
              gasto.monto,
              gasto.moneda,
              "ARS",
              gasto.tipo_cambio
            );
            const unidadesGasto = montoGastoARS / cacGasto;
            unidadesGastadas += unidadesGasto;

            console.log(
              `  💸 Gasto ${
                gasto.fecha
              }: $${montoGastoARS.toLocaleString()} ÷ CAC ${cacGasto.toFixed(
                2
              )} = ${unidadesGasto.toFixed(2)} unidades`
            );

            // Actualizar fecha del último gasto
            if (
              !fechaUltimoGasto ||
              fechaGastoDate > new Date(fechaUltimoGasto + "T00:00:00")
            ) {
              fechaUltimoGasto = fechaGasto;
            }
          } else {
            console.log(
              `⚠️ Sin CAC para gasto del ${mesGasto} - usando último disponible`
            );
            // Si no hay CAC, usar el último disponible
            const ultimoCAC = await obtenerUltimoIndiceRealDisponible(
              "CAC_GENERAL",
              "presupuesto"
            );
            if (ultimoCAC) {
              const montoGastoARS = convertirMonto(
                gasto.monto,
                gasto.moneda,
                "ARS",
                gasto.tipo_cambio
              );
              const unidadesGasto = montoGastoARS / ultimoCAC;
              unidadesGastadas += unidadesGasto;
            }
          }
        }
      }

      console.log(
        `🔢 UNIDADES GASTADAS TOTAL: ${unidadesGastadas.toFixed(2)} unidades`
      );

      // Determinar fecha hasta la cual aplicar CAC
      let fechaHastaCAC;
      if (presupuesto.estado === "cerrado" && fechaUltimoGasto) {
        fechaHastaCAC = fechaUltimoGasto;
        console.log(
          `🔒 PRESUPUESTO CERRADO - CAC hasta último gasto: ${fechaUltimoGasto}`
        );
      } else {
        fechaHastaCAC = new Date().toISOString().split("T")[0];
        console.log(`🔓 PRESUPUESTO ABIERTO - CAC hasta hoy: ${fechaHastaCAC}`);
      }

      // Obtener CAC más reciente hasta la fecha límite
      const fechaHastaDate = new Date(fechaHastaCAC + "T00:00:00");
      const mesHasta = formatearFechaParaIndice(fechaHastaDate);

      let cacActual = await obtenerIndicePorFecha(
        mesHasta,
        "CAC_GENERAL",
        "presupuesto"
      );
      if (!cacActual) {
        cacActual = await obtenerUltimoIndiceRealDisponible(
          "CAC_GENERAL",
          "presupuesto"
        );
      }

      if (!cacActual) {
        console.log(`❌ NO HAY CAC ACTUAL DISPONIBLE`);
        return {
          ...presupuesto,
          monto_actualizado: presupuesto.monto_presupuestado,
          saldo_actualizado: presupuesto.saldo_pendiente,
          usa_cac: false,
          observaciones_cac: "Sin CAC actual disponible",
        };
      }

      console.log(`📊 CAC ACTUAL (${mesHasta}): ${cacActual.toFixed(2)}`);

      // Calcular valores actualizados
      const unidadesRestantes = unidadesBase - unidadesGastadas;
      const montoActualizado = unidadesBase * cacActual;
      const saldoActualizado = unidadesRestantes * cacActual;

      console.log(`💰 RESULTADOS:`, {
        montoOriginal: presupuesto.monto_presupuestado,
        montoActualizado: montoActualizado.toFixed(0),
        saldoOriginal: presupuesto.saldo_pendiente,
        saldoActualizado: saldoActualizado.toFixed(0),
        variacionCAC: ((cacActual / cacCreacion - 1) * 100).toFixed(1) + "%",
      });

      return {
        ...presupuesto,
        monto_actualizado: montoActualizado,
        saldo_actualizado: saldoActualizado,
        usa_cac: true,
        cac_creacion: cacCreacion,
        cac_actual: cacActual,
        unidades_base: unidadesBase,
        unidades_gastadas: unidadesGastadas,
        fecha_hasta_cac: fechaHastaCAC,
        observaciones_cac: `Actualizado con CAC desde ${mesReferencia} hasta ${mesHasta}`,
      };
    } catch (error) {
      console.error(
        `❌ ERROR ACTUALIZANDO CAC para ${presupuesto.nombre}:`,
        error
      );
      return {
        ...presupuesto,
        monto_actualizado: presupuesto.monto_presupuestado,
        saldo_actualizado: presupuesto.saldo_pendiente,
        usa_cac: false,
        observaciones_cac: `Error aplicando CAC: ${error.message}`,
      };
    }
  };

  // Función para convertir con tipo de cambio del proyecto
  const convertirMontoConTCProyecto = (monto, monedaOrigen, monedaDestino) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOrigen === monedaDestino) {
      return montoNum;
    }

    const tcProyecto =
      proyectoSeleccionado?.tipo_cambio || cotizacionDolar || 1000;

    if (monedaOrigen === "USD" && monedaDestino === "ARS") {
      return montoNum * tcProyecto;
    } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
      return montoNum / tcProyecto;
    }

    return montoNum;
  };

  // FUNCIÓN MODIFICADA: Calcular total presupuesto inicial (sin cambios)
  const calcularTotalPresupuestoInicial = () => {
    let total = 0;
    let baseParaHonorarios = 0;

    // Llave en mano es exclusivo y tiene su propio total
    if (datosLlaveEnMano && proyectoSeleccionado?.monto_total_llave_en_mano) {
      return convertirMontoConTCProyecto(
        proyectoSeleccionado.monto_total_llave_en_mano,
        proyectoSeleccionado.moneda_llave_en_mano,
        monedaVista
      );
    }

    // Sumar Mano de Obra (si existe) a la base y al total
    if (
      datosManoObra &&
      proyectoSeleccionado?.mano_obra_separada &&
      proyectoSeleccionado?.monto_mano_obra
    ) {
      const montoManoObra = convertirMontoConTCProyecto(
        proyectoSeleccionado.monto_mano_obra,
        proyectoSeleccionado.moneda_mano_obra,
        monedaVista
      );
      total += montoManoObra;
      baseParaHonorarios += montoManoObra;
    }

    // Sumar Presupuestos de Obra (si existen) a la base y al total
    if (presupuestosIniciales && presupuestosIniciales.length > 0) {
      const montoPresupuestos = presupuestosIniciales.reduce((sum, p) => {
        return (
          sum +
          convertirMonto(
            p.monto_presupuestado, // Usar valor ORIGINAL
            p.moneda,
            monedaVista,
            p.tipo_cambio
          )
        );
      }, 0);
      total += montoPresupuestos;
      baseParaHonorarios += montoPresupuestos;
    }

    // Sumar Honorarios (calculados o fijos)
    if (datosHonorarios) {
      if (proyectoSeleccionado?.tipo_obra === "administracion_porcentaje") {
        const porcentaje =
          parseFloat(proyectoSeleccionado.porcentaje_honorario || 0) / 100;
        total += baseParaHonorarios * porcentaje; // Añadir honorarios calculados
      } else if (proyectoSeleccionado?.honorarios) {
        // Lógica original para monto fijo
        total += convertirMontoConTCProyecto(
          proyectoSeleccionado.honorarios,
          proyectoSeleccionado.moneda_honorarios,
          monedaVista
        );
      }
    }

    return total;
  };

  // FUNCIÓN MODIFICADA: Calcular total presupuesto actualizado (con CAC)

  // FUNCIÓN: Obtener total optimizado (con fallback)
  const calcularTotalPresupuestoActualizadoOptimizado = () => {
    if (totalOptimizado !== null) {
      console.log('✅ Usando total optimizado:', totalOptimizado);
      return totalOptimizado;
    }
    
    console.log('⚠️ Total optimizado no disponible, usando función legacy');
    return calcularTotalPresupuestoActualizado();
  };
  // FUNCIÓN LEGACY (mantener como fallback)
  const calcularTotalPresupuestoActualizado = () => {
    let total = 0;
    let baseParaHonorarios = 0;

    console.log('🔍 DEBUG calcularTotalPresupuestoActualizado (LEGACY):');
    console.log('  monedaVista:', monedaVista);
    console.log('  cotizacionDolar:', cotizacionDolar);

    // Llave en mano es exclusivo y tiene su propio total actualizado
    if (datosLlaveEnMano) {
      const resultado = convertirMonto(
        datosLlaveEnMano.total_pagado + datosLlaveEnMano.saldo_pendiente,
        datosLlaveEnMano.moneda,
        monedaVista,
        cotizacionDolar
      );
      console.log('  Llave en mano:', resultado);
      return resultado;
    }

    // Sumar Mano de obra actualizada (si existe) a la base y al total
    if (datosManoObra && proyectoSeleccionado?.mano_obra_separada) {
      const montoManoObra = convertirMonto(
        datosManoObra.total_pagado + datosManoObra.saldo_pendiente,
        datosManoObra.moneda,
        monedaVista,
        cotizacionDolar
      );
      total += montoManoObra;
      baseParaHonorarios += montoManoObra;
      console.log('  Mano de obra:', montoManoObra);
    }

    // Sumar Presupuestos de Obra actualizados (con CAC) a la base y al total
    if (presupuestosIniciales && presupuestosIniciales.length > 0) {
      const montoPresupuestos = presupuestosIniciales.reduce((sum, p) => {
        const montoParaCalculo = p.monto_actualizado || p.monto_presupuestado;
        // Usar cotizacionDolar para conversión a USD, p.tipo_cambio para conversión a ARS
        const tipoCambio = monedaVista === 'USD' ? cotizacionDolar : p.tipo_cambio;
        const montoConvertido = convertirMonto(montoParaCalculo, p.moneda, monedaVista, tipoCambio);
        return sum + montoConvertido;
      }, 0);
      total += montoPresupuestos;
      baseParaHonorarios += montoPresupuestos;
      console.log('  Presupuestos de obra:', montoPresupuestos);
    }

    // Sumar Honorarios actualizados (calculados o fijos)
    if (datosHonorarios) {
      if (proyectoSeleccionado?.tipo_obra === "administracion_porcentaje") {
        const porcentaje =
          parseFloat(proyectoSeleccionado.porcentaje_honorario || 0) / 100;
        const honorariosCalculados = baseParaHonorarios * porcentaje;
        total += honorariosCalculados;
        console.log('  Honorarios calculados:', honorariosCalculados);
      } else {
        // Lógica original para monto fijo actualizado
        const honorariosFijos = convertirMonto(
          datosHonorarios.total_pagado + datosHonorarios.saldo_pendiente,
          datosHonorarios.moneda,
          monedaVista,
          cotizacionDolar
        );
        total += honorariosFijos;
        console.log('  Honorarios fijos:', honorariosFijos);
      }
    }

    console.log('  TOTAL FINAL LEGACY:', total);
    return total;
  };

  const cargarIndicesCAC = async () => {
    try {
      const { data, error } = await supabase
        .from("indices_cac")
        .select("*")
        .order("fecha", { ascending: true });

      if (!error && data) {
        setIndicesCAC(data);
        console.log("🗄️ ÍNDICES CAC CARGADOS:", data.length, "registros");
      }
    } catch (err) {
      console.error("Error cargando índices CAC:", err);
    }
  };

  const cargarDatosProyecto = async () => {
    if (!proyectoSeleccionado) return;

    setLoadingData(true);
    try {
      await Promise.all([
        cargarPresupuestosIniciales(),
        cargarEtapasYSubetapas(),
        cargarConceptosSalida(),
        cargarDatosHonorariosMO(),
        cargarGastosNoAsignados(),
      ]);
    } catch (err) {
      console.error("Error cargando datos del proyecto:", err);
      showCustomAlert("error", "Error", "Error cargando datos: " + err.message);
    }
    setLoadingData(false);
  };
  // FUNCIÓN OPTIMIZADA: cargarPresupuestosIniciales usando tabla optimizada
  const cargarPresupuestosIniciales = async () => {
    try {
      console.log("🔄 Cargando presupuestos iniciales desde tabla optimizada...");

      // 1. Cargar datos desde la tabla optimizada
      const { data: dataOptimizada, error: errorOptimizada } = await supabase
        .from("presupuestos_iniciales_actualizados")
        .select("*")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo", "obra")
        .order("created_at", { ascending: false });

      if (errorOptimizada) {
        console.error("❌ Error cargando tabla optimizada:", errorOptimizada);
        return;
      }

      console.log("📊 Datos optimizados encontrados:", dataOptimizada?.length || 0);

      // 2. Obtener IDs para cargar datos relacionados
      const presupuestoIds = dataOptimizada?.map(item => item.presupuesto_inicial_id).filter(Boolean) || [];
      const etapaIds = dataOptimizada?.map(item => item.etapa_id).filter(Boolean) || [];
      const subetapaIds = dataOptimizada?.map(item => item.subetapa_id).filter(Boolean) || [];

      // 3. Cargar presupuestos originales, etapas, subetapas y conceptos en paralelo
      const [presupuestosOriginales, etapas, subetapas, conceptos] = await Promise.all([
        presupuestoIds.length > 0 ? supabase
          .from("presupuestos_iniciales")
          .select("id, nombre, descripcion, estado, created_at, monto_presupuestado, concepto_salida_id, fecha, tipo_cambio, moneda, porcentaje_obra")
          .in("id", presupuestoIds) : Promise.resolve({ data: [] }),
        
        etapaIds.length > 0 ? supabase
          .from("etapas_obra")
          .select("id, nombre, orden")
          .in("id", etapaIds) : Promise.resolve({ data: [] }),
        
        subetapaIds.length > 0 ? supabase
          .from("subetapas_obra")
          .select("id, nombre, orden")
          .in("id", subetapaIds) : Promise.resolve({ data: [] }),
        
        supabase
          .from("conceptos_salida")
          .select("id, nombre")
      ]);

      // 4. Crear mapas para búsqueda rápida
      const presupuestosMap = new Map();
      (presupuestosOriginales.data || []).forEach(p => presupuestosMap.set(p.id, p));
      
      const etapasMap = new Map();
      (etapas.data || []).forEach(e => etapasMap.set(e.id, e));
      
      const subetapasMap = new Map();
      (subetapas.data || []).forEach(s => subetapasMap.set(s.id, s));
      
      const conceptosMap = new Map();
      (conceptos.data || []).forEach(c => conceptosMap.set(c.id, c));

      // 5. ✅ RECALCULAR EN TIEMPO REAL: Obtener gastos asignados para todos los presupuestos
      // (reutilizando presupuestoIds ya declarado arriba)
      
      // Consultar todos los gastos asignados en paralelo
      const gastosAsignadosPromises = presupuestoIds.map(presupuestoId => 
        supabase
          .from("salidas")
          .select("presupuesto_inicial_id, monto, moneda, tipo_cambio, caja_origen_id")
          .eq("presupuesto_inicial_id", presupuestoId)
          .eq("activo", true)
          .is("deleted_at", null)
      );
      
      const gastosAsignadosResults = await Promise.all(gastosAsignadosPromises);
      
      // Crear mapa de gastos por presupuesto
      const gastosPorPresupuesto = new Map();
      // Cargar mapa de cajas para excluir mano_obra
      const { data: cajasProyecto } = await supabase
        .from("cajas")
        .select("id, tipo_caja")
        .eq("constructora_id", constructora.id);
      const cajaIdToTipo = new Map((cajasProyecto||[]).map(c=>[c.id,c.tipo_caja]));

      gastosAsignadosResults.forEach((result, index) => {
        const presupuestoId = presupuestoIds[index];
        if (result.data) {
          const filtrados = result.data.filter(g => {
            const tipo = cajaIdToTipo.get(g.caja_origen_id);
            return tipo !== 'mano_obra';
          });
          gastosPorPresupuesto.set(presupuestoId, filtrados);
        }
      });

      // 6. Transformar datos optimizados
      const presupuestosTransformados = (dataOptimizada || []).map(item => {
        const presupuestoOriginal = presupuestosMap.get(item.presupuesto_inicial_id);
        const etapa = etapasMap.get(item.etapa_id);
        const subetapa = subetapasMap.get(item.subetapa_id);
        const concepto = conceptosMap.get(presupuestoOriginal?.concepto_salida_id);
        
        // Debug: verificar si las relaciones están llegando
        if (!etapa || !subetapa) {
          console.warn(`⚠️ Presupuesto ${item.nombre_presupuesto}: etapa_id=${item.etapa_id}, subetapa_id=${item.subetapa_id}`);
          console.warn(`   Etapa encontrada:`, etapa);
          console.warn(`   Subetapa encontrada:`, subetapa);
        }

        // Determinar moneda para cálculos
        const monedaCalculo = item.moneda_original;
        const totalActualizado = monedaCalculo === 'USD' ? item.total_actualizado_usd : item.total_actualizado_ars;
        
        // ✅ Calcular total gastado en tiempo real usando datos ya obtenidos
        const gastosAsignados = gastosPorPresupuesto.get(item.presupuesto_inicial_id) || [];
        const totalGastadoReal = gastosAsignados.reduce((sum, gasto) => {
          return sum + convertirMonto(
            gasto.monto,
            gasto.moneda,
            monedaCalculo,
            gasto.tipo_cambio
          );
        }, 0);
        
        const pagado = totalGastadoReal;
        const saldo = Math.max(totalActualizado - pagado, 0);

        // Calcular monto original
        const montoOriginal = presupuestoOriginal?.monto_presupuestado || 0;
        
        // Calcular variación
        const variacionMonto = totalActualizado - montoOriginal;
        const variacionPorcentaje = montoOriginal > 0 ? (variacionMonto / montoOriginal) * 100 : 0;

        return {
          // Datos del presupuesto original
          id: item.presupuesto_inicial_id,
          nombre: presupuestoOriginal?.nombre || presupuestoOriginal?.descripcion || item.nombre_presupuesto,
          descripcion: presupuestoOriginal?.descripcion || presupuestoOriginal?.nombre || item.nombre_presupuesto,
          estado: presupuestoOriginal?.estado || (item.cerrado ? 'cerrado' : 'abierto'),
          moneda: presupuestoOriginal?.moneda || item.moneda_original,
          created_at: presupuestoOriginal?.created_at || item.created_at,
          activo: item.activo,
          deleted_at: null, // Los eliminados se filtran antes
          
          // Campos críticos para edición
          concepto_salida_id: presupuestoOriginal?.concepto_salida_id,
          fecha: presupuestoOriginal?.fecha,
          tipo_cambio: presupuestoOriginal?.tipo_cambio,
          porcentaje_obra: presupuestoOriginal?.porcentaje_obra,
          
          // Datos de etapas
          etapa_id: item.etapa_id,
          subetapa_id: item.subetapa_id,
          etapa: etapa?.nombre || null,
          subetapa: subetapa?.nombre || null,
          orden_etapa: etapa?.orden || 0,
          orden_subetapa: subetapa?.orden || 0,
          
          // Relaciones para compatibilidad con el código existente
          etapas_obra: etapa ? { nombre: etapa.nombre, orden: etapa.orden } : null,
          subetapas_obra: subetapa ? { nombre: subetapa.nombre, orden: subetapa.orden } : null,
          conceptos_salida: concepto ? { nombre: concepto.nombre } : null,

          // Datos originales
          monto_presupuestado: montoOriginal,
          monto_original: montoOriginal,
          
          // Datos actualizados con CAC
          monto_actualizado: totalActualizado,
          total_actualizado: totalActualizado,
          variacion_monto: variacionMonto,
          variacion_porcentaje: variacionPorcentaje,
          
          // Datos de pagos
          total_gastado: pagado,
          saldo_pendiente: saldo,
          porcentaje_ejecutado: montoOriginal > 0 ? (pagado / montoOriginal) * 100 : 0,
          
          // Información de CAC
          usa_cac: item.metodo_actualizacion && item.metodo_actualizacion !== 'SIN_ACTUALIZACION',
          metodo_actualizacion: item.metodo_actualizacion,
          fecha_base: item.fecha_base,
          fecha_corte: item.fecha_corte,
          indice_base: item.indice_base,
          indice_corte: item.indice_corte,
          
          // Observaciones
          observaciones_cac: item.metodo_actualizacion && item.metodo_actualizacion !== 'SIN_ACTUALIZACION' 
            ? `Actualizado con ${item.metodo_actualizacion} (${variacionPorcentaje.toFixed(1)}%)`
            : 'Sin actualización CAC'
        };
      });

      console.log("📊 Presupuestos transformados:", presupuestosTransformados.length);

      // 7. Aplicar filtros (vigencia y concepto)
      let presupuestosFiltrados = presupuestosTransformados;
      if (filterVigencia === 'activos') {
        presupuestosFiltrados = presupuestosTransformados.filter(p => p.activo);
      } else if (filterVigencia === 'eliminados') {
        presupuestosFiltrados = presupuestosTransformados.filter(p => !p.activo);
      }

      // Filtro por concepto (cuando se elige uno específico)
      if (filterConceptoId && filterConceptoId !== 'todos') {
        presupuestosFiltrados = presupuestosFiltrados.filter(p => p.concepto_salida_id === filterConceptoId);
      }

      // Filtro por búsqueda
      if (searchTerm.trim()) {
        const termino = searchTerm.toLowerCase().trim();
        presupuestosFiltrados = presupuestosFiltrados.filter(p => {
          return (
            (p.nombre && p.nombre.toLowerCase().includes(termino)) ||
            (p.etapa && p.etapa.toLowerCase().includes(termino)) ||
            (p.subetapa && p.subetapa.toLowerCase().includes(termino)) ||
            (p.conceptos_salida?.nombre && p.conceptos_salida.nombre.toLowerCase().includes(termino)) ||
            (p.descripcion && p.descripcion.toLowerCase().includes(termino))
          );
        });
      }

      console.log("💾 Presupuestos finales:", presupuestosFiltrados.length);
      setPresupuestosIniciales(presupuestosFiltrados);
      agruparPresupuestosPorEtapa(presupuestosFiltrados);

      // AUTO-ASIGNACIÓN INTELIGENTE DE GASTOS
      // Ejecutar auto-asignación solo una vez por proyecto en esta sesión
      try {
        const keyAuto = `autoasign_${proyectoSeleccionado.id}`;
        const puedeUsarSession = typeof window !== 'undefined' && window.sessionStorage;
        const yaEjecutado = puedeUsarSession ? sessionStorage.getItem(keyAuto) === '1' : false;
        if (!yaEjecutado) {
          // Marcar como ejecutado ANTES de correr, para evitar bucles por recargas dentro de autoAsignarGastos
          if (puedeUsarSession) {
            try { sessionStorage.setItem(keyAuto, '1'); } catch (_) {}
          }
          await autoAsignarGastos(presupuestosFiltrados);
        }
      } catch (_) {
        // no-op si sessionStorage no disponible
      }
    } catch (err) {
      console.error("❌ Error cargando presupuestos iniciales:", err);
    } finally {
      setLoadingData(false);
    }
  };
  // FUNCIÓN CORREGIDA: Auto-asignación inteligente de gastos
  const autoAsignarGastos = async (presupuestos) => {
    try {
      console.log("🤖 INICIANDO AUTO-ASIGNACIÓN INTELIGENTE...");

      // 1. Buscar gastos sin asignar
      const { data: gastosNoAsignados, error } = await supabase
        .from("salidas")
        .select(`
          *,
          conceptos_salida(nombre),
          etapas_obra(nombre),
          subetapas_obra(nombre)
        `)
        .eq("proyecto_id", proyectoSeleccionado.id)
        .is("presupuesto_inicial_id", null)
        .eq('activo', true)
        .is('deleted_at', null);

      if (error || !gastosNoAsignados?.length) {
        console.log("❌ No hay gastos para auto-asignar");
        return;
      }

      console.log(
        `🔍 Encontrados ${gastosNoAsignados.length} gastos sin asignar`
      );

      let asignaciones = 0;

      // 2. Para cada gasto, buscar presupuesto que coincida
      // Cargar cajas una vez para filtrar mano_obra en auto-asignación
      const { data: cajasAuto } = await supabase
        .from("cajas")
        .select("id, tipo_caja")
        .eq("constructora_id", constructora.id);
      const tipoCajaById = new Map((cajasAuto||[]).map(c=>[c.id,c.tipo_caja]));

      for (const gasto of gastosNoAsignados) {
        // Excluir gastos provenientes de caja mano_obra
        if (gasto.caja_origen_id && tipoCajaById.get(gasto.caja_origen_id) === 'mano_obra') {
          continue;
        }
        console.log(`🔍 Analizando gasto: ${gasto.conceptos_salida?.nombre || 'Sin concepto'} (etapa: ${gasto.etapas_obra?.nombre || 'Sin etapa'}, subetapa: ${gasto.subetapas_obra?.nombre || 'Sin subetapa'})`);
        
        // Buscar presupuesto que coincida por etapa y subetapa
        let presupuestoCoincidente = presupuestos.find(
          (presupuesto) =>
            presupuesto.etapa_id === gasto.etapa_id &&
            presupuesto.subetapa_id === gasto.subetapa_id &&
            presupuesto.estado === "abierto" &&
            (presupuesto.saldo_pendiente || 0) > 0
        );

        // Si no hay coincidencia exacta, buscar por concepto (para casos como "obra gris corrlaón")
        if (!presupuestoCoincidente && gasto.concepto_id) {
          presupuestoCoincidente = presupuestos.find(
            (presupuesto) =>
              presupuesto.concepto_salida_id === gasto.concepto_id &&
              presupuesto.estado === "abierto" &&
              (presupuesto.saldo_pendiente || 0) > 0
          );
        }

        if (presupuestoCoincidente) {
          console.log(
            `✅ MATCH: Gasto ${gasto.id} (${gasto.conceptos_salida?.nombre}) → Presupuesto ${presupuestoCoincidente.id} (${presupuestoCoincidente.etapas_obra?.nombre} → ${presupuestoCoincidente.subetapas_obra?.nombre})`
          );

          // ✅ CORRECCIÓN: Usar el presupuesto_inicial_id de PIA
          const { error: updateError } = await supabase
            .from("salidas")
            .update({ presupuesto_inicial_id: presupuestoCoincidente.presupuesto_inicial_id })
            .eq("id", gasto.id);

          if (!updateError) {
            asignaciones++;
            console.log(`🎯 Gasto asignado exitosamente`);
          } else {
            console.error("❌ Error asignando gasto:", updateError);
          }
        } else {
          console.log(
            `⚠️ Sin match para gasto ${gasto.id} - ${gasto.conceptos_salida?.nombre || 'Sin concepto'} (etapa: ${gasto.etapa_id}, subetapa: ${gasto.subetapa_id}, concepto: ${gasto.concepto_id})`
          );
        }
      }

      if (asignaciones > 0) {
        console.log(
          `🎉 AUTO-ASIGNACIÓN COMPLETADA: ${asignaciones} gastos asignados`
        );
        showCustomAlert(
          "success",
          "Auto-asignación completada",
          `Se asignaron automáticamente ${asignaciones} gastos a sus presupuestos correspondientes.`
        );

        // Recargar datos para mostrar los cambios
        await cargarPresupuestosIniciales();
      } else {
        console.log("📝 No se realizaron auto-asignaciones");
      }
    } catch (err) {
      console.error("❌ Error en auto-asignación:", err);
    }
  };

  const cargarEtapasYSubetapas = async () => {
    try {
      const [etapasResult, subetapasResult] = await Promise.all([
        supabase
          .from("etapas_obra")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .order("orden"),

        supabase
          .from("subetapas_obra")
          .select("*")
          .eq("constructora_id", constructora.id)
          .eq("activo", true)
          .is("deleted_at", null)
          .order("orden"),
      ]);

      if (!etapasResult.error) setEtapas(etapasResult.data || []);
      if (!subetapasResult.error) setSubetapas(subetapasResult.data || []);
    } catch (err) {
      console.error("Error cargando etapas:", err);
    }
  };

  const cargarConceptosSalida = async () => {
    try {
      const { data, error } = await supabase
        .from("conceptos_salida")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (!error) {
        setConceptosSalida(data || []);
      }
    } catch (err) {
      console.error("Error cargando conceptos:", err);
    }
  };

  // Reutilizar lógica de HonorariosMO para calcular datos
  // NUEVA FUNCIÓN: Reutilizar EXACTAMENTE la misma lógica de HonorariosMO
  const cargarDatosHonorariosMO = async () => {
    const proyecto = proyectoSeleccionado;

    try {
      // ✅ PRIORIZAR datos optimizados si están disponibles
      if (datosHonorariosOptimizados) {
        console.log("✅ Usando honorarios optimizados:", datosHonorariosOptimizados);
        setDatosHonorarios(datosHonorariosOptimizados);
      }
      
      if (datosManoObraOptimizados) {
        console.log("✅ Usando mano de obra optimizada:", datosManoObraOptimizados);
        setDatosManoObra(datosManoObraOptimizados);
      }
      
      // Si ya tenemos datos optimizados para ambos (o los que apliquen), no cargar legacy
      const tieneHonorariosNecesarios = !proyecto.honorarios || datosHonorariosOptimizados;
      const tieneManoObraNecesaria = !proyecto.mano_obra_separada || datosManoObraOptimizados;
      
      if (tieneHonorariosNecesarios && tieneManoObraNecesaria && proyecto.tipo_obra !== "llave_en_mano") {
        console.log("✅ Usando solo datos optimizados, saltando carga legacy");
        return;
      }
      if (proyecto.tipo_obra === "llave_en_mano") {
        await cargarDatosLlaveEnManoConActualizacion();
        // Limpiar los otros estados para no mostrar datos viejos
        setDatosHonorarios(null);
        setDatosManoObra(null);
      } else {
        // Manejar casos de administración (fijo y por porcentaje)
        let honorariosPromise = Promise.resolve();
        let manoObraPromise = Promise.resolve();

        // Cargar Mano de Obra siempre que exista
        if (proyecto.mano_obra_separada && proyecto.monto_mano_obra) {
          manoObraPromise = cargarDatosManoObraConActualizacion();
        }

        // Cargar Honorarios (sea fijo o por porcentaje)
        if (proyecto.honorarios) {
          // Lógica para honorarios con monto fijo
          honorariosPromise = cargarDatosHonorariosConActualizacion();
        } else if (proyecto.tipo_obra === "administracion_porcentaje") {
          // **INICIO DE LA CORRECCIÓN**
          // Para proyectos por porcentaje, no llamamos a la función de actualización.
          // En su lugar, creamos un objeto "marcador de posición" para los honorarios.
          // Esto asegura que la fila de "Honorarios Profesionales" se renderice en la UI
          // inmediatamente. El cálculo del valor se realiza después en el renderizado (JSX),
          // una vez que todos los datos necesarios están disponibles.
          setDatosHonorarios({
            nombre: "Honorarios Profesionales",
            es_calculado_porcentaje: true, // Se añade una bandera para identificarlo
            monto_inicial: 0, // El valor real se calculará en el render
            moneda: proyecto.moneda_honorarios || "ARS",
            estado: "abierto",
            // Se inicializan otros campos para evitar errores
            total_pagado: 0,
            saldo_pendiente: 0,
            porcentaje_pagado: 0,
          });
          // **FIN DE LA CORRECCIÓN**
        }

        // Esperar a que ambas operaciones (si se iniciaron) terminen
        await Promise.all([honorariosPromise, manoObraPromise]);
      }
    } catch (err) {
      console.error("❌ Error cargando datos honorarios/MO:", err);
      // Opcional: mostrar alerta al usuario
      // showCustomAlert("error", "Error de Carga", "No se pudieron cargar los datos de honorarios y mano de obra.");
    }
  };

  // NUEVA FUNCIÓN: Cargar llave en mano CON sistema de actualización
  const cargarDatosLlaveEnManoConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.monto_total_llave_en_mano) {
      setDatosLlaveEnMano({
        error: "No hay monto configurado para llave en mano",
      });
      return;
    }

    // Obtener todos los pagos del proyecto
    const { data: pagos, error: errorPagos } = await supabase
      .from("entradas")
      .select(
        "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
      )
      .eq("proyecto_id", proyecto.id)
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    if (errorPagos) {
      console.error("Error cargando pagos llave en mano:", errorPagos);
      setDatosLlaveEnMano({ error: "Error cargando pagos" });
      return;
    }

    // Calcular con sistema de actualización (IGUAL QUE HONORARIOS/MO)
    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_total_llave_en_mano,
      proyecto.moneda_llave_en_mano,
      proyecto.metodo_actualizacion,
      proyecto.fecha_inicio_actualizacion_llave_en_mano,
      pagos || [],
      "llave_en_mano",
      !!proyecto.metodo_actualizacion
    );

    const totalActualizadoLlave =
      (resultado.resumenFinal?.pagosAcumulados || 0) +
      (resultado.resumenFinal?.saldoPendiente || 0);

    setDatosLlaveEnMano({
      nombre: "Costo Total de la Obra",
      monto_inicial: totalActualizadoLlave, // ✅ NUEVA LÍNEA
      moneda: proyecto.moneda_llave_en_mano,
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      porcentaje_pagado:
        resultado.resumenFinal?.totalUnidades > 0
          ? ((resultado.resumenFinal?.unidadesGastadas || 0) /
              resultado.resumenFinal.totalUnidades) *
            100
          : 0,
      estado: "abierto",
    });
  };

  // NUEVA FUNCIÓN: Cargar honorarios CON sistema de actualización
  const cargarDatosHonorariosConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.honorarios) {
      setDatosHonorarios({ error: "No hay honorarios configurados" });
      return;
    }

    // Obtener pagos de honorarios (MISMA LÓGICA EXISTENTE)
    const { data: pagosHonorarios1, error: error1 } = await supabase
      .from("entradas")
      .select(
        `
     fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante,
     conceptos_entrada!inner(nombre)
   `
      )
      .eq("proyecto_id", proyecto.id)
      .ilike("conceptos_entrada.nombre", "%honorario%")
      .eq('activo', true)
      .is('deleted_at', null)
      .order("fecha", { ascending: true });

    let pagosHonorarios = pagosHonorarios1 || [];

    // Método 2 si no encuentra nada
    if ((!pagosHonorarios || pagosHonorarios.length === 0) && !error1) {
      const { data: todosPagos, error: error2 } = await supabase
        .from("entradas")
        .select(
          `
       fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante,
       concepto, conceptos_entrada(nombre)
     `
        )
        .eq("proyecto_id", proyecto.id)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (!error2 && todosPagos) {
        pagosHonorarios = todosPagos.filter((pago) => {
          const nombreConcepto =
            pago.conceptos_entrada?.nombre || pago.concepto || "";
          return nombreConcepto.toLowerCase().includes("honorario");
        });
      }
    }

    if (error1) {
      console.error("Error cargando pagos honorarios:", error1);
      setDatosHonorarios({ error: "Error cargando pagos" });
      return;
    }

    // Calcular con sistema de actualización (IGUAL QUE HONORARIOS/MO)
    const resultado = await calcularConSistemaActualizacion(
      proyecto.honorarios,
      proyecto.moneda_honorarios,
      proyecto.metodo_actualizacion_honorarios,
      proyecto.fecha_inicio_actualizacion_honorarios,
      pagosHonorarios || [],
      "honorarios",
      proyecto.honorarios_se_actualizan
    );

    const totalActualizadoHonorarios =
      (resultado.resumenFinal?.pagosAcumulados || 0) +
      (resultado.resumenFinal?.saldoPendiente || 0);

    setDatosHonorarios({
      nombre: "Honorarios Profesionales",
      monto_inicial: totalActualizadoHonorarios, // ✅ NUEVA LÍNEA
      moneda: proyecto.moneda_honorarios,
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      porcentaje_pagado:
        resultado.resumenFinal?.totalUnidades > 0
          ? ((resultado.resumenFinal?.unidadesGastadas || 0) /
              resultado.resumenFinal.totalUnidades) *
            100
          : (resultado.resumenFinal?.pagosAcumulados || 0) +
              (resultado.resumenFinal?.saldoPendiente || 0) >
            0
          ? ((resultado.resumenFinal?.pagosAcumulados || 0) /
              ((resultado.resumenFinal?.pagosAcumulados || 0) +
                (resultado.resumenFinal?.saldoPendiente || 0))) *
            100
          : 0,
      estado: "abierto",
    });
  };

  // NUEVA FUNCIÓN: Cargar mano de obra CON sistema de actualización
  const cargarDatosManoObraConActualizacion = async () => {
    const proyecto = proyectoSeleccionado;

    if (!proyecto.monto_mano_obra) {
      setDatosManoObra({ error: "No hay monto de mano de obra configurado" });
      return;
    }

    // Obtener cajas de mano de obra del proyecto (MISMA LÓGICA EXISTENTE)
    const { data: cajasManoObra } = await supabase
      .from("cajas")
      .select("id")
      .eq("proyecto_id", proyecto.id)
      .eq('activo', true)
      .is('deleted_at', null)
      .eq("tipo_caja", "mano_obra");

    const idsCajasManoObra = cajasManoObra?.map((c) => c.id) || [];

    let pagos = [];
    if (idsCajasManoObra.length > 0) {
      const { data: pagosMO, error: errorPagos } = await supabase
        .from("entradas")
        .select(
          "fecha, monto, moneda, tipo_cambio, concepto_id, comprobante_url, tiene_comprobante, conceptos_entrada!inner(nombre)"
        )
        .eq("proyecto_id", proyecto.id)
        .in("caja_destino_id", idsCajasManoObra)
        .eq('activo', true)
        .is('deleted_at', null)
        .order("fecha", { ascending: true });

      if (!errorPagos) {
        pagos = pagosMO || [];
      }
    }

    // Calcular con sistema de actualización (IGUAL QUE HONORARIOS/MO)
    const resultado = await calcularConSistemaActualizacion(
      proyecto.monto_mano_obra,
      proyecto.moneda_mano_obra,
      proyecto.metodo_actualizacion_mano_obra,
      proyecto.fecha_inicio_actualizacion_mano_obra,
      pagos,
      "mano_obra",
      proyecto.mano_obra_se_actualiza
    );

    const totalActualizadoMO =
      (resultado.resumenFinal?.pagosAcumulados || 0) +
      (resultado.resumenFinal?.saldoPendiente || 0);

    setDatosManoObra({
      nombre: "Mano de Obra",
      monto_inicial: proyecto.monto_mano_obra, // ✅ CORREGIDO: monto original
      monto_actualizado: totalActualizadoMO, // ✅ NUEVO: monto actualizado
      moneda: proyecto.moneda_mano_obra,
      total_pagado: resultado.resumenFinal?.pagosAcumulados || 0,
      saldo_pendiente: resultado.resumenFinal?.saldoPendiente || 0,
      porcentaje_pagado:
        resultado.resumenFinal?.totalUnidades > 0
          ? ((resultado.resumenFinal?.unidadesGastadas || 0) /
              resultado.resumenFinal.totalUnidades) *
            100
          : 0,
      estado: "abierto",
    });
  };

  // FUNCIONES HELPER COPIADAS EXACTAMENTE DE HONORARIOS/MO

  // FUNCIÓN: Formatear fecha sin conversión de timezone
  const formatearFechaSinTimezone = (fechaString) => {
    if (!fechaString) return new Date().toISOString().split("T")[0];
    if (fechaString.includes("T")) {
      return fechaString.split("T")[0];
    }
    return fechaString;
  };

  // FUNCIÓN: Detectar si es método personalizado
  const esMetodoPersonalizado = (metodo) => {
    if (!metodo) return false;
    return !["CAC_GENERAL", "CAC_MATERIALES", "CAC_MANO_OBRA"].includes(metodo);
  };
  // FUNCIÓN PRINCIPAL: CALCULAR CON SISTEMA DE ACTUALIZACIÓN
  const calcularConSistemaActualizacion = async (
    montoInicial,
    monedaInicial,
    metodoActualizacion,
    fechaInicio,
    pagos,
    tipo,
    seActualiza
  ) => {
    console.log(`🔄 CALCULANDO CON SISTEMA:`, {
      montoInicial,
      seActualiza,
      metodoActualizacion,
      tipo,
      esPersonalizado: esMetodoPersonalizado(metodoActualizacion),
    });

    // SI NO SE ACTUALIZA: Cálculo simple sin índices
    if (!seActualiza || !metodoActualizacion || !fechaInicio) {
      console.log(`❌ SIN ACTUALIZACIÓN - Cálculo directo`);

      const totalPagadoConvertido = pagos.reduce((sum, pago) => {
        return (
          sum +
          convertirMonto(
            pago.monto,
            pago.moneda,
            monedaInicial,
            pago.tipo_cambio
          )
        );
      }, 0);

      return {
        evolucion: [
          {
            mes: "Presupuesto",
            fecha: new Date().toISOString().split("T")[0],
            montoInicial: parseFloat(montoInicial),
            totalPagado: totalPagadoConvertido,
            saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
            observaciones: "Sin actualización configurada",
          },
        ],
        resumenFinal: {
          montoActualizado: parseFloat(montoInicial),
          pagosAcumulados: totalPagadoConvertido,
          saldoPendiente: parseFloat(montoInicial) - totalPagadoConvertido,
        },
      };
    }

    // CON ACTUALIZACIÓN: Lógica de unidades
    console.log(`✅ CON ACTUALIZACIÓN - Sistema: ${metodoActualizacion}`);

    // CORREGIR FECHA SIN TIMEZONE
    const fechaInicioSinTZ = formatearFechaSinTimezone(fechaInicio);
    const fechaInicioDate = new Date(fechaInicioSinTZ + "T00:00:00");
    const mesInicio = formatearFechaParaIndice(fechaInicioDate);

    console.log(
      `📅 FECHA INICIO CORREGIDA: ${fechaInicio} → ${fechaInicioSinTZ} → ${mesInicio}`
    );

    // 1. OBTENER ÍNDICE INICIAL (del mes de inicio del presupuesto)
    const indiceInicial = await obtenerIndicePorFecha(
      mesInicio,
      metodoActualizacion,
      tipo
    );

    if (!indiceInicial) {
      if (esMetodoPersonalizado(metodoActualizacion)) {
        console.log(
          `⚠️ Método personalizado "${metodoActualizacion}" sin valor para ${mesInicio} - USANDO VALOR POR DEFECTO`
        );

        const indiceDefecto = 1000;

        return {
          seActualiza: true,
          metodoActualizacion: metodoActualizacion,
          requiereConfiguracion: true,
          indiceDefectoUsado: indiceDefecto,
          evolucion: [
            {
              mes: "Configuración Pendiente",
              fecha: mesInicio,
              observaciones: `Sistema "${metodoActualizacion}" requiere configuración de índices. Se usa valor temporal de ${indiceDefecto}.`,
            },
          ],
          resumenFinal: {
            montoActualizado: parseFloat(montoInicial),
            pagosAcumulados: pagos.reduce(
              (sum, pago) =>
                sum +
                convertirMonto(
                  pago.monto,
                  pago.moneda,
                  monedaInicial,
                  pago.tipo_cambio
                ),
              0
            ),
            saldoPendiente:
              parseFloat(montoInicial) -
              pagos.reduce(
                (sum, pago) =>
                  sum +
                  convertirMonto(
                    pago.monto,
                    pago.moneda,
                    monedaInicial,
                    pago.tipo_cambio
                  ),
                0
              ),
            unidades: parseFloat(montoInicial) / indiceDefecto,
            ultimoIndiceUsado: indiceDefecto,
            totalUnidades: parseFloat(montoInicial) / indiceDefecto,
            unidadesGastadas: 0,
          },
        };
      } else {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE CAC OFICIAL para ${mesInicio}`);
        return {
          error: `No se encontró índice CAC para ${mesInicio} con el método ${metodoActualizacion}.`,
        };
      }
    }

    console.log(
      `📊 ÍNDICE INICIAL (${mesInicio}): ${indiceInicial.toFixed(2)}`
    );

    // 2. CONVERTIR PRESUPUESTO A UNIDADES
    const unidadesSistema = parseFloat(montoInicial) / indiceInicial;
    console.log(`🔢 UNIDADES SISTEMA: ${unidadesSistema.toFixed(2)} unidades`);

    // 3. PROCESAR PAGOS MES A MES
    const evolucion = [];
    const pagosPorMes = agruparPagosPorMes(pagos, monedaInicial);

    let unidadesGastadas = 0;
    const fechaHoy = new Date();
    let mesActual = new Date(
      fechaInicioDate.getFullYear(),
      fechaInicioDate.getMonth(),
      1
    );

    // OBTENER EL ÚLTIMO ÍNDICE REAL DISPONIBLE PARA CONSISTENCIA
    const ultimoIndiceReal = await obtenerUltimoIndiceRealDisponible(
      metodoActualizacion,
      tipo
    );

    while (mesActual <= fechaHoy) {
      const mesString = formatearFechaParaIndice(mesActual);
      const pagosDelMes = pagosPorMes[mesString] || [];

      // Índice para este mes
      let indiceDelMes = await obtenerIndicePorFecha(
        mesString,
        metodoActualizacion,
        tipo
      );
      let esProvisorio = false;

      // Si no hay índice para este mes, usar el último real disponible (CONSISTENTE)
      if (!indiceDelMes) {
        indiceDelMes = ultimoIndiceReal;
        esProvisorio = true;
      }

      if (!indiceDelMes) {
        console.error(`❌ NO SE ENCONTRÓ ÍNDICE para ${mesString}`);
        break;
      }

      // Convertir pagos del mes a unidades
      let unidadesDelMes = 0;
      const pagosDetallados = pagosDelMes.map((pago) => {
        const unidadesPago = pago.montoConvertido / indiceDelMes;
        unidadesDelMes += unidadesPago;
        return {
          ...pago,
          indiceUsado: indiceDelMes,
          unidades: unidadesPago,
          esProvisorio: esProvisorio,
        };
      });

      unidadesGastadas += unidadesDelMes;

      // Saldo en unidades y en pesos
      const unidadesRestantes = unidadesSistema - unidadesGastadas;
      const saldoEnPesos = unidadesRestantes * indiceDelMes;

      evolucion.push({
        mes: formatearMesAno(mesActual),
        fecha: mesString,
        indiceUsado: indiceDelMes,
        esProvisorio: esProvisorio,
        unidadesDelMes: unidadesDelMes,
        unidadesGastadas: unidadesGastadas,
        unidadesRestantes: unidadesRestantes,
        saldoEnPesos: saldoEnPesos,
        pagosDelMes: pagosDetallados,
        cantidadPagos: pagosDelMes.length,
        observaciones: esProvisorio
          ? `Índice provisorio (último disponible)`
          : `Índice oficial del mes`,
      });

      mesActual.setMonth(mesActual.getMonth() + 1);
    }

    // 4. CALCULAR TOTALES FINALES
    const ultimoIndiceDisponible = ultimoIndiceReal;

    // Recalcular saldo final con el último índice disponible
    const unidadesFinales = unidadesSistema - unidadesGastadas;
    const saldoFinalEnPesos = unidadesFinales * ultimoIndiceDisponible;
    const totalPagadoEnPesos = pagos.reduce((sum, pago) => {
      return (
        sum +
        convertirMonto(pago.monto, pago.moneda, monedaInicial, pago.tipo_cambio)
      );
    }, 0);

    console.log(`💰 RESULTADO FINAL:`, {
      unidadesSistema: unidadesSistema.toFixed(2),
      unidadesGastadas: unidadesGastadas.toFixed(2),
      unidadesRestantes: unidadesFinales.toFixed(2),
      ultimoIndice: ultimoIndiceDisponible.toFixed(2),
      saldoFinalEnPesos: saldoFinalEnPesos.toFixed(0),
    });

    return {
      unidadesSistema: unidadesSistema,
      ultimoIndiceDisponible: ultimoIndiceDisponible,
      evolucion: evolucion,
      resumenFinal: {
        montoActualizado: unidadesFinales * ultimoIndiceDisponible,
        pagosAcumulados: totalPagadoEnPesos,
        saldoPendiente: saldoFinalEnPesos,
        unidades: unidadesFinales,
        ultimoIndiceUsado: ultimoIndiceDisponible,
        totalUnidades: unidadesSistema,
        unidadesGastadas: unidadesGastadas,
      },
    };
  };
  // FUNCIÓN: OBTENER ÍNDICE POR FECHA (CAC oficial o personalizado)
  const obtenerIndicePorFecha = async (fecha, metodo, tipo) => {
    if (!metodo) return null;

    console.log(
      `🔍 BUSCANDO ÍNDICE: fecha=${fecha}, metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    // PRIMERO: Buscar si hay valor personalizado (override)
    try {
      const { data: valorPersonalizado, error } = await supabase
        .from("valores_personalizados_actualizacion")
        .select("coeficiente")
        .eq("proyecto_id", proyectoSeleccionado.id)
        .eq("tipo", tipo)
        .eq("fecha_aplicacion", fecha)
        .maybeSingle();

      if (!error && valorPersonalizado) {
        console.log(
          `🎛️ VALOR PERSONALIZADO encontrado para ${fecha}:`,
          valorPersonalizado.coeficiente
        );
        return parseFloat(valorPersonalizado.coeficiente);
      }
    } catch (err) {
      console.error("Error buscando valor personalizado:", err);
    }

    // Si es método personalizado pero no hay valor, devolver null
    if (esMetodoPersonalizado(metodo)) {
      console.log(
        `⚠️ Método personalizado "${metodo}" pero no hay valor configurado para ${fecha}`
      );
      return null;
    }

    // Buscar en índices CAC oficiales
    const indice = indicesCAC.find((i) => i.fecha === fecha);
    if (!indice) {
      console.log(`❌ No hay CAC oficial para ${fecha}`);
      return null;
    }

    let valor = null;
    switch (metodo) {
      case "CAC_GENERAL":
        valor = parseFloat(indice.cac_general);
        break;
      case "CAC_MATERIALES":
        valor = parseFloat(indice.cac_materiales);
        break;
      case "CAC_MANO_OBRA":
        valor = parseFloat(indice.cac_mano_obra);
        break;
    }

    console.log(`📊 CAC OFICIAL para ${fecha} (${metodo}): ${valor}`);
    return valor;
  };

  // FUNCIÓN: OBTENER ÚLTIMO ÍNDICE REAL DISPONIBLE
  const obtenerUltimoIndiceRealDisponible = async (metodo, tipo) => {
    console.log(
      `🔍 BUSCANDO ÚLTIMO ÍNDICE DISPONIBLE: metodo=${metodo}, tipo=${tipo}, esPersonalizado=${esMetodoPersonalizado(
        metodo
      )}`
    );

    if (esMetodoPersonalizado(metodo)) {
      // Para personalizados, buscar el último valor cargado
      try {
        const { data: ultimoPersonalizado, error } = await supabase
          .from("valores_personalizados_actualizacion")
          .select("coeficiente, fecha_aplicacion")
          .eq("proyecto_id", proyectoSeleccionado.id)
          .eq("tipo", tipo)
          .order("fecha_aplicacion", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && ultimoPersonalizado) {
          console.log(
            `🔍 ÚLTIMO PERSONALIZADO: ${ultimoPersonalizado.fecha_aplicacion} = ${ultimoPersonalizado.coeficiente}`
          );
          return parseFloat(ultimoPersonalizado.coeficiente);
        }
      } catch (err) {
        console.error("Error buscando último personalizado:", err);
      }

      console.log(
        `❌ NO SE ENCONTRÓ NINGÚN VALOR PERSONALIZADO para método ${metodo}`
      );
      return null;
    }

    // Para CAC, buscar el último disponible
    const indicesOrdenados = [...indicesCAC].sort(
      (a, b) => new Date(b.fecha) - new Date(a.fecha)
    );

    for (const indice of indicesOrdenados) {
      let valor = null;
      switch (metodo) {
        case "CAC_GENERAL":
          valor = parseFloat(indice.cac_general);
          break;
        case "CAC_MATERIALES":
          valor = parseFloat(indice.cac_materiales);
          break;
        case "CAC_MANO_OBRA":
          valor = parseFloat(indice.cac_mano_obra);
          break;
      }

      if (valor) {
        console.log(
          `🔍 ÚLTIMO CAC DISPONIBLE: ${indice.fecha} = ${valor.toFixed(2)}`
        );
        return valor;
      }
    }

    console.error(`❌ NO SE ENCONTRÓ NINGÚN ÍNDICE CAC DISPONIBLE`);
    return null;
  };
  // FUNCIÓN: AGRUPAR PAGOS POR MES
  const agruparPagosPorMes = (pagos, monedaDestino) => {
    const grupos = {};

    pagos.forEach((pago) => {
      const fechaPago = new Date(
        formatearFechaSinTimezone(pago.fecha) + "T00:00:00"
      );
      const mesString = formatearFechaParaIndice(fechaPago);

      if (!grupos[mesString]) {
        grupos[mesString] = [];
      }

      grupos[mesString].push({
        ...pago,
        montoConvertido: convertirMonto(
          pago.monto,
          pago.moneda,
          monedaDestino,
          pago.tipo_cambio
        ),
      });
    });

    return grupos;
  };

  const formatearFechaParaIndice = (fecha) => {
    return fecha.toISOString().split("T")[0].substring(0, 7) + "-01";
  };

  const formatearMesAno = (fecha) => {
    return new Intl.DateTimeFormat("es-ES", {
      year: "numeric",
      month: "short",
    }).format(fecha);
  };

  const cargarGastosNoAsignados = async () => {
    try {
      const { data, error } = await supabase
        .from("salidas")
        .select(
          `
         *,
         etapas_obra(nombre),
                          subetapas_obra!left(nombre),
         conceptos_salida(nombre),
         proveedores_servicios(nombre)
       `
        )
        .eq("proyecto_id", proyectoSeleccionado.id)
        .is("presupuesto_inicial_id", null)
        .not("etapa_id", "is", null)
        .not("subetapa_id", "is", null)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("fecha", { ascending: false });

      if (!error) {
        // Excluir caja mano_obra
        const { data: cajas } = await supabase
          .from("cajas")
          .select("id, tipo_caja")
          .eq("constructora_id", constructora.id);
        const tipoById = new Map((cajas||[]).map(c=>[c.id,c.tipo_caja]));
        const filtrados = (data||[]).filter(g => tipoById.get(g.caja_origen_id) !== 'mano_obra');
        setGastosNoAsignados(filtrados);
      }
    } catch (err) {
      console.error("Error cargando gastos no asignados:", err);
    }
  };

  // Cargar gastos asignados para modal de información (solo activos)
  const cargarGastosAsignados = async (presupuestoId) => {
    try {
      const { data, error } = await supabase
        .from("salidas")
        .select(
          `
         *,
         conceptos_salida(nombre),
         proveedores_servicios(nombre)
       `
        )
        .eq("presupuesto_inicial_id", presupuestoId)
        .eq("activo", true)
        .is("deleted_at", null)
        .order("fecha", { ascending: false });

      if (!error) {
        // Excluir caja mano_obra de asignados al mostrar
        const { data: cajas } = await supabase
          .from("cajas")
          .select("id, tipo_caja")
          .eq("constructora_id", constructora.id);
        const tipoById = new Map((cajas||[]).map(c=>[c.id,c.tipo_caja]));
        const filtrados = (data||[]).filter(g => tipoById.get(g.caja_origen_id) !== 'mano_obra');
        setGastosAsignados(filtrados);
      }
    } catch (err) {
      console.error("Error cargando gastos asignados:", err);
      setGastosAsignados([]);
    }
  };

  // FUNCIÓN MODIFICADA: Agrupar presupuestos por etapa con valores actualizados
  const agruparPresupuestosPorEtapa = (presupuestos) => {
    console.log(
      "🔍 AGRUPANDO - Presupuestos recibidos:",
      presupuestos?.length || 0
    );
    console.log("🔍 AGRUPANDO - Etapas disponibles:", etapas?.length || 0);

    // DEBUG: Inspeccionar cada presupuesto
    presupuestos.forEach((presupuesto, index) => {
      console.log(`🔍 Presupuesto ${index}:`, {
        esValido: !!presupuesto,
        tieneEtapaId: !!presupuesto?.etapa_id,
        etapaId: presupuesto?.etapa_id,
        nombre: presupuesto?.nombre,
        montoOriginal: presupuesto?.monto_presupuestado,
        montoActualizado: presupuesto?.monto_actualizado,
        usaCAC: presupuesto?.usa_cac,
      });
    });

    // Si no hay presupuestos, limpiar etapas agrupadas
    if (!presupuestos || presupuestos.length === 0) {
      console.log("❌ No hay presupuestos para agrupar");
      setEtapasAgrupadas([]);
      return;
    }

    const etapasMap = new Map();

    // PASO 1: Crear etapas dinámicamente desde los presupuestos
    presupuestos.forEach((presupuesto, index) => {
      // VALIDACIÓN: Verificar que el presupuesto existe y tiene etapa_id
      if (
        !presupuesto ||
        typeof presupuesto !== "object" ||
        !presupuesto.etapa_id
      ) {
        console.log("⚠️ Presupuesto inválido encontrado:", presupuesto);
        return; // Saltar este presupuesto
      }

      const etapaId = presupuesto.etapa_id;

      if (!etapasMap.has(etapaId)) {
        // Crear etapa dinámicamente
        const etapaNombre =
          presupuesto.etapa || presupuesto.etapas_obra?.nombre || `Etapa ${etapaId}`;
        const etapaOrden = presupuesto.orden_etapa || presupuesto.etapas_obra?.orden || index;

        console.log("🏗️ Creando etapa:", etapaNombre);

        etapasMap.set(etapaId, {
          id: etapaId,
          nombre: etapaNombre,
          orden: etapaOrden,
          color: ETAPA_COLORS[etapasMap.size % ETAPA_COLORS.length],
          presupuestos: [],
          monto_total: 0,
          monto_total_actualizado: 0, // NUEVO CAMPO
          total_gastado: 0,
          saldo_pendiente: 0,
          saldo_actualizado: 0, // NUEVO CAMPO
          porcentaje_ejecutado: 0,
        });
      }

      // Agregar presupuesto a la etapa
      const etapa = etapasMap.get(etapaId);
      etapa.presupuestos.push(presupuesto);

      // Usar valores actualizados cuando estén disponibles
      const montoParaTotal =
        presupuesto.monto_actualizado || presupuesto.monto_presupuestado || 0;
      const saldoParaTotal =
        presupuesto.saldo_actualizado || presupuesto.saldo_pendiente || 0;

      etapa.monto_total += presupuesto.monto_presupuestado || 0; // Original
      etapa.monto_total_actualizado += montoParaTotal; // Actualizado
      etapa.total_gastado += presupuesto.total_gastado || 0;
      etapa.saldo_pendiente += presupuesto.saldo_pendiente || 0; // Original
      etapa.saldo_actualizado += saldoParaTotal; // Actualizado
    });

    // PASO 2: Calcular porcentajes de cada etapa usando valores actualizados
    etapasMap.forEach((etapa) => {
      const montoParaCalculo =
        etapa.monto_total_actualizado || etapa.monto_total;
      if (montoParaCalculo > 0) {
        etapa.porcentaje_ejecutado =
          (etapa.total_gastado / montoParaCalculo) * 100;
      }
      // Agregar alias para compatibilidad
      etapa.monto_presupuestado = etapa.monto_total;
      etapa.monto_inicial = etapa.monto_total;
    });

    // PASO 3: Convertir a array (TODAS las etapas que tienen presupuestos)
    const etapasArray = Array.from(etapasMap.values()).sort(
      (a, b) => (a.orden || 0) - (b.orden || 0)
    );

    console.log("✅ RESULTADO - Etapas agrupadas:", etapasArray.length);
    etapasArray.forEach((etapa) => {
      console.log(
        `📊 ${etapa.nombre}: ${etapa.presupuestos.length} presupuestos`,
        `Original: $${etapa.monto_total.toLocaleString()}`,
        `Actualizado: $${etapa.monto_total_actualizado.toLocaleString()}`
      );
    });

    setEtapasAgrupadas(etapasArray);
  };

  // Función auxiliar para convertir montos (reutilizada de HonorariosMO)
  const convertirMonto = (
    monto,
    monedaOrigen,
    monedaDestino,
    tipoCambio = null
  ) => {
    const montoNum = parseFloat(monto || 0);

    if (monedaOrigen === monedaDestino) {
      return montoNum;
    }

    const tc = tipoCambio || cotizacionDolar || 1000;

    if (monedaOrigen === "USD" && monedaDestino === "ARS") {
      return montoNum * tc;
    } else if (monedaOrigen === "ARS" && monedaDestino === "USD") {
      return montoNum / tc;
    }

    return montoNum;
  };

  const formatearMoneda = (monto, moneda) => {
    // Convertir a la moneda de vista seleccionada
    const montoConvertido = convertirMonto(
      monto,
      moneda,
      monedaVista,
      cotizacionDolar
    );
    const numero = parseFloat(montoConvertido || 0);

    if (isNaN(numero)) {
      return "$0.00";
    }

    const simbolo = monedaVista === "USD" ? "US$" : "$";
    return `${simbolo}${numero.toLocaleString("es-AR", {
      maximumFractionDigits: 2,
    })}`;
  };

  // FUNCIÓN MODIFICADA: Calcular totales globales con valores actualizados
  const calcularTotalesGlobales = () => {
    // Usar el total del presupuesto actualizado como base
    const totalPresupuestado = calcularTotalPresupuestoActualizadoOptimizado();

    let totalGastado = 0;
    let totalAhorros = 0;
    let totalSobregartos = 0;

    // Calcular gastado de honorarios
    if (datosHonorarios && typeof datosHonorarios === "object") {
      const pagadoHonorarios = datosHonorarios.total_pagado || 0;
      const monedaHonorarios = datosHonorarios.moneda || "ARS";

      totalGastado += convertirMonto(
        pagadoHonorarios,
        monedaHonorarios,
        monedaVista,
        cotizacionDolar
      );
    }

    // Calcular gastado de mano de obra
    if (
      datosManoObra &&
      typeof datosManoObra === "object" &&
      proyectoSeleccionado?.mano_obra_separada
    ) {
      const pagadoMO = datosManoObra.total_pagado || 0;
      const monedaMO = datosManoObra.moneda || "ARS";

      totalGastado += convertirMonto(
        pagadoMO,
        monedaMO,
        monedaVista,
        cotizacionDolar
      );
    }

    // Calcular gastado de llave en mano
    if (datosLlaveEnMano && typeof datosLlaveEnMano === "object") {
      const pagadoLlave = datosLlaveEnMano.total_pagado || 0;
      const monedaLlave = datosLlaveEnMano.moneda || "ARS";

      totalGastado += convertirMonto(
        pagadoLlave,
        monedaLlave,
        monedaVista,
        cotizacionDolar
      );
    }

    // ✅ CALCULAR AHORROS/SOBREGASTOS DESDE TABLA OPTIMIZADA
    if (presupuestosIniciales && Array.isArray(presupuestosIniciales)) {
      presupuestosIniciales.forEach((p) => {
        if (p && typeof p === "object") {
          const totalGastadoItem = p.total_gastado || 0;
          const moneda = p.moneda || "ARS";
          const tipoCambio = p.tipo_cambio || cotizacionDolar || 1000;
          const estaCerrado = p.estado === "cerrado";

          const gastadoConvertido = convertirMonto(
            totalGastadoItem,
            moneda,
            monedaVista,
            tipoCambio
          );

          totalGastado += gastadoConvertido;

          // ✅ SOLO SUMAR AHORROS/SOBREGASTOS SI ESTÁ CERRADO
          if (estaCerrado) {
            // Calcular diferencia directamente: monto_actualizado - total_gastado
            const montoActualizado = p.monto_actualizado || p.monto_presupuestado || 0;
            
            const presupuestadoConvertido = convertirMonto(
              montoActualizado,
              moneda,
              monedaVista,
              tipoCambio
            );
            
            const diferencia = presupuestadoConvertido - gastadoConvertido;
            
            if (diferencia > 0) {
              // Diferencia positiva = ahorro (gastó menos de lo presupuestado)
              totalAhorros += diferencia;
              console.log(`💰 Ahorro ${p.nombre}: ${diferencia} (presupuestado: ${presupuestadoConvertido}, gastado: ${gastadoConvertido})`);
            } else if (diferencia < 0) {
              // Diferencia negativa = sobregasto (gastó más de lo presupuestado)
              totalSobregartos += Math.abs(diferencia);
              console.log(`💸 Sobregasto ${p.nombre}: ${Math.abs(diferencia)} (presupuestado: ${presupuestadoConvertido}, gastado: ${gastadoConvertido})`);
            }
          }
        }
      });
    }

    const saldoPendiente = totalPresupuestado - totalGastado;
    const porcentajeEjecutado =
      totalPresupuestado > 0 ? (totalGastado / totalPresupuestado) * 100 : 0;
    const balanceNeto = totalAhorros - totalSobregartos;

    return {
      totalPresupuestado: totalPresupuestado || 0,
      totalGastado: totalGastado || 0,
      saldoPendiente: saldoPendiente || 0,
      porcentajeEjecutado: porcentajeEjecutado || 0,
      totalAhorros: totalAhorros || 0,
      totalSobregartos: totalSobregartos || 0,
      balanceNeto: balanceNeto || 0,
    };
  };
  // FUNCIÓN MEJORADA: Toggle estado con loading y recálculo automático
  const handleToggleEstado = async (presupuesto) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para modificar presupuestos iniciales."
      );
      return;
    }

    const nuevoEstado =
      presupuesto.estado === "abierto" ? "cerrado" : "abierto";
    const mensaje =
      nuevoEstado === "cerrado"
        ? "¿Cerrar presupuesto?"
        : "¿Reabrir presupuesto?";
    const descripcion =
      nuevoEstado === "cerrado"
        ? "Esto marcará el presupuesto como finalizado y calculará el resultado final."
        : "Podrás seguir asignando gastos a este presupuesto.";

    showCustomAlert("confirm", mensaje, descripcion, async () => {
      // Mostrar loading state
      setLoading(true);
      
      try {
        console.log("🔄 Cambiando estado del presupuesto:", presupuesto.id, "a", nuevoEstado);
        
        // 1. Actualizar estado en presupuestos_iniciales
        const { error } = await supabase
          .from("presupuestos_iniciales")
          .update({ estado: nuevoEstado })
          .eq("id", presupuesto.id);

        if (error) {
          console.error("Error actualizando estado:", error);
          showCustomAlert(
            "error",
            "Error",
            "Error al actualizar: " + error.message
          );
          return;
        }

        console.log("✅ Estado actualizado en presupuestos_iniciales");

        // 2. Simplemente recargar datos (la tabla se actualiza automáticamente con el estado)
        console.log("🔄 Recargando datos del proyecto...");
        await cargarDatosProyecto();
        
        // 3. Actualizar total optimizado
        await actualizarTotalOptimizado();
        
        showCustomAlert(
          "success",
          "Éxito",
          `Presupuesto ${
            nuevoEstado === "cerrado" ? "cerrado" : "reabierto"
          } correctamente.`
        );

      } catch (err) {
        console.error("Error:", err);
        showCustomAlert("error", "Error", "Error: " + err.message);
        
        // Fallback: recargar datos de todos modos
        try {
          await cargarDatosProyecto();
        } catch (reloadError) {
          console.error("Error recargando datos:", reloadError);
        }
      } finally {
        setLoading(false);
      }
    });
  };

  const handleEdit = (presupuesto) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para editar presupuestos iniciales."
      );
      return;
    }
    setEditingPresupuesto(presupuesto);
    setShowModal(true);
  };

  const handleViewInfo = async (presupuesto) => {
    setSelectedPresupuesto(presupuesto);
    await cargarGastosAsignados(presupuesto.id);
    setShowInfoModal(true);
  };

  const handleDelete = async (presupuestoId) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para eliminar presupuestos iniciales."
      );
      return;
    }

    showCustomAlert(
      "confirm",
      "¿Eliminar presupuesto?",
      "Se eliminará (soft) y podrás restaurarlo. Los gastos asignados quedarán sin vincular.",
      async () => {
        try {
          const { error } = await supabase.rpc('rpc_soft_delete_presupuesto_inicial', {
            p_id: presupuestoId,
            p_usuario_id: usuario?.id || null,
            p_razon: null,
          });

          if (error) {
            console.error("Error eliminando presupuesto:", error);
            showCustomAlert(
              "error",
              "Error",
              "Error al eliminar: " + error.message
            );
          } else {
            // ✅ Actualizar presupuestos totales automáticamente
            try {
              // Actualizar presupuesto total inicial
              await actualizarPresupuestoTotalInicial(proyectoSeleccionado.id);
              // Actualizar presupuesto total actualizado
              await actualizarPresupuestoTotalActualizado(proyectoSeleccionado.id);
            } catch (error) {
              console.error("⚠️ Error actualizando presupuestos totales:", error);
              // No mostrar error al usuario, solo log
            }
            
            showCustomAlert(
              "success",
              "Éxito",
              "Presupuesto eliminado correctamente"
            );
            cargarDatosProyecto();
          }
        } catch (err) {
          console.error("Error:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const handleRestore = async (presupuestoId) => {
    showCustomAlert('confirm', '¿Restaurar presupuesto?', 'Se restaurará el presupuesto inicial.', async () => {
      const { error } = await supabase.rpc('rpc_restore_presupuesto_inicial', {
        p_id: presupuestoId,
        p_usuario_id: usuario?.id || null,
      });
      if (error) {
        showCustomAlert('error', 'Error', 'Error al restaurar: ' + error.message);
      } else {
        try {
          if (proyectoSeleccionado?.id) {
            await actualizarPresupuestoTotalInicial(proyectoSeleccionado.id);
            await actualizarPresupuestoTotalActualizado(proyectoSeleccionado.id);
          }
        } catch (e) {
          console.error('⚠️ Error actualizando presupuestos totales tras restaurar:', e);
        }
        showCustomAlert('success', 'Éxito', 'Presupuesto restaurado correctamente');
        cargarDatosProyecto();
      }
    });
  };

  const handleAsignarGasto = async (gastoId, presupuestoId) => {
    try {
      const gasto = gastosNoAsignados.find(g => g.id === gastoId);
      const montoGasto = convertirMonto(gasto.monto, gasto.moneda, 'ARS', gasto.tipo_cambio);

    // UI optimista: sumar al gastado existente
    setGastosNoAsignados(prev => prev.filter(g => g.id !== gastoId));
    setPresupuestosIniciales(prev => prev.map(p => {
      if (p.id === presupuestoId) {
        const gastadoActual = p.pagado_ars || p.total_pagado || p.total_gastado || 0;
        const presupuestadoActual = p.total_actualizado_ars || p.monto_actualizado || p.monto_presupuestado || 0;
        const nuevoPagado = gastadoActual + montoGasto;
        const nuevoSaldo = Math.max(presupuestadoActual - nuevoPagado, 0);
        const nuevoPorcentaje = presupuestadoActual > 0 ? (nuevoPagado / presupuestadoActual * 100) : 0;
        return { 
          ...p, 
          pagado_ars: nuevoPagado, 
          total_pagado: nuevoPagado,
          saldo_ars: nuevoSaldo, 
          saldo_pendiente: nuevoSaldo,
          porcentaje_ejecutado: nuevoPorcentaje 
        };
      }
      return p;
    }));

      // Resolver presupuesto_inicial_id
      let presupuestoInicialIdReal = null;

      // 1) Intentar como si fuera un ID de PIA
      const { data: piaById, error: piaErrorById } = await supabase
        .from("presupuestos_iniciales_actualizados")
        .select("presupuesto_inicial_id")
        .eq("id", presupuestoId)
        .maybeSingle?.() || { data: null, error: null }; // compat

      if (piaById && piaById.presupuesto_inicial_id) {
        presupuestoInicialIdReal = piaById.presupuesto_inicial_id;
      }

      // 2) Si no lo encontramos, probar como si fuese el ID original
      if (!presupuestoInicialIdReal) {
        const altQuery = await supabase
          .from("presupuestos_iniciales_actualizados")
          .select("presupuesto_inicial_id")
          .eq("presupuesto_inicial_id", presupuestoId)
          .limit(1);
        const altData = altQuery?.data || [];
        if (altData.length > 0 && altData[0]?.presupuesto_inicial_id) {
          presupuestoInicialIdReal = altData[0].presupuesto_inicial_id;
        }
      }

      if (!presupuestoInicialIdReal) {
        console.error("Error obteniendo presupuesto_inicial_id de PIA:", piaErrorById);
        showCustomAlert("error", "Error", "Error obteniendo datos del presupuesto: JSON object requested, multiple (or no) rows returned");
        return;
      }

      // Update en DB
      const { error } = await supabase
        .from("salidas")
        .update({ presupuesto_inicial_id: presupuestoInicialIdReal })
        .eq("id", gastoId);

      if (error) {
        console.error("Error asignando gasto:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al asignar gasto: " + error.message
        );
      } else {
        try {
          // 🔄 Recalcular PIA y sincronizar totales para ver el impacto al instante
          if (proyectoSeleccionado?.id) {
            await recalcularPresupuestosInicialesActualizados(proyectoSeleccionado.id);
            await sincronizarDashboard(proyectoSeleccionado.id);
          }
        } catch (e) {
          console.error("⚠️ Error en recálculo inmediato tras asignación:", e);
        }
        await cargarDatosProyecto();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
      // rollback UI optimista si falla
      cargarDatosProyecto();
    }
  };

  const handleDesasignarGasto = async (gastoId) => {
    if (permisosCliente.esCliente) {
      showCustomAlert(
        "error",
        "Acción No Permitida",
        "No tienes permisos para desasignar gastos."
      );
      return;
    }

    showCustomAlert(
      "confirm",
      "¿Desasignar gasto?",
      "¿Estás seguro de quitar este gasto del presupuesto?",
      async () => {
        try {
          const { error } = await supabase
            .from("salidas")
            .update({ presupuesto_inicial_id: null })
            .eq("id", gastoId);

          if (error) {
            console.error("Error desasignando gasto:", error);
            showCustomAlert(
              "error",
              "Error",
              "Error al desasignar: " + error.message
            );
          } else {
            showCustomAlert(
              "success",
              "Éxito",
              "Gasto desasignado correctamente"
            );
            cargarDatosProyecto();
          }
        } catch (err) {
          console.error("Error:", err);
          showCustomAlert("error", "Error", "Error: " + err.message);
        }
      }
    );
  };

  const handleMoveEtapa = async (etapaId, direction) => {
    if (permisosCliente.esCliente) return;

    try {
      const currentIndex = etapasAgrupadas.findIndex((e) => e.id === etapaId);
      if (currentIndex === -1) return;

      let newOrder;
      if (direction === "up" && currentIndex > 0) {
        newOrder = etapasAgrupadas[currentIndex - 1].orden;
        await supabase
          .from("etapas_obra")
          .update({ orden: etapasAgrupadas[currentIndex].orden })
          .eq("id", etapasAgrupadas[currentIndex - 1].id);
      } else if (
        direction === "down" &&
        currentIndex < etapasAgrupadas.length - 1
      ) {
        newOrder = etapasAgrupadas[currentIndex + 1].orden;
        await supabase
          .from("etapas_obra")
          .update({ orden: etapasAgrupadas[currentIndex].orden })
          .eq("id", etapasAgrupadas[currentIndex + 1].id);
      } else {
        return;
      }

      await supabase
        .from("etapas_obra")
        .update({ orden: newOrder })
        .eq("id", etapaId);

      // Recargar datos
      await cargarEtapasYSubetapas();
      await cargarPresupuestosIniciales();
    } catch (err) {
      console.error("Error moviendo etapa:", err);
      showCustomAlert("error", "Error", "Error al reordenar: " + err.message);
    }
  };

  // NUEVA FUNCIÓN: Sincronizar presupuestos calculados
  const sincronizarPresupuestosCalculados = async () => {
    showCustomAlert(
      "confirm",
      "¿Sincronizar todos los presupuestos?",
      "Esto actualizará los presupuestos calculados de TODOS los proyectos en Supabase. Puede tomar unos minutos. ¿Continuar?",
      async () => {
        try {
          console.log("🔄 Iniciando sincronización masiva de presupuestos...");

          // Ejecutar sincronización de TODOS los proyectos
          const resultado = await sincronizarTodosLosProyectosConstructora(
            constructora.id
          );

          if (resultado.exitosos > 0) {
            console.log("✅ Sincronización legacy completada");
            
            // Actualizar tabla presupuestos_iniciales_actualizados para el proyecto actual
            if (proyectoSeleccionado?.id) {
              console.log("🔄 Actualizando tabla presupuestos_iniciales_actualizados...");
              const resultadoPIA = await recalcularPresupuestosInicialesActualizados(proyectoSeleccionado.id);
              
              if (resultadoPIA.success) {
                console.log("✅ Tabla presupuestos_iniciales_actualizados actualizada");
                // ✅ Sincronizar totales del Dashboard (PIA → presupuestos_totales_actualizados)
                try {
                  console.log("🔄 Sincronizando totales del Dashboard desde PIA...");
                  await sincronizarDashboard(proyectoSeleccionado.id);
                  console.log("✅ Totales del Dashboard sincronizados");
                } catch (e) {
                  console.error("⚠️ Error sincronizando Dashboard:", e);
                }
              } else {
                console.error("⚠️ Error actualizando tabla optimizada:", resultadoPIA.error);
              }
            }
            
            showCustomAlert(
              "success",
              "Sincronización completada",
              `Sincronización masiva completada:\n✅ ${resultado.exitosos} proyectos procesados\n❌ ${resultado.fallidos} proyectos con errores\n\n(Se sincronizaron todos los presupuestos de obra, honorarios y mano de obra + tabla optimizada)`
            );

            // Recargar datos del proyecto actual si hay uno seleccionado
            if (proyectoSeleccionado) {
              await cargarDatosProyecto();
              await actualizarTotalOptimizado();
            }
          } else {
            throw new Error(
              `Todos los proyectos fallaron. Errores: ${resultado.errores?.join(
                ", "
              )}`
            );
          }
        } catch (error) {
          console.error("❌ Error en sincronización masiva:", error);
          showCustomAlert(
            "error",
            "Error de sincronización",
            "Error al sincronizar presupuestos: " + error.message
          );
        }
      }
    );
  };
  // VALIDACIÓN: Calcular totales con protección contra errores
  let totalesGlobales;
  try {
    totalesGlobales = calcularTotalesGlobales();
  } catch (error) {
    console.error("❌ Error calculando totales globales:", error);
    totalesGlobales = {
      totalPresupuestado: 0,
      totalGastado: 0,
      saldoPendiente: 0,
      porcentajeEjecutado: 0,
      totalAhorros: 0,
      totalSobregartos: 0,
      balanceNeto: 0,
    };
  }
  // Preparar datos para las barras de progreso con valores actualizados
  const honorariosParaBarra = datosHonorarios
    ? {
        ...datosHonorarios,
        monto_inicial:
          datosHonorarios.total_pagado + datosHonorarios.saldo_pendiente,
        monto_presupuestado:
          datosHonorarios.total_pagado + datosHonorarios.saldo_pendiente,
        porcentaje_ejecutado: datosHonorarios.porcentaje_pagado,
        porcentaje_pagado: datosHonorarios.porcentaje_pagado,
        saldo_pendiente: datosHonorarios.saldo_pendiente,
      }
    : null;

  const manoObraParaBarra = datosManoObra
    ? {
        ...datosManoObra,
        monto_inicial:
          datosManoObra.total_pagado + datosManoObra.saldo_pendiente,
        monto_presupuestado:
          datosManoObra.total_pagado + datosManoObra.saldo_pendiente,
        porcentaje_ejecutado: datosManoObra.porcentaje_pagado,
        porcentaje_pagado: datosManoObra.porcentaje_pagado,
        saldo_pendiente: datosManoObra.saldo_pendiente,
      }
    : null;

  const llaveEnManoParaBarra = datosLlaveEnMano
    ? {
        ...datosLlaveEnMano,
        monto_inicial:
          datosLlaveEnMano.total_pagado + datosLlaveEnMano.saldo_pendiente,
        monto_presupuestado:
          datosLlaveEnMano.total_pagado + datosLlaveEnMano.saldo_pendiente,
        porcentaje_ejecutado: datosLlaveEnMano.porcentaje_pagado,
        porcentaje_pagado: datosLlaveEnMano.porcentaje_pagado,
        saldo_pendiente: datosLlaveEnMano.saldo_pendiente,
      }
    : null;
  // Si es cliente y no puede ver presupuestos iniciales, mostrar mensaje
  if (
    permisosCliente.esCliente &&
    !puedeAccederAVista(permisosCliente.permisos, "presupuestos")
  ) {
    return (
      <div
        style={{
          padding: isMobile ? "16px 12px 120px 12px" : "20px",
          maxWidth: "100%",
          margin: "0 auto",
          background: "#f8fafc",
          minHeight: "100vh",
          animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px solid #fef2f2",
            animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: "#fef2f2",
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <XCircle size={32} color="#dc2626" />
          </div>
          <h3
            style={{
              fontSize: "24px",
              color: "#dc2626",
              marginBottom: "16px",
              fontWeight: "600",
            }}
          >
            Acceso Restringido
          </h3>
          <p
            style={{
              color: "#64748b",
              fontSize: "16px",
              lineHeight: "1.5",
            }}
          >
            {obtenerMensajeRestriccion(permisosCliente, "presupuestos")}
          </p>
        </div>

        <CustomModal
          isOpen={customModal.isOpen}
          onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
          type={customModal.type}
          title={customModal.title}
          message={customModal.message}
          onConfirm={customModal.onConfirm}
          isMobile={isMobile}
        />
      </div>
    );
  }
  return (
    <div
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header unificado */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
            minHeight: "56px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: getVistaColor("presupuestos-iniciales").gradient,
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px rgba(37, 99, 235, 0.25)",
                flexShrink: 0,
              }}
            >
              <Target size={20} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                Presupuesto vs Realidad
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: 0,
                  fontWeight: "400",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {permisosCliente.esCliente
                  ? `Control del avance de tu proyecto ${permisosCliente.proyectoCodigo}`
                  : "Control visual del avance por etapas con actualización CAC"}
              </p>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            {/* Toggle moneda */}
            <div
              style={{
                display: "flex",
                background: "white",
                borderRadius: "12px",
                padding: "4px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
              }}
              title="Conversión: Presupuestos manuales usan CAC + TC original. Honorarios/MO usan TC actual."
            >
              <button
                onClick={() => setMonedaVista("ARS")}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: monedaVista === "ARS" ? "#2563eb" : "transparent",
                  color: monedaVista === "ARS" ? "white" : "#64748b",
                }}
              >
                ARS
              </button>
              <button
                onClick={() => setMonedaVista("USD")}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  background: monedaVista === "USD" ? "#2563eb" : "transparent",
                  color: monedaVista === "USD" ? "white" : "#64748b",
                }}
              >
                USD
              </button>
            </div>

            {/* Solo mostrar botones si no es cliente */}
            {permisosCliente.esCliente === false && (
              <>
                <button
                  onClick={() => {
                    setEditingPresupuesto(null);
                    setShowModal(true);
                  }}
                  style={{
                    background: getVistaColor("presupuestos-iniciales")
                      .gradient,
                    color: "white",
                    border: "none",
                    padding: isMobile ? "12px 20px" : "14px 24px",
                    borderRadius: "12px",
                    fontSize: isMobile ? "14px" : "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    flexShrink: 0,
                    minWidth: "max-content",
                    whiteSpace: "nowrap",
                  }}
                  className="button-hover"
                >
                  <Plus size={16} />
                  Nueva Cotización
                </button>

                {/* Botón de sincronización */}
                <button
                  onClick={sincronizarPresupuestosCalculados}
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "white",
                    border: "none",
                    padding: isMobile ? "12px 20px" : "14px 24px",
                    borderRadius: "12px",
                    fontSize: isMobile ? "14px" : "15px",
                    fontWeight: "600",
                    cursor: "pointer",
                    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    flexShrink: 0,
                    minWidth: "max-content",
                    whiteSpace: "nowrap",
                  }}
                  className="button-hover"
                  title="Sincronizar presupuestos calculados en Supabase"
                >
                  <Zap size={16} />
                  Sincronizar
                </button>
              </>
            )}
          </div>
        </div>

        {/* Selector de proyecto - Solo mostrar si no es cliente */}
        {!permisosCliente.esCliente && (
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}
            >
              Proyecto
            </label>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
              {/* Campo de búsqueda mejorado - PRIMERO */}
              <div style={{ 
                flex: "1 1 auto",
                minWidth: isMobile ? "100%" : "200px", 
                marginBottom: isMobile ? "12px" : "0"
              }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={16}
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#64748b",
                    zIndex: 1,
                  }}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar presupuesto por nombre, etapa, concepto..."
                  autoFocus={false}
                  autoComplete="off"
                  style={{
                    width: "100%",
                    padding: isMobile
                      ? "12px 16px 12px 44px"
                      : "10px 10px 10px 36px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: isMobile ? "16px" : "14px",
                    WebkitAppearance: "none",
                    appearance: "none",
                    background: "white",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    boxSizing: "border-box",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#3b82f6";
                    e.target.style.boxShadow = "0 0 0 3px rgba(59, 130, 246, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 2px 8px rgba(0,0,0,0.04)";
                  }}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm("")}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      color: "#64748b",
                      cursor: "pointer",
                      padding: "4px",
                      borderRadius: "4px",
                      fontSize: "16px",
                      fontWeight: "600",
                      width: "24px",
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#fee2e2";
                      e.target.style.color = "#dc2626";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.color = "#64748b";
                    }}
                  >
                    ×
                  </button>
                )}
                {searchTerm && (
                  <div style={{
                    position: "absolute",
                    bottom: "-8px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    fontSize: "11px",
                    color: "#64748b",
                    backgroundColor: "#f1f5f9",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    border: "1px solid #e2e8f0",
                    fontWeight: "500",
                    letterSpacing: "0.025em"
                  }}>
                    {presupuestosIniciales.length} resultado{presupuestosIniciales.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>
            </div>
            
            {/* Filtro Proyecto - SEGUNDO */}
            <div style={{ flex: "0 0 280px", minWidth: isMobile ? "100%" : "280px" }}>
              <CustomDropdown
                value={proyectoSeleccionado?.id || ""}
                onChange={(value) => {
                  const proyecto = proyectos.find((p) => p.id === value);
                  setProyectoSeleccionado(proyecto);
                  guardarProyectoSeleccionado(proyecto);
                }}
                options={proyectos.map((proyecto) => ({
                  value: proyecto.id,
                  label: `${proyecto.codigo} - ${proyecto.apellido_cliente}${
                    proyecto.obra_finalizada ? " (Finalizado)" : ""
                  }`,
                }))}
                placeholder="Selecciona un proyecto"
                icon={Building2}
              />
            </div>
            
            {/* Filtro Estado - TERCERO */}
            <div style={{ width: isMobile ? "100%" : "160px" }}>
              <CustomDropdown
                value={filterVigencia}
                onChange={setFilterVigencia}
                options={[
                  { value: 'activos', label: 'Activos' },
                  { value: 'eliminados', label: 'Eliminados' },
                ]}
                placeholder="Estado"
                icon={Filter}
              />
            </div>
            
            {/* Filtro Concepto - CUARTO */}
            <div style={{ width: isMobile ? "100%" : "260px" }}>
              <CustomDropdown
                value={filterConceptoId}
                onChange={setFilterConceptoId}
                options={[
                  { value: 'todos', label: 'Todos los conceptos' },
                  ...conceptosSalida.map(c => ({ value: c.id, label: c.nombre }))
                ]}
                placeholder="Filtrar por concepto"
                icon={Tag}
              />
            </div>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : !proyectoSeleccionado ? (
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            padding: isMobile ? "40px 20px" : "60px 40px",
            textAlign: "center",
            boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
            border: "2px dashed #cbd5e1",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: getVistaColor("presupuestos-iniciales").light,
              borderRadius: "32px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            <Target
              size={32}
              color={getVistaColor("presupuestos-iniciales").solid}
            />
          </div>
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1e293b",
              marginBottom: "8px",
            }}
          >
            No hay proyectos disponibles
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#64748b",
            }}
          >
            {permisosCliente.esCliente
              ? "No se encontró un proyecto asociado a tu email."
              : "Crea tu primer proyecto para gestionar presupuestos iniciales"}
          </p>
        </div>
      ) : loadingData ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "80px 20px",
            background: "white",
            borderRadius: "16px",
            border: "1px solid #f1f5f9",
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                border: "2px solid #e2e8f0",
                borderTop: "2px solid #3b82f6",
                borderRadius: "50%",
                margin: "0 auto 16px",
                animation: "spin 1s linear infinite",
              }}
            />
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                color: "#64748b",
                fontWeight: "500",
              }}
            >
              Cargando...
            </p>
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "24px",
            animation: "fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* Cards de resumen superior unificadas - SISTEMA ACTUALIZADO */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "20px",
              marginBottom: "8px",
            }}
          >
            {/* Tarjeta 1 - Presupuesto Inicial */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#eff6ff",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FileText size={20} color="#3b82f6" />
                  </div>
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#1e293b",
                      }}
                    >
                      Presupuesto Inicial
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      Valores al momento de cotización
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setShowInfoModalPresupuestoInicial(true)}
                    style={{
                      background: "#eff6ff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                      color: "#3b82f6",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#dbeafe";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#eff6ff";
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = "none";
                    }}
                    title="Ver información detallada"
                  >
                    <Info size={16} />
                  </button>
                  <button
                    onClick={() => setTarjetasColapsadas(!tarjetasColapsadas)}
                    style={{
                      background: "#eff6ff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                      color: "#3b82f6",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#dbeafe";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#eff6ff";
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = "none";
                    }}
                    title={
                      tarjetasColapsadas
                        ? "Expandir detalles"
                        : "Colapsar detalles"
                    }
                  >
                    <ChevronDown
                      size={16}
                      style={{
                        transform: tarjetasColapsadas
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                        transition:
                          "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </button>
                </div>
              </div>

              {/* Total visible cuando está colapsado */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0 0 0",
                  marginTop: "8px",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  maxHeight: tarjetasColapsadas ? "60px" : "0px",
                  opacity: tarjetasColapsadas ? 1 : 0,
                  transform: tarjetasColapsadas
                    ? "translateY(0px)"
                    : "translateY(-10px)",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    color: "#1e293b",
                    fontWeight: "700",
                  }}
                >
                  Total Presupuesto Inicial
                </span>
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: "#3b82f6",
                  }}
                >
                  {formatearMoneda(
                    calcularTotalPresupuestoInicial(),
                    monedaVista
                  )}
                </span>
              </div>

              {/* Contenido del presupuesto inicial */}
              <div
                style={{
                  display: "grid",
                  gap: "12px",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  maxHeight: tarjetasColapsadas ? "0px" : "600px",
                  opacity: tarjetasColapsadas ? 0 : 1,
                  transform: tarjetasColapsadas
                    ? "translateY(-10px)"
                    : "translateY(0px)",
                  marginTop: tarjetasColapsadas ? "0px" : "16px",
                }}
              >
                {/* Honorarios */}
                {datosHonorarios && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        Honorarios Profesionales
                      </span>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#3b82f6",
                        }}
                      >
                        {proyectoSeleccionado.tipo_obra ===
                        "administracion_porcentaje"
                          ? (() => {
                              // Sumar Mano de Obra y Presupuestos de Obra en la moneda de visualización
                              const manoObra =
                                proyectoSeleccionado.mano_obra_separada &&
                                proyectoSeleccionado.monto_mano_obra
                                  ? convertirMontoConTCProyecto(
                                      proyectoSeleccionado.monto_mano_obra,
                                      proyectoSeleccionado.moneda_mano_obra,
                                      monedaVista
                                    )
                                  : 0;
                              const presupuestosObra = (
                                presupuestosIniciales || []
                              ).reduce(
                                (sum, p) =>
                                  sum +
                                  convertirMonto(
                                    p.monto_presupuestado,
                                    p.moneda,
                                    monedaVista,
                                    p.tipo_cambio
                                  ),
                                0
                              );
                              const porcentaje =
                                parseFloat(
                                  proyectoSeleccionado.porcentaje_honorario || 0
                                ) / 100;
                              const honorarios =
                                (manoObra + presupuestosObra) * porcentaje;
                              return formatearMoneda(honorarios, monedaVista);
                            })()
                          : formatearMoneda(
                              convertirMontoConTCProyecto(
                                proyectoSeleccionado.honorarios,
                                proyectoSeleccionado.moneda_honorarios,
                                monedaVista
                              ),
                              monedaVista
                            )}
                      </span>
                    </div>
                    {/* Aclaración visual del cálculo */}
                    {proyectoSeleccionado.tipo_obra ===
                      "administracion_porcentaje" && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "2px",
                          textAlign: "right",
                        }}
                      >
                        (Mano de Obra + Presupuestos de Obra) ×{" "}
                        {proyectoSeleccionado.porcentaje_honorario || 0}%
                      </span>
                    )}
                  </div>
                )}

                {/* Mano de obra */}
                {datosManoObra && proyectoSeleccionado.mano_obra_separada && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Mano de Obra
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#3b82f6",
                      }}
                    >
                      {formatearMoneda(
                        convertirMontoConTCProyecto(
                          proyectoSeleccionado.monto_mano_obra,
                          proyectoSeleccionado.moneda_mano_obra,
                          monedaVista
                        ),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Llave en mano */}
                {datosLlaveEnMano && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Costo Total Obra
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#3b82f6",
                      }}
                    >
                      {formatearMoneda(
                        convertirMontoConTCProyecto(
                          proyectoSeleccionado.monto_total_llave_en_mano,
                          proyectoSeleccionado.moneda_llave_en_mano,
                          monedaVista
                        ),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Presupuestos manuales */}
                {presupuestosIniciales && presupuestosIniciales.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Presupuestos de Obra ({presupuestosIniciales.length})
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#3b82f6",
                      }}
                    >
                      {formatearMoneda(
                        presupuestosIniciales.reduce((total, p) => {
                          return (
                            total +
                            convertirMonto(
                              p.monto_presupuestado, // USAR VALORES ORIGINALES
                              p.moneda,
                              monedaVista,
                              p.tipo_cambio
                            )
                          );
                        }, 0),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0 0 0",
                    marginTop: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      fontWeight: "700",
                    }}
                  >
                    Total Presupuesto Inicial
                  </span>
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "800",
                      color: "#3b82f6",
                    }}
                  >
                    {formatearMoneda(
                      calcularTotalPresupuestoInicial(),
                      monedaVista
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Tarjeta 2 - Presupuesto con Actualizaciones */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      background: "#f0fdf4",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <TrendingUp size={20} color="#10b981" />
                  </div>
                  <div>
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "18px",
                        fontWeight: "700",
                        color: "#1e293b",
                      }}
                    >
                      Presupuesto Actualizado
                    </h3>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      Con indexaciones CAC y actualizaciones
                    </p>
                  </div>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    onClick={() => setShowInfoModalPresupuestoActualizado(true)}
                    style={{
                      background: "#f0fdf4",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                      color: "#10b981",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#dcfce7";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#f0fdf4";
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = "none";
                    }}
                    title="Ver información detallada"
                  >
                    <Info size={16} />
                  </button>
                  <button
                    onClick={() => setTarjetasColapsadas(!tarjetasColapsadas)}
                    style={{
                      background: "#f0fdf4",
                      border: "none",
                      borderRadius: "8px",
                      padding: "8px",
                      cursor: "pointer",
                      color: "#10b981",
                      transition: "all 0.2s ease",
                      outline: "none",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#dcfce7";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#f0fdf4";
                    }}
                    onFocus={(e) => {
                      e.target.style.outline = "none";
                    }}
                    title={
                      tarjetasColapsadas
                        ? "Expandir detalles"
                        : "Colapsar detalles"
                    }
                  >
                    <ChevronDown
                      size={16}
                      style={{
                        transform: tarjetasColapsadas
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                        transition:
                          "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    />
                  </button>
                </div>
              </div>

              {/* Total visible cuando está colapsado */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "16px 0 0 0",
                  marginTop: "8px",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  maxHeight: tarjetasColapsadas ? "60px" : "0px",
                  opacity: tarjetasColapsadas ? 1 : 0,
                  transform: tarjetasColapsadas
                    ? "translateY(0px)"
                    : "translateY(-10px)",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    color: "#1e293b",
                    fontWeight: "700",
                  }}
                >
                  Total Presupuesto Actualizado
                </span>
                <span
                  style={{
                    fontSize: "20px",
                    fontWeight: "800",
                    color: "#10b981",
                  }}
                >
                  {formatearMoneda(
                    calcularTotalPresupuestoActualizadoOptimizado(),
                    monedaVista
                  )}
                </span>
              </div>

              {/* Contenido del presupuesto actualizado */}
              <div
                style={{
                  display: "grid",
                  gap: "12px",
                  overflow: "hidden",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  maxHeight: tarjetasColapsadas ? "0px" : "600px",
                  opacity: tarjetasColapsadas ? 0 : 1,
                  transform: tarjetasColapsadas
                    ? "translateY(-10px)"
                    : "translateY(0px)",
                  marginTop: tarjetasColapsadas ? "0px" : "16px",
                }}
              >
                {/* Honorarios */}
                {datosHonorarios && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          color: "#64748b",
                          fontWeight: "500",
                        }}
                      >
                        Honorarios Profesionales
                      </span>
                      <span
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#10b981",
                        }}
                      >
                        {proyectoSeleccionado.tipo_obra ===
                        "administracion_porcentaje"
                          ? (() => {
                              // Sumar Mano de Obra y Presupuestos de Obra en la moneda de visualización
                              const manoObra =
                                proyectoSeleccionado.mano_obra_separada &&
                                proyectoSeleccionado.monto_mano_obra
                                  ? convertirMontoConTCProyecto(
                                      proyectoSeleccionado.monto_mano_obra,
                                      proyectoSeleccionado.moneda_mano_obra,
                                      monedaVista
                                    )
                                  : 0;
                              const presupuestosObra = (
                                presupuestosIniciales || []
                              ).reduce(
                                (sum, p) =>
                                  sum +
                                  convertirMonto(
                                    p.monto_presupuestado,
                                    p.moneda,
                                    monedaVista,
                                    p.tipo_cambio
                                  ),
                                0
                              );
                              const porcentaje =
                                parseFloat(
                                  proyectoSeleccionado.porcentaje_honorario || 0
                                ) / 100;
                              const honorarios =
                                (manoObra + presupuestosObra) * porcentaje;
                              return formatearMoneda(honorarios, monedaVista);
                            })()
                          : formatearMoneda(
                              convertirMonto(
                                datosHonorarios.total_pagado +
                                  datosHonorarios.saldo_pendiente,
                                datosHonorarios.moneda,
                                monedaVista,
                                cotizacionDolar
                              ),
                              monedaVista
                            )}
                      </span>
                    </div>
                    {/* Aclaración visual del cálculo */}
                    {proyectoSeleccionado.tipo_obra ===
                      "administracion_porcentaje" && (
                      <span
                        style={{
                          fontSize: "12px",
                          color: "#64748b",
                          marginTop: "2px",
                          textAlign: "right",
                        }}
                      >
                        (Mano de Obra + Presupuestos de Obra) ×{" "}
                        {proyectoSeleccionado.porcentaje_honorario || 0}%
                      </span>
                    )}
                  </div>
                )}

                {/* Mano de obra */}
                {datosManoObra && proyectoSeleccionado.mano_obra_separada && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Mano de Obra
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {formatearMoneda(
                        convertirMonto(
                          datosManoObra.total_pagado +
                            datosManoObra.saldo_pendiente,
                          datosManoObra.moneda,
                          monedaVista,
                          cotizacionDolar
                        ),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Llave en mano */}
                {datosLlaveEnMano && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Costo Total Obra
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {formatearMoneda(
                        convertirMonto(
                          datosLlaveEnMano.total_pagado +
                            datosLlaveEnMano.saldo_pendiente,
                          datosLlaveEnMano.moneda,
                          monedaVista,
                          cotizacionDolar
                        ),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Presupuestos manuales - MOSTRAR VALORES ACTUALIZADOS CON CAC */}
                {presupuestosIniciales && presupuestosIniciales.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom: "1px solid #f1f5f9",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Presupuestos de Obra ({presupuestosIniciales.length})
                      {presupuestosIniciales.some((p) => p.usa_cac) && (
                        <span
                          style={{
                            color: "#10b981",
                            fontSize: "12px",
                            marginLeft: "4px",
                          }}
                        >
                          ⚡ CAC
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#10b981",
                      }}
                    >
                      {formatearMoneda(
                        presupuestosIniciales.reduce((total, p) => {
                          // USAR VALORES ACTUALIZADOS CON CAC CUANDO ESTÉN DISPONIBLES
                          const montoParaCalculo =
                            p.monto_actualizado || p.monto_presupuestado;
                          // Si la moneda de visualización es USD, convertir desde ARS a USD usando cotizacionDolar actual
                          if (monedaVista === "USD") {
                            return (
                              total +
                              convertirMonto(
                                montoParaCalculo,
                                "ARS",
                                "USD",
                                cotizacionDolar
                              )
                            );
                          } else {
                            // Si es ARS, mostrar el monto tal cual
                            return total + montoParaCalculo;
                          }
                        }, 0),
                        monedaVista
                      )}
                    </span>
                  </div>
                )}

                {/* Total */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 0 0 0",
                    marginTop: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#1e293b",
                      fontWeight: "700",
                    }}
                  >
                    Total Presupuesto Actualizado
                  </span>
                  <span
                    style={{
                      fontSize: "20px",
                      fontWeight: "800",
                      color: "#10b981",
                    }}
                  >
                    {formatearMoneda(
                      calcularTotalPresupuestoActualizadoOptimizado(),
                      monedaVista
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Cards adicionales con métricas */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(4, 1fr)",
              gap: "16px",
              marginBottom: "8px",
            }}
          >
            {/* Total Ejecutado */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#fef2f2",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <TrendingDown size={16} color="#ef4444" />
              </div>
              <div
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  color: "#ef4444",
                  marginBottom: "4px",
                }}
              >
                {formatearMoneda(totalesGlobales.totalGastado, monedaVista)}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Ejecutado
              </div>
            </div>

            {/* Saldo Pendiente */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#fffbeb",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <Wallet size={16} color="#f59e0b" />
              </div>
              <div
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  color:
                    totalesGlobales.saldoPendiente >= 0 ? "#f59e0b" : "#ef4444",
                  marginBottom: "4px",
                }}
              >
                {formatearMoneda(totalesGlobales.saldoPendiente, monedaVista)}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Saldo
              </div>
            </div>

            {/* Progreso Global */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background: "#f3e8ff",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <BarChart3 size={16} color="#8b5cf6" />
              </div>
              <div
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  color: "#8b5cf6",
                  marginBottom: "4px",
                }}
              >
                {totalesGlobales.porcentajeEjecutado.toFixed(1)}%
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Progreso
              </div>
            </div>

            {/* Diferencia */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "20px",
                textAlign: "center",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 1.0s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  background:
                    calcularTotalPresupuestoActualizadoOptimizado() -
                      calcularTotalPresupuestoInicial() >=
                    0
                      ? "#f0fdf4"
                      : "#fef2f2",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 12px",
                }}
              >
                <ArrowUpDown
                  size={16}
                  color={
                    calcularTotalPresupuestoActualizadoOptimizado() -
                      calcularTotalPresupuestoInicial() >=
                    0
                      ? "#10b981"
                      : "#ef4444"
                  }
                />
              </div>
              <div
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "700",
                  color:
                    calcularTotalPresupuestoActualizadoOptimizado() -
                      calcularTotalPresupuestoInicial() >=
                    0
                      ? "#10b981"
                      : "#ef4444",
                  marginBottom: "4px",
                }}
              >
                {calcularTotalPresupuestoActualizadoOptimizado() -
                  calcularTotalPresupuestoInicial() >=
                0
                  ? "+"
                  : ""}
                {formatearMoneda(
                  calcularTotalPresupuestoActualizadoOptimizado() -
                    calcularTotalPresupuestoInicial(),
                  monedaVista
                )}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#64748b",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Actualización
              </div>
            </div>
          </div>
          {/* Card especial de Ahorros/Sobregartos */}
          {(totalesGlobales.totalAhorros > 0 ||
            totalesGlobales.totalSobregartos > 0) && (
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                padding: "24px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
                border: "1px solid #f1f5f9",
                animation: "slideInRight 0.9s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Calculator size={16} />
                Balance Financiero del Proyecto
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap: "16px",
                }}
              >
                {/* Ahorros */}
                <div
                  style={{
                    background: "#f0fdf4",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #bbf7d0",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <Award size={16} color="#059669" />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#059669",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Ahorros Totales
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "700",
                      color: "#059669",
                    }}
                  >
                    {formatearMoneda(totalesGlobales.totalAhorros, monedaVista)}
                  </div>
                </div>

                {/* Sobregartos */}
                <div
                  style={{
                    background: "#fef2f2",
                    padding: "16px",
                    borderRadius: "12px",
                    border: "1px solid #fecaca",
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <AlertCircle size={16} color="#dc2626" />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#dc2626",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Sobregastos
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "700",
                      color: "#dc2626",
                    }}
                  >
                    {formatearMoneda(
                      totalesGlobales.totalSobregartos,
                      monedaVista
                    )}
                  </div>
                </div>

                {/* Balance Neto */}
                <div
                  style={{
                    background:
                      totalesGlobales.balanceNeto >= 0 ? "#f0fdf4" : "#fef2f2",
                    padding: "16px",
                    borderRadius: "12px",
                    border: `1px solid ${
                      totalesGlobales.balanceNeto >= 0 ? "#bbf7d0" : "#fecaca"
                    }`,
                    textAlign: "center",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "6px",
                      marginBottom: "8px",
                    }}
                  >
                    <TrendingUpIcon
                      size={16}
                      color={
                        totalesGlobales.balanceNeto >= 0 ? "#059669" : "#dc2626"
                      }
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        color:
                          totalesGlobales.balanceNeto >= 0
                            ? "#059669"
                            : "#dc2626",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Balance Neto
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "18px" : "20px",
                      fontWeight: "700",
                      color:
                        totalesGlobales.balanceNeto >= 0
                          ? "#059669"
                          : "#dc2626",
                    }}
                  >
                    {totalesGlobales.balanceNeto >= 0 ? "+" : ""}
                    {formatearMoneda(totalesGlobales.balanceNeto, monedaVista)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Barra de progreso global mejorada */}
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: isMobile ? "20px" : "24px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
              border: "1px solid #f1f5f9",
              animation: "slideInRight 1.0s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "16px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                }}
              >
                Progreso General del Proyecto
              </h3>
              <span
                style={{
                  fontSize: "18px",
                  fontWeight: "700",
                  color: "#8b5cf6",
                }}
              >
                {totalesGlobales.porcentajeEjecutado.toFixed(1)}%
              </span>
            </div>

            <div
              style={{
                background: "#f1f5f9",
                height: "16px",
                borderRadius: "8px",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <div
                style={{
                  height: "100%",
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #10b981 100%)",
                  width: `${Math.min(
                    totalesGlobales.porcentajeEjecutado,
                    100
                  )}%`,
                  borderRadius: "8px",
                  transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.4)",
                  position: "relative",
                }}
              >
                {/* Efecto de brillo en la barra */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "50%",
                    background:
                      "linear-gradient(180deg, rgba(255,255,255,0.4) 0%, transparent 100%)",
                    borderRadius: "8px 8px 0 0",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Sección Honorarios y Mano de Obra */}
          {(datosHonorarios || datosManoObra || datosLlaveEnMano) && (
            <div style={{ marginBottom: "20px" }}>
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: "0 0 16px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Briefcase size={18} />
                Honorarios y Servicios Profesionales
              </h3>

              <div
                style={{
                  background: "white",
                  borderRadius: "16px",
                  padding: isMobile ? "16px" : "20px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                  border: "1px solid #f1f5f9",
                }}
              >
                {datosLlaveEnMano && (
                  <BarraProgresoMinimalista
                    item={llaveEnManoParaBarra}
                    tipo="llave_en_mano"
                    color={SPECIAL_COLORS.honorarios}
                    isEtapa={false}
                    onToggleEstado={() => {}}
                    onViewInfo={
                      permisosCliente.esCliente ? handleViewInfo : null
                    }
                    formatearMoneda={formatearMoneda}
                    isMobile={isMobile}
                    permisosCliente={permisosCliente}
                    monedaVista={monedaVista}
                    convertirMonto={(monto, monedaOrigen, monedaDestino) =>
                      convertirMonto(
                        monto,
                        monedaOrigen,
                        monedaDestino,
                        cotizacionDolar
                      )
                    }
                    cotizacionDolar={cotizacionDolar} // NUEVO PARÁMETRO
                  />
                )}

                {datosHonorarios && (
                  <BarraProgresoMinimalista
                    item={honorariosParaBarra}
                    tipo="honorarios"
                    color={SPECIAL_COLORS.honorarios}
                    isEtapa={false}
                    onToggleEstado={() => {}}
                    onViewInfo={
                      permisosCliente.esCliente ? handleViewInfo : null
                    }
                    formatearMoneda={formatearMoneda}
                    isMobile={isMobile}
                    permisosCliente={permisosCliente}
                    monedaVista={monedaVista}
                    convertirMonto={(monto, monedaOrigen, monedaDestino) =>
                      convertirMonto(
                        monto,
                        monedaOrigen,
                        monedaDestino,
                        cotizacionDolar
                      )
                    }
                    cotizacionDolar={cotizacionDolar} // NUEVO PARÁMETRO
                  />
                )}

                {datosManoObra && (
                  <BarraProgresoMinimalista
                    item={manoObraParaBarra}
                    tipo="mano_obra"
                    color={SPECIAL_COLORS.mano_obra}
                    isEtapa={false}
                    onToggleEstado={() => {}}
                    onViewInfo={
                      permisosCliente.esCliente ? handleViewInfo : null
                    }
                    formatearMoneda={formatearMoneda}
                    isMobile={isMobile}
                    permisosCliente={permisosCliente}
                    monedaVista={monedaVista}
                    convertirMonto={(monto, monedaOrigen, monedaDestino) =>
                      convertirMonto(
                        monto,
                        monedaOrigen,
                        monedaDestino,
                        cotizacionDolar
                      )
                    }
                    cotizacionDolar={cotizacionDolar} // NUEVO PARÁMETRO
                  />
                )}
              </div>
            </div>
          )}

          {/* Sección Etapas de Obra REDISEÑADA */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "12px",
                marginBottom: "20px",
              }}
            >
              <h3
                style={{
                  fontSize: "16px",
                  fontWeight: "700",
                  color: "#1e293b",
                  margin: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Building2 size={18} />
                Etapas de Obra ({etapasAgrupadas.length})
                {presupuestosIniciales.some((p) => p.usa_cac) && (
                  <span
                    style={{
                      background: "#f0fdf4",
                      color: "#059669",
                      padding: "2px 8px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    ⚡ CAC Activo
                  </span>
                )}
              </h3>

              {/* Solo mostrar botones de gestión si no es cliente */}
              {!permisosCliente.esCliente && gastosNoAsignados.length > 0 && (
                <button
                  onClick={() => setShowAsignarModal(true)}
                  style={{
                    background:
                      "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                    color: "white",
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s ease",
                    boxShadow: "0 4px 12px rgba(251, 191, 36, 0.4)",
                  }}
                  className="button-hover"
                >
                  <AlertCircle size={14} />
                  {gastosNoAsignados.length} Sin Asignar
                </button>
              )}
            </div>

            {etapasAgrupadas.length === 0 ? (
              <div
                style={{
                  background: "white",
                  borderRadius: "20px",
                  padding: isMobile ? "40px 20px" : "60px 40px",
                  textAlign: "center",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  border: "2px dashed #cbd5e1",
                }}
              >
                <div
                  style={{
                    width: "80px",
                    height: "80px",
                    background: getVistaColor("presupuestos-iniciales").light,
                    borderRadius: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 24px",
                  }}
                >
                  <Layers
                    size={40}
                    color={getVistaColor("presupuestos-iniciales").solid}
                  />
                </div>
                <h3
                  style={{
                    fontSize: "22px",
                    fontWeight: "700",
                    color: "#1e293b",
                    marginBottom: "12px",
                  }}
                >
                  No hay presupuestos iniciales
                </h3>
                <p
                  style={{
                    fontSize: "16px",
                    color: "#64748b",
                    marginBottom: permisosCliente.esCliente ? "0" : "32px",
                    lineHeight: "1.5",
                  }}
                >
                  {permisosCliente.esCliente
                    ? "La constructora aún no ha definido el presupuesto inicial de tu proyecto"
                    : "Crea el primer presupuesto inicial para comenzar a gestionar el proyecto por etapas"}
                </p>
                {!permisosCliente.esCliente && (
                  <button
                    onClick={() => {
                      setEditingPresupuesto(null);
                      setShowModal(true);
                    }}
                    style={{
                      background: getVistaColor("presupuestos-iniciales")
                        .gradient,
                      color: "white",
                      border: "none",
                      padding: "16px 32px",
                      borderRadius: "12px",
                      fontSize: "16px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      margin: "0 auto",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      boxShadow: "0 8px 24px rgba(124, 58, 237, 0.4)",
                    }}
                    className="button-hover"
                  >
                    <Plus size={18} />
                    Crear Primer Presupuesto
                  </button>
                )}
              </div>
            ) : (
              <div>
                {etapasAgrupadas.map((etapa, index) => (
                  <EtapaColapsable
                    key={etapa.id}
                    etapa={etapa}
                    index={index}
                    totalEtapas={etapasAgrupadas.length}
                    onMoveEtapa={handleMoveEtapa}
                    onToggleEstado={handleToggleEstado}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onRestore={handleRestore}
                    onViewInfo={handleViewInfo}
                    formatearMoneda={formatearMoneda}
                    isMobile={isMobile}
                    permisosCliente={permisosCliente}
                    monedaVista={monedaVista}
                    convertirMonto={convertirMonto}
                    cotizacionDolar={cotizacionDolar} // NUEVO PARÁMETRO
                    filterVigencia={filterVigencia}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Crear/Editar Presupuesto - Solo si no es cliente */}
      {showModal && permisosCliente.esCliente === false && (
        <PresupuestoInicialModal
          constructora={constructora}
          proyecto={proyectoSeleccionado}
          etapas={etapas}
          subetapas={subetapas}
          conceptosSalida={conceptosSalida}
          presupuesto={editingPresupuesto}
          cotizacionDolar={cotizacionDolar}
          onClose={() => {
            setShowModal(false);
            setEditingPresupuesto(null);
          }}
          onSave={() => {
            setShowModal(false);
            setEditingPresupuesto(null);
            cargarDatosProyecto();
          }}
          formatearMoneda={formatearMoneda}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
          onNuevaSubetapa={(nuevaSubetapa) => {
            setSubetapas((prev) => {
              if (prev.some((s) => s.id === nuevaSubetapa.id)) return prev;
              return [...prev, nuevaSubetapa].sort(
                (a, b) => (a.orden || 999) - (b.orden || 999)
              );
            });
          }}
        />
      )}

      {/* Modal Ver Información - Para clientes */}
      {showInfoModal && selectedPresupuesto && (
        <PresupuestoInfoModal
          presupuesto={selectedPresupuesto}
          onClose={() => {
            setShowInfoModal(false);
            setSelectedPresupuesto(null);
            setGastosAsignados([]);
          }}
          formatearMoneda={formatearMoneda}
          isMobile={isMobile}
          monedaVista={monedaVista}
          convertirMonto={convertirMonto}
          gastosAsignados={gastosAsignados}
          datosHonorarios={datosHonorarios}
          datosManoObra={datosManoObra}
          proyectoSeleccionado={proyectoSeleccionado}
        />
      )}

      {/* Modal Asignar Gastos - Solo si no es cliente */}
      {showAsignarModal && permisosCliente.esCliente === false && (
        <AsignarGastosModal
          gastosNoAsignados={gastosNoAsignados}
          presupuestosIniciales={presupuestosIniciales.filter(p => {
            // Solo mostrar presupuestos que tengan saldo disponible (mayor a 0)
            const saldo = p.saldo_pendiente || 0;
            return saldo > 0;
          })}
          onClose={() => setShowAsignarModal(false)}
          onAsignar={handleAsignarGasto}
          formatearMoneda={formatearMoneda}
          isMobile={isMobile}
          showCustomAlert={showCustomAlert}
          monedaVista={monedaVista}
          convertirMonto={convertirMonto}
        />
      )}

      {/* Modal de información - Presupuesto Inicial ACTUALIZADO */}
      {showInfoModalPresupuestoInicial && (
        <InfoModalPresupuestoInicial
          onClose={() => setShowInfoModalPresupuestoInicial(false)}
          proyectoSeleccionado={proyectoSeleccionado}
          datosHonorarios={datosHonorarios}
          datosManoObra={datosManoObra}
          datosLlaveEnMano={datosLlaveEnMano}
          presupuestosIniciales={presupuestosIniciales}
          isMobile={isMobile}
        />
      )}

      {/* Modal de información - Presupuesto Actualizado ACTUALIZADO */}
      {showInfoModalPresupuestoActualizado && (
        <InfoModalPresupuestoActualizado
          onClose={() => setShowInfoModalPresupuestoActualizado(false)}
          proyectoSeleccionado={proyectoSeleccionado}
          datosHonorarios={datosHonorarios}
          datosManoObra={datosManoObra}
          datosLlaveEnMano={datosLlaveEnMano}
          presupuestosIniciales={presupuestosIniciales}
          cotizacionDolar={cotizacionDolar}
          isMobile={isMobile}
        />
      )}

      {/* Modal personalizado para alertas */}
      <CustomModal
        isOpen={customModal.isOpen}
        onClose={() => setCustomModal((prev) => ({ ...prev, isOpen: false }))}
        type={customModal.type}
        title={customModal.title}
        message={customModal.message}
        onConfirm={customModal.onConfirm}
        isMobile={isMobile}
      />

      <style jsx>{`
        /* Configuración anti-zoom */
        input[type="text"],
        input[type="number"],
        input[type="date"],
        input[type="email"],
        input[type="tel"],
        input[type="search"],
        textarea,
        select {
          font-size: 16px !important;
          touch-action: manipulation;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        input:focus,
        textarea:focus,
        select:focus {
          font-size: 16px !important;
          outline: none;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        @media (max-width: 768px) {
          input,
          textarea,
          select,
          button {
            font-size: 16px !important;
            -webkit-appearance: none !important;
            appearance: none !important;
          }
        }

        /* Animaciones estándar */
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        /* Efectos de botones mejorados */
        .button-hover:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
        }

        /* Responsive improvements */
        @media (max-width: 768px) {
          .button-hover:hover {
            transform: translateY(-1px);
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
          }
        }

        /* Estados especiales */
        .finalizada {
          background: linear-gradient(
            135deg,
            #ecfdf5 0%,
            #f0fdf4 100%
          ) !important;
          border-color: #10b981 !important;
        }

        /* Mejoras de accesibilidad */
        button:focus,
        .barra-hover:focus {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }

        /* Efectos de loading mejorados */
        .loading-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );
}
// Modal para crear/editar presupuesto inicial (manteniendo toda la lógica original)
function PresupuestoInicialModal({
  constructora,
  proyecto,
  etapas,
  subetapas,
  conceptosSalida,
  presupuesto,
  cotizacionDolar,
  onClose,
  onSave,
  formatearMoneda,
  isMobile,
  showCustomAlert,
  onNuevaSubetapa,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [guardando, setGuardando] = useState(false);

  const [formData, setFormData] = useState({
    etapa_id: "",
    subetapa_id: "",
    concepto_salida_id: "",
    nombre: "",
    descripcion: "",
    monto_presupuestado: "",
    moneda: "ARS",
    tipo_cambio: cotizacionDolar || 1000,
    estado: "abierto",
    porcentaje_obra: "",
    fecha: new Date().toISOString().split("T")[0], // <-- nuevo campo obligatorio
  });

  // Fallbacks para mantener seleccionadas etapa/subetapa aunque estén eliminadas
  const [etapaFallback, setEtapaFallback] = useState(null);
  const [subetapaFallback, setSubetapaFallback] = useState(null);

  useEffect(() => {
    const ensureFallbacks = async () => {
      try {
        // Etapa seleccionada pero no presente en lista activa
        if (formData.etapa_id && !etapas.find((e) => e.id === formData.etapa_id)) {
          const { data: etapaSel } = await supabase
            .from("etapas_obra")
            .select("*")
            .eq("id", formData.etapa_id)
            .single();
          setEtapaFallback(etapaSel || null);
        } else {
          setEtapaFallback(null);
        }

        // Subetapa seleccionada pero no presente en lista activa
        if (formData.subetapa_id && !subetapas.find((s) => s.id === formData.subetapa_id)) {
          const { data: subSel } = await supabase
            .from("subetapas_obra")
            .select("*")
            .eq("id", formData.subetapa_id)
            .single();
          setSubetapaFallback(subSel || null);
        } else {
          setSubetapaFallback(null);
        }
      } catch {
        // ignorar errores de fallback
      }
    };
    ensureFallbacks();
  }, [formData.etapa_id, formData.subetapa_id, etapas, subetapas]);

  useEffect(() => {
    if (presupuesto) {
      // Modo edición
      console.log("🔍 EDITANDO PRESUPUESTO INICIAL - Tipo de cambio original:", presupuesto.tipo_cambio);
      setFormData({
        etapa_id: presupuesto.etapa_id,
        subetapa_id: presupuesto.subetapa_id,
        concepto_salida_id: presupuesto.concepto_salida_id,
        nombre: presupuesto.nombre,
        descripcion: presupuesto.descripcion || "",
        monto_presupuestado: presupuesto.monto_presupuestado.toString(),
        moneda: presupuesto.moneda,
        tipo_cambio: presupuesto.tipo_cambio ? presupuesto.tipo_cambio.toString() : (cotizacionDolar || 1000).toString(),
        estado: presupuesto.estado,
        porcentaje_obra: presupuesto.porcentaje_obra?.toString() || "",
        fecha: presupuesto.fecha
          ? presupuesto.fecha.split("T")[0]
          : presupuesto.created_at
          ? presupuesto.created_at.split("T")[0]
          : new Date().toISOString().split("T")[0],
      });
      setCurrentStep(1); // Siempre abrir en el primer paso
    } else {
      // Modo creación - actualizar tipo cambio
      setFormData((prev) => ({
        ...prev,
        tipo_cambio: cotizacionDolar || 1000,
        fecha: new Date().toISOString().split("T")[0],
      }));
    }
  }, [presupuesto, cotizacionDolar]);

  // Función para crear nuevas subetapas
  const crearNuevaSubetapa = async (nombre) => {
    if (!formData.etapa_id) {
      showCustomAlert("error", "Error", "Primero debes seleccionar una etapa");
      return;
    }

    try {
      const subetapasParaEtapa = subetapas.filter(
        (s) => s.etapa_id === formData.etapa_id
      );

      const { data, error } = await supabase
        .from("subetapas_obra")
        .insert([
          {
            constructora_id: constructora.id,
            etapa_id: formData.etapa_id,
            nombre: nombre.trim(),
            orden: subetapasParaEtapa.length + 1,
            activo: true,
          },
        ])
        .select();

      if (error) {
        console.error("Error creando subetapa:", error);
        showCustomAlert(
          "error",
          "Error",
          "Error al crear subetapa: " + error.message
        );
      } else {
        const nuevaSubetapa = data[0];
        // Notificar al padre para actualizar el array global
        if (onNuevaSubetapa) onNuevaSubetapa(nuevaSubetapa);
        setFormData((prev) => ({
          ...prev,
          subetapa_id: nuevaSubetapa.id,
        }));
        showCustomAlert("success", "Éxito", "Subetapa creada correctamente");
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.etapa_id) {
          showCustomAlert("error", "Error", "Por favor selecciona una etapa");
          return false;
        }
        if (!formData.subetapa_id) {
          showCustomAlert(
            "error",
            "Error",
            "Por favor selecciona una subetapa"
          );
          return false;
        }
        if (!formData.concepto_salida_id) {
          showCustomAlert("error", "Error", "Por favor selecciona un concepto");
          return false;
        }
        return true;
      case 2:
        if (!formData.nombre.trim()) {
          showCustomAlert("error", "Error", "Por favor ingresa un nombre");
          return false;
        }
        return true;
      case 3:
        if (
          !formData.monto_presupuestado ||
          parseFloat(formData.monto_presupuestado) <= 0
        ) {
          showCustomAlert(
            "error",
            "Error",
            "Por favor ingresa un monto válido"
          );
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const handleSave = async () => {
    if (!validateStep(3)) return;

    setGuardando(true);
    try {
      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: proyecto.id,
        etapa_id: formData.etapa_id,
        subetapa_id: formData.subetapa_id,
        concepto_salida_id: formData.concepto_salida_id,
        nombre: formData.nombre.trim(),
        descripcion: formData.descripcion.trim() || null,
        monto_presupuestado: parseFloat(formData.monto_presupuestado),
        moneda: formData.moneda,
        tipo_cambio: parseFloat(formData.tipo_cambio),
        estado: formData.estado,
        porcentaje_obra: formData.porcentaje_obra
          ? parseFloat(formData.porcentaje_obra)
          : null,
        fecha: formData.fecha,
      };

      let result;
      if (presupuesto) {
        // Actualizar
        result = await supabase
          .from("presupuestos_iniciales")
          .update(dataToSave)
          .eq("id", presupuesto.id)
          .select();
      } else {
        // Crear
        result = await supabase
          .from("presupuestos_iniciales")
          .insert([dataToSave])
          .select();
      }

      const { data, error } = result;

      if (error) {
        console.error("Error guardando presupuesto inicial:", error);
        showCustomAlert("error", "Error", "Error al guardar: " + error.message);
      } else {
        // ✅ Actualizar presupuestos totales automáticamente (en background)
        console.log("🔍 Programando actualización de presupuestos totales para proyecto:", proyecto.id);
        
        // Ejecutar en background sin bloquear la UI
        (async () => {
          try {
            console.log("🔄 Actualizando totales en background...");
            
            // Actualizar presupuesto total inicial
            const resultadoInicial = await actualizarPresupuestoTotalInicial(proyecto.id);
            console.log("🔍 Resultado de actualización inicial:", resultadoInicial);
            
            // Actualizar presupuesto total actualizado
            const resultadoActualizado = await actualizarPresupuestoTotalActualizado(proyecto.id);
            console.log("🔍 Resultado de actualización actualizado:", resultadoActualizado);
            
            // ✅ NUEVO: Recalcular tabla presupuestos_iniciales_actualizados
            console.log("🔄 Recalculando presupuestos iniciales actualizados...");
            const resultadoPIA = await recalcularPresupuestosInicialesActualizados(proyecto.id);
            console.log("🔍 Resultado de recálculo PIA:", resultadoPIA);
            
            if (resultadoPIA.success) {
              console.log("✅ Presupuestos iniciales actualizados recalculados exitosamente");
              // Los totales se actualizarán automáticamente en el próximo refresh
              console.log("📊 Totales actualizados en background");
            } else {
              console.error("⚠️ Error recalculando presupuestos iniciales actualizados:", resultadoPIA.error);
            }
          } catch (error) {
            console.error("⚠️ Error actualizando presupuestos totales:", error);
            // No mostrar error al usuario, solo log
          }
        })();
        
        showCustomAlert(
          "success",
          "Éxito",
          presupuesto
            ? "Presupuesto actualizado correctamente"
            : "Presupuesto creado correctamente"
        );
        
        // Recargar datos inmediatamente para mostrar el cambio
        // onSave() ya recarga los datos del componente padre
        
        onSave();
      }
    } catch (err) {
      console.error("Error:", err);
      showCustomAlert("error", "Error", "Error: " + err.message);
    }
    setGuardando(false);
  };

  const getStepTitle = (step) => {
    switch (step) {
      case 1:
        return "Etapa y Concepto";
      case 2:
        return "Información del Presupuesto";
      case 3:
        return "Monto y Configuración";
      case 4:
        return "Resumen y Confirmación";
      default:
        return "";
    }
  };

  const getSubetapasFiltradas = () => {
    return subetapas.filter(
      (subetapa) => subetapa.etapa_id === formData.etapa_id
    );
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Etapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Etapa de obra *
              </label>
              <CustomDropdown
                value={formData.etapa_id}
                onChange={(value) => {
                  setFormData({
                    ...formData,
                    etapa_id: value,
                    subetapa_id: "", // Reset subetapa al cambiar etapa
                  });
                }}
                options={[
                  ...etapas.map((etapa) => ({ value: etapa.id, label: etapa.nombre })),
                  ...(etapaFallback && !etapas.find(e => e.id === etapaFallback.id)
                    ? [{ value: etapaFallback.id, label: `${etapaFallback.nombre} (eliminada)` }]
                    : []),
                ]}
                placeholder="Selecciona una etapa"
                icon={Layers}
              />
            </div>

            {/* Subetapa */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Subetapa *
              </label>
              <CustomDropdown
                value={formData.subetapa_id}
                onChange={(value) =>
                  setFormData({ ...formData, subetapa_id: value })
                }
                options={[
                  ...getSubetapasFiltradas().map((subetapa) => ({ value: subetapa.id, label: subetapa.nombre })),
                  ...(subetapaFallback && !getSubetapasFiltradas().find(s => s.id === subetapaFallback.id)
                    ? [{ value: subetapaFallback.id, label: `${subetapaFallback.nombre} (eliminada)` }]
                    : []),
                ]}
                placeholder="Selecciona una subetapa"
                disabled={!formData.etapa_id}
                icon={Package}
                canCreate={!!formData.etapa_id}
                onCreateNew={crearNuevaSubetapa}
                createText="Crear subetapa"
              />
              {!formData.etapa_id && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#64748b",
                    margin: "4px 0 0 0",
                    fontStyle: "italic",
                  }}
                >
                  Primero selecciona una etapa
                </p>
              )}
            </div>

            {/* Concepto */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Concepto de gasto *
              </label>
              <CustomDropdown
                value={formData.concepto_salida_id}
                onChange={(value) =>
                  setFormData({ ...formData, concepto_salida_id: value })
                }
                options={conceptosSalida.map((concepto) => ({
                  value: concepto.id,
                  label: concepto.nombre,
                }))}
                placeholder="Selecciona un concepto"
                icon={Receipt}
              />
            </div>

            {/* Fecha de referencia CAC */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Fecha de referencia para actualización CAC *
              </label>
              <input
                type="date"
                value={formData.fecha}
                onChange={(e) =>
                  setFormData({ ...formData, fecha: e.target.value })
                }
                required
                style={{
                  width: "100%",
                  padding: "8px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  fontSize: "15px",
                }}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Nombre */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Nombre del Presupuesto *
              </label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) =>
                  setFormData({ ...formData, nombre: e.target.value })
                }
                placeholder="Ej: Materiales para fundación"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>

            {/* Descripción */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Descripción (Opcional)
              </label>
              <textarea
                value={formData.descripcion}
                onChange={(e) =>
                  setFormData({ ...formData, descripcion: e.target.value })
                }
                placeholder="Descripción detallada del presupuesto..."
                rows="4"
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  resize: "vertical",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Monto y Moneda */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Monto presupuestado *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.monto_presupuestado}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      monto_presupuestado: e.target.value,
                    })
                  }
                  placeholder="0.00"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Moneda *
                </label>
                <CustomDropdown
                  value={formData.moneda}
                  onChange={(value) =>
                    setFormData({ ...formData, moneda: value })
                  }
                  options={[
                    { value: "ARS", label: "ARS (Pesos)" },
                    { value: "USD", label: "USD (Dólares)" },
                  ]}
                  placeholder="Selecciona moneda"
                  icon={DollarSign}
                />
              </div>
            </div>

            {/* Tipo de cambio */}
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#374151",
                }}
              >
                Tipo de cambio (Dólar oficial) *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.tipo_cambio}
                onChange={(e) =>
                  setFormData({ ...formData, tipo_cambio: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  touchAction: "manipulation",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#2563eb";
                  e.target.style.boxShadow = "0 0 0 3px rgba(37, 99, 235, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#f1f5f9";
                  e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                }}
              />
              {cotizacionDolar && (
                <p
                  style={{
                    fontSize: "12px",
                    color: "#059669",
                    margin: "4px 0 0 0",
                    fontWeight: "500",
                  }}
                >
                  <DollarSign
                    size={12}
                    style={{ verticalAlign: "text-bottom", marginRight: "4px" }}
                  />
                  Dólar oficial actual: ${cotizacionDolar.toLocaleString()}
                </p>
              )}
            </div>

            {/* Porcentaje de obra y Estado */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: "12px",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  % de la Obra (Opcional)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.porcentaje_obra}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      porcentaje_obra: e.target.value,
                    })
                  }
                  placeholder="15.5"
                  style={{
                    width: "100%",
                    padding: "14px 16px",
                    border: "2px solid #f1f5f9",
                    borderRadius: "12px",
                    fontSize: "16px",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    touchAction: "manipulation",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#2563eb";
                    e.target.style.boxShadow =
                      "0 0 0 3px rgba(37, 99, 235, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#f1f5f9";
                    e.target.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)";
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#374151",
                  }}
                >
                  Estado del Presupuesto
                </label>
                <CustomDropdown
                  value={formData.estado}
                  onChange={(value) =>
                    setFormData({ ...formData, estado: value })
                  }
                  options={[
                    { value: "abierto", label: "Abierto" },
                    { value: "cerrado", label: "Cerrado" },
                  ]}
                  placeholder="Selecciona estado"
                  icon={Settings}
                />
              </div>
            </div>

            {/* Información sobre CAC */}
            {formData.moneda === "ARS" && (
              <div
                style={{
                  background: "#f0fdf4",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  ⚡ Actualización CAC Automática
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#166534",
                    lineHeight: "1.4",
                  }}
                >
                  Este presupuesto en pesos argentinos se actualizará
                  automáticamente usando el índice CAC General desde la fecha de
                  creación. Los gastos se descuentan usando el CAC del mes en
                  que se realizaron.
                </p>
              </div>
            )}
          </div>
        );
      case 4:
        const etapaSeleccionada = etapas.find(
          (e) => e.id === formData.etapa_id
        );
        const subetapaSeleccionada = subetapas.find(
          (s) => s.id === formData.subetapa_id
        );
        const conceptoSeleccionado = conceptosSalida.find(
          (c) => c.id === formData.concepto_salida_id
        );

        return (
          <div style={{ display: "grid", gap: "20px" }}>
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FileText size={16} />
                Resumen del Presupuesto Inicial
              </h3>

              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Proyecto:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {proyecto?.codigo} - {proyecto?.apellido_cliente}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Etapa:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {etapaSeleccionada?.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Subetapa:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {subetapaSeleccionada?.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Concepto:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {conceptoSeleccionado?.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Nombre:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {formData.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Monto:
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#059669",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(
                      formData.monto_presupuestado,
                      formData.moneda
                    )}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "120px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Estado:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color:
                        formData.estado === "cerrado" ? "#059669" : "#f59e0b",
                      fontWeight: "600",
                    }}
                  >
                    {formData.estado === "cerrado" ? "Cerrado" : "Abierto"}
                  </span>
                </div>

                {formData.porcentaje_obra && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      % de Obra:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#1e293b",
                        fontWeight: "600",
                      }}
                    >
                      {formData.porcentaje_obra}%
                    </span>
                  </div>
                )}
              </div>
            </div>

            {formData.descripcion && (
              <div
                style={{
                  background: "#f8fafc",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Descripción
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "14px",
                    color: "#64748b",
                    lineHeight: "1.5",
                  }}
                >
                  {formData.descripcion}
                </p>
              </div>
            )}

            {/* Información sobre CAC en el resumen */}
            {formData.moneda === "ARS" && (
              <div
                style={{
                  background: "#f0fdf4",
                  padding: "16px",
                  borderRadius: "12px",
                  border: "1px solid #bbf7d0",
                }}
              >
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#059669",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  ⚡ Actualización CAC Habilitada
                </h4>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#166534",
                    lineHeight: "1.4",
                  }}
                >
                  Este presupuesto se actualizará automáticamente con el índice
                  CAC General para mantener el valor real en el tiempo.
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "40px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: getVistaColor("presupuestos-iniciales").gradient,
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Target
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              {presupuesto ? "Editar presupuesto" : "Nuevo presupuesto inicial"}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              Paso {currentStep} de 4: {getStepTitle(currentStep)}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress bar */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                style={{
                  flex: 1,
                  height: "4px",
                  borderRadius: "2px",
                  background:
                    step <= currentStep
                      ? getVistaColor("presupuestos-iniciales").solid
                      : "#e2e8f0",
                  transition: "background 0.3s ease",
                }}
              />
            ))}
          </div>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          {renderStep()}
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: isMobile ? "16px 20px" : "20px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <button
            onClick={currentStep === 1 ? onClose : handlePrev}
            style={{
              padding: "12px 20px",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              background: "white",
              color: "#64748b",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            className="button-hover"
          >
            {currentStep === 1 ? "Cancelar" : "Anterior"}
          </button>

          {currentStep < 4 ? (
            <button
              onClick={handleNext}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: getVistaColor("presupuestos-iniciales").gradient,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              className="button-hover"
            >
              Siguiente
              <ChevronDown size={16} style={{ transform: "rotate(-90deg)" }} />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={guardando}
              style={{
                padding: "12px 24px",
                border: "none",
                borderRadius: "8px",
                background: guardando
                  ? "#94a3b8"
                  : getVistaColor("presupuestos-iniciales").gradient,
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                cursor: guardando ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              className={!guardando ? "button-hover" : ""}
            >
              {guardando ? (
                <>
                  <Loader2 size={16} className="loading-pulse" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  {presupuesto ? "Actualizar" : "Crear"} Presupuesto
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
// Modal para asignar gastos no asignados
function AsignarGastosModal({
  gastosNoAsignados,
  presupuestosIniciales,
  onClose,
  onAsignar,
  formatearMoneda,
  isMobile,
  showCustomAlert,
  monedaVista,
  convertirMonto,
}) {
  const [selectedGasto, setSelectedGasto] = useState(null);
  const [draggedGasto, setDraggedGasto] = useState(null);
  const [dragOverPresupuesto, setDragOverPresupuesto] = useState(null);

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  };

  const handleDragStart = (e, gasto) => {
    setDraggedGasto(gasto);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, presupuestoId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverPresupuesto(presupuestoId);
  };

  const handleDragLeave = () => {
    setDragOverPresupuesto(null);
  };

  const handleDrop = (e, presupuestoId) => {
    e.preventDefault();
    setDragOverPresupuesto(null);

    if (draggedGasto) {
      handleAsignarGasto(draggedGasto.id, presupuestoId);
      setDraggedGasto(null);
    }
  };

  const handleAsignarGasto = (gastoId, presupuestoId) => {
    const gasto = gastosNoAsignados.find((g) => g.id === gastoId);
    const presupuesto = presupuestosIniciales.find((p) => p.id === presupuestoId);

    if (!gasto || !presupuesto) return;

    if (gasto.etapa_id !== presupuesto.etapa_id || gasto.subetapa_id !== presupuesto.subetapa_id) {
        const gastoEtapa = gasto.etapas_obra?.nombre || "Sin etapa";
        const gastoSubetapa = gasto.subetapas_obra?.nombre || "Sin subetapa";
        const presupuestoEtapa = presupuesto.etapas_obra?.nombre || "Sin etapa";
        const presupuestoSubetapa = presupuesto.subetapas_obra?.nombre || "Sin subetapa";
        
        showCustomAlert(
          "confirm",
          "Etapas diferentes",
          `El gasto pertenece a "${gastoEtapa} → ${gastoSubetapa}" pero el presupuesto es de "${presupuestoEtapa} → ${presupuestoSubetapa}". ¿Continuar?`,
          () => onAsignar(gastoId, presupuestoId)
        );
    } else {
      onAsignar(gastoId, presupuestoId);
    }
  };

  const getGastosByEtapa = () => {
    const grupos = {};
    gastosNoAsignados.forEach((gasto) => {
      const etapaKey = `${gasto.etapa_id}-${gasto.subetapa_id}`;
      const etapaLabel = `${gasto.etapas_obra?.nombre} → ${gasto.subetapas_obra?.nombre}`;

      if (!grupos[etapaKey]) {
        grupos[etapaKey] = {
          label: etapaLabel,
          gastos: [],
        };
      }
      grupos[etapaKey].gastos.push(gasto);
    });
    return grupos;
  };

  const gastosPorEtapa = getGastosByEtapa();

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: "1200px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Link
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              Asignar Gastos a Presupuestos
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              Arrastra los gastos a los presupuestos correspondientes o usa los
              botones
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            overflow: "hidden",
          }}
        >
          {/* Panel izquierdo - Gastos no asignados */}
          <div
            style={{
              padding: "24px",
              borderRight: isMobile ? "none" : "1px solid #e2e8f0",
              borderBottom: isMobile ? "1px solid #e2e8f0" : "none",
              overflowY: "auto",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertCircle size={16} color="#f59e0b" />
              Gastos Sin Asignar ({gastosNoAsignados.length})
            </h3>

            {gastosNoAsignados.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#64748b",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#f0fdf4",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <CheckCircle2 size={24} color="#10b981" />
                </div>
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    color: "#1e293b",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  ¡Todos los gastos están asignados!
                </h4>
                <p style={{ margin: 0, fontSize: "14px" }}>
                  No hay gastos pendientes de asignación
                </p>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "12px" }}>
                {Object.entries(gastosPorEtapa).map(([etapaKey, grupo]) => (
                  <div key={etapaKey}>
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#64748b",
                        padding: "8px 12px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                      }}
                    >
                      {grupo.label} ({grupo.gastos.length})
                    </h4>

                    {grupo.gastos.map((gasto) => (
                      <div
                        key={gasto.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, gasto)}
                        onClick={() =>
                          setSelectedGasto(
                            selectedGasto === gasto.id ? null : gasto.id
                          )
                        }
                        className="drag-item"
                        style={{
                          background:
                            selectedGasto === gasto.id ? "#eff6ff" : "white",
                          border:
                            selectedGasto === gasto.id
                              ? "2px solid #2563eb"
                              : "1px solid #e2e8f0",
                          borderRadius: "12px",
                          padding: "12px",
                          cursor: "grab",
                          transition: "all 0.2s ease",
                          marginBottom: "8px",
                        }}
                        onMouseEnter={(e) => {
                          if (selectedGasto !== gasto.id) {
                            e.target.style.boxShadow =
                              "0 4px 12px rgba(0, 0, 0, 0.1)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedGasto !== gasto.id) {
                            e.target.style.boxShadow = "none";
                          }
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "8px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <p
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: "#1e293b",
                              }}
                            >
                              {gasto.conceptos_salida?.nombre}
                            </p>
                            <p
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "11px",
                                color: "#64748b",
                              }}
                            >
                              {gasto.proveedores_servicios?.nombre}
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "10px",
                                color: "#9ca3af",
                              }}
                            >
                              {formatDateForDisplay(gasto.fecha)}
                            </p>
                          </div>

                          <div
                            style={{
                              textAlign: "right",
                              marginLeft: "12px",
                            }}
                          >
                            <p
                              style={{
                                margin: "0 0 4px 0",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#ef4444",
                              }}
                            >
                              {formatearMoneda(
                                convertirMonto(
                                  gasto.monto,
                                  gasto.moneda,
                                  monedaVista,
                                  gasto.tipo_cambio
                                ),
                                monedaVista
                              )}
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: "10px",
                                color: "#64748b",
                              }}
                            >
                              Original: {gasto.moneda} $
                              {parseFloat(gasto.monto).toLocaleString()}
                            </p>
                          </div>
                        </div>

                        {gasto.descripcion && (
                          <p
                            style={{
                              margin: "8px 0 0 0",
                              fontSize: "11px",
                              color: "#64748b",
                              fontStyle: "italic",
                              borderTop: "1px solid #f1f5f9",
                              paddingTop: "8px",
                            }}
                          >
                            {gasto.descripcion}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Panel derecho - Presupuestos disponibles */}
          <div
            style={{
              padding: "24px",
              overflowY: "auto",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#1e293b",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Target size={16} color="#2563eb" />
              Presupuestos Disponibles ({presupuestosIniciales.length})
            </h3>

            {presupuestosIniciales.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "#64748b",
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    background: "#e0e7ff",
                    borderRadius: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 16px",
                  }}
                >
                  <Target size={24} color="#2563eb" />
                </div>
                <h4
                  style={{
                    margin: "0 0 8px 0",
                    color: "#1e293b",
                    fontSize: "16px",
                    fontWeight: "600",
                  }}
                >
                  No hay presupuestos
                </h4>
                <p style={{ margin: 0, fontSize: "14px" }}>
                  Crea presupuestos iniciales para poder asignar gastos
                </p>
              </div>
            ) : (
              <div style={{ display: "grid", gap: "12px" }}>
                {presupuestosIniciales.map((presupuesto) => (
                  <div
                    key={presupuesto.id}
                    onDragOver={(e) => handleDragOver(e, presupuesto.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, presupuesto.id)}
                    className={`drop-zone ${
                      dragOverPresupuesto === presupuesto.id ? "drag-over" : ""
                    }`}
                    style={{
                      background:
                        dragOverPresupuesto === presupuesto.id
                          ? "rgba(37, 99, 235, 0.05)"
                          : "white",
                      border:
                        dragOverPresupuesto === presupuesto.id
                          ? "2px dashed #2563eb"
                          : "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "16px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <h4
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1e293b",
                          }}
                        >
                          {presupuesto.nombre}
                          {presupuesto.usa_cac && (
                            <span
                              style={{
                                background: "#f0fdf4",
                                color: "#059669",
                                padding: "1px 6px",
                                borderRadius: "4px",
                                fontSize: "10px",
                                fontWeight: "600",
                                marginLeft: "6px",
                                border: "1px solid #bbf7d0",
                              }}
                            >
                              CAC
                            </span>
                          )}
                        </h4>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "12px",
                            color: "#64748b",
                          }}
                        >
                          {presupuesto.etapas_obra?.nombre} →{" "}
                          {presupuesto.subetapas_obra?.nombre}
                        </p>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "11px",
                            color: "#9ca3af",
                          }}
                        >
                          {presupuesto.conceptos_salida?.nombre}
                        </p>
                        {/* Info adicional: Gastado / Presupuestado y Saldo */}
                        <div style={{ fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                          {console.log('DEBUG presupuesto en modal:', presupuesto.nombre, Object.keys(presupuesto))}
                          <div>Presupuestado: ${(presupuesto.total_actualizado_ars || presupuesto.monto_actualizado || presupuesto.monto_presupuestado || 0).toLocaleString()}</div>
                          <div>Gastado: ${(presupuesto.pagado_ars || presupuesto.total_pagado || presupuesto.total_gastado || 0).toLocaleString()}</div>
                          <div>Saldo: ${(presupuesto.saldo_ars || presupuesto.saldo_pendiente || 0).toLocaleString()}</div>
                        </div>
                      </div>

                      <div
                        style={{
                          textAlign: "right",
                          marginLeft: "12px",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "14px",
                            fontWeight: "700",
                            color: "#2563eb",
                          }}
                        >
                          {formatearMoneda(
                            convertirMonto(
                              presupuesto.monto_actualizado ||
                                presupuesto.monto_presupuestado,
                              presupuesto.moneda,
                              monedaVista,
                              presupuesto.tipo_cambio
                            ),
                            monedaVista
                          )}
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "10px",
                            color:
                              presupuesto.porcentaje_ejecutado > 80
                                ? "#ef4444"
                                : "#64748b",
                          }}
                        >
                          {presupuesto.porcentaje_ejecutado.toFixed(1)}%
                          ejecutado
                        </p>
                      </div>
                    </div>

                    {/* Barra de progreso mini */}
                    <div
                      style={{
                        background: "#f1f5f9",
                        height: "4px",
                        borderRadius: "2px",
                        overflow: "hidden",
                        marginBottom: "12px",
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          background:
                            presupuesto.porcentaje_ejecutado > 80
                              ? "#ef4444"
                              : "#2563eb",
                          width: `${Math.min(
                            presupuesto.porcentaje_ejecutado,
                            100
                          )}%`,
                          transition: "width 0.3s ease",
                        }}
                      ></div>
                    </div>

                    {/* Botón de asignación manual */}
                    {selectedGasto && (
                      <button
                        onClick={() =>
                          handleAsignarGasto(selectedGasto, presupuesto.id)
                        }
                        style={{
                          width: "100%",
                          padding: "8px 12px",
                          background: "#2563eb",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "12px",
                          fontWeight: "600",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = "#1d4ed8";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = "#2563eb";
                        }}
                      >
                        <Link size={12} />
                        Asignar Gasto Seleccionado
                      </button>
                    )}

                    {dragOverPresupuesto === presupuesto.id && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "8px",
                          background: "rgba(37, 99, 235, 0.1)",
                          borderRadius: "8px",
                          marginTop: "8px",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            fontSize: "12px",
                            color: "#2563eb",
                            fontWeight: "600",
                          }}
                        >
                          ⬇️ Suelta aquí para asignar
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer con instrucciones */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "16px",
              fontSize: "12px",
              color: "#64748b",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  background: "#2563eb",
                  borderRadius: "2px",
                }}
              ></div>
              <span>Arrastra gastos aquí</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <div
                style={{
                  width: "12px",
                  height: "12px",
                  background: "#2563eb",
                  borderRadius: "2px",
                }}
              ></div>
              <span>Gasto seleccionado</span>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              background: "#1e293b",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#1e293b";
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
// ACTUALIZACION: Modal de información para Presupuesto Inicial
function InfoModalPresupuestoInicial({
  onClose,
  proyectoSeleccionado,
  datosHonorarios,
  datosManoObra,
  datosLlaveEnMano,
  presupuestosIniciales,
  isMobile,
}) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <FileText
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              Presupuesto Inicial
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              Valores al momento de la cotización original
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Explicación principal */}
            <div
              style={{
                background: "#f0f9ff",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #bfdbfe",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={16} color="#2563eb" />
                ¿Qué incluye este presupuesto?
              </h3>
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  color: "#64748b",
                  lineHeight: "1.5",
                }}
              >
                Este presupuesto muestra los valores originales al momento de la
                cotización, usando los tipos de cambio vigentes en ese momento.
              </p>

              <div style={{ display: "grid", gap: "8px", fontSize: "13px" }}>
                {datosHonorarios && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        background: "#2563eb",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <span style={{ color: "#64748b" }}>
                      <strong>Honorarios:</strong> $
                      {proyectoSeleccionado.honorarios?.toLocaleString()}{" "}
                      {proyectoSeleccionado.moneda_honorarios}
                      (TC: ${proyectoSeleccionado.tipo_cambio?.toLocaleString()}
                      )
                    </span>
                  </div>
                )}

                {datosManoObra && proyectoSeleccionado.mano_obra_separada && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        background: "#2563eb",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <span style={{ color: "#64748b" }}>
                      <strong>Mano de Obra:</strong> $
                      {proyectoSeleccionado.monto_mano_obra?.toLocaleString()}{" "}
                      {proyectoSeleccionado.moneda_mano_obra}
                      (TC: ${proyectoSeleccionado.tipo_cambio?.toLocaleString()}
                      )
                    </span>
                  </div>
                )}

                {datosLlaveEnMano && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        background: "#2563eb",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <span style={{ color: "#64748b" }}>
                      <strong>Costo Total Obra:</strong> $
                      {proyectoSeleccionado.monto_total_llave_en_mano?.toLocaleString()}{" "}
                      {proyectoSeleccionado.moneda_llave_en_mano}
                      (TC: ${proyectoSeleccionado.tipo_cambio?.toLocaleString()}
                      )
                    </span>
                  </div>
                )}

                {presupuestosIniciales && presupuestosIniciales.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "6px",
                        height: "6px",
                        background: "#2563eb",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <span style={{ color: "#64748b" }}>
                      <strong>Presupuestos de Obra:</strong>{" "}
                      {presupuestosIniciales.length} presupuestos manuales con
                      sus tipos de cambio originales
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Información sobre tipos de cambio */}
            <div
              style={{
                background: "#fffbeb",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #fed7aa",
              }}
            >
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <DollarSign size={14} />
                Tipos de Cambio Históricos
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#78350f",
                  lineHeight: "1.4",
                }}
              >
                Los valores mostrados respetan los tipos de cambio vigentes al
                momento de crear cada presupuesto, preservando la integridad
                histórica de la cotización.
              </p>
            </div>

            {/* Comparación con presupuesto actualizado */}
            <div
              style={{
                background: "#f0fdf4",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #bbf7d0",
              }}
            >
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <TrendingUp size={14} />
                Comparación con Valores Actuales
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#065f46",
                  lineHeight: "1.4",
                }}
              >
                El "Presupuesto Actualizado" muestra estos mismos valores
                ajustados por inflación (CAC) y tipos de cambio actuales,
                reflejando el valor real en pesos de hoy.
              </p>
            </div>

            {/* Desglose de presupuestos manuales */}
            {presupuestosIniciales && presupuestosIniciales.length > 0 && (
              <div>
                <h4
                  style={{
                    margin: "0 0 12px 0",
                    fontSize: "15px",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  Desglose de Presupuestos de Obra (
                  {presupuestosIniciales.length})
                </h4>

                <div style={{ display: "grid", gap: "8px" }}>
                  {presupuestosIniciales.slice(0, 5).map((presupuesto) => (
                    <div
                      key={presupuesto.id}
                      style={{
                        background: "#f8fafc",
                        padding: "12px",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            {presupuesto.nombre}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              color: "#64748b",
                            }}
                          >
                            {presupuesto.etapas_obra?.nombre} →{" "}
                            {presupuesto.subetapas_obra?.nombre}
                          </p>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <p
                            style={{
                              margin: "0 0 2px 0",
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#2563eb",
                            }}
                          >
                            ${presupuesto.monto_presupuestado?.toLocaleString()}{" "}
                            {presupuesto.moneda}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "10px",
                              color: "#64748b",
                            }}
                          >
                            TC: ${presupuesto.tipo_cambio?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}

                  {presupuestosIniciales.length > 5 && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "8px",
                        color: "#64748b",
                        fontSize: "12px",
                        fontStyle: "italic",
                      }}
                    >
                      ... y {presupuestosIniciales.length - 5} presupuestos más
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              background: "#1e293b",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#1e293b";
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
// ACTUALIZACION: Modal de información para Presupuesto Actualizado
function InfoModalPresupuestoActualizado({
  onClose,
  proyectoSeleccionado,
  datosHonorarios,
  datosManoObra,
  datosLlaveEnMano,
  presupuestosIniciales,
  cotizacionDolar,
  isMobile,
}) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <TrendingUp
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              Presupuesto Actualizado
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              Con indexaciones CAC y tipos de cambio actuales
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Explicación principal */}
            <div
              style={{
                background: "#f0fdf4",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #bbf7d0",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={16} color="#2563eb" />
                ¿Cómo se actualiza este presupuesto?
              </h3>
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "14px",
                  color: "#64748b",
                  lineHeight: "1.5",
                }}
              >
                Este presupuesto refleja el valor real actual de tu proyecto,
                aplicando diferentes sistemas de actualización según el tipo de
                costo:
              </p>

              <div style={{ display: "grid", gap: "12px", fontSize: "13px" }}>
                {/* Honorarios/MO con sistema de actualización */}
                {(datosHonorarios || datosManoObra || datosLlaveEnMano) && (
                  <div
                    style={{
                      background: "#ecfdf5",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #d1fae5",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#2563eb",
                      }}
                    >
                      🔄 Honorarios y Servicios Profesionales
                    </h4>

                    {datosHonorarios && (
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Honorarios:</strong>
                        {proyectoSeleccionado.honorarios_se_actualizan ? (
                          <span style={{ color: "#2563eb" }}>
                            {" "}
                            Sistema de actualización por{" "}
                            {proyectoSeleccionado.metodo_actualizacion_honorarios ||
                              "CAC"}{" "}
                            activo
                          </span>
                        ) : (
                          <span style={{ color: "#64748b" }}>
                            {" "}
                            Convertidos a TC actual ($
                            {cotizacionDolar?.toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}

                    {datosManoObra && (
                      <div style={{ marginBottom: "8px" }}>
                        <strong>Mano de Obra:</strong>
                        {proyectoSeleccionado.mano_obra_se_actualiza ? (
                          <span style={{ color: "#2563eb" }}>
                            {" "}
                            Sistema de actualización por{" "}
                            {proyectoSeleccionado.metodo_actualizacion_mano_obra ||
                              "CAC"}{" "}
                            activo
                          </span>
                        ) : (
                          <span style={{ color: "#64748b" }}>
                            {" "}
                            Convertidos a TC actual ($
                            {cotizacionDolar?.toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}

                    {datosLlaveEnMano && (
                      <div>
                        <strong>Costo Total Obra:</strong>
                        {proyectoSeleccionado.metodo_actualizacion ? (
                          <span style={{ color: "#2563eb" }}>
                            {" "}
                            Sistema de actualización por{" "}
                            {proyectoSeleccionado.metodo_actualizacion} activo
                          </span>
                        ) : (
                          <span style={{ color: "#64748b" }}>
                            {" "}
                            Convertidos a TC actual ($
                            {cotizacionDolar?.toLocaleString()})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Presupuestos manuales con CAC */}
                {presupuestosIniciales && presupuestosIniciales.length > 0 && (
                  <div
                    style={{
                      background: "#ecfdf5",
                      padding: "12px",
                      borderRadius: "8px",
                      border: "1px solid #d1fae5",
                    }}
                  >
                    <h4
                      style={{
                        margin: "0 0 8px 0",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#2563eb",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      ⚡ Presupuestos de Obra con CAC
                    </h4>
                    <p style={{ margin: "0 0 8px 0", color: "#064e3b" }}>
                      Los presupuestos en pesos argentinos se actualizan
                      automáticamente usando:
                    </p>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: "16px",
                        color: "#065f46",
                      }}
                    >
                      <li>Índice CAC General desde la fecha de creación</li>
                      <li>
                        Gastos descontados con CAC del mes correspondiente
                      </li>
                      <li>Saldo actualizado al último CAC disponible</li>
                    </ul>

                    {presupuestosIniciales.some((p) => p.usa_cac) && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "8px",
                          background: "#f0fdf4",
                          borderRadius: "6px",
                          fontSize: "12px",
                        }}
                      >
                        <strong>Estado actual:</strong>{" "}
                        {presupuestosIniciales.filter((p) => p.usa_cac).length}{" "}
                        de {presupuestosIniciales.length} presupuestos con CAC
                        activo
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Información sobre CAC */}
            <div
              style={{
                background: "#fefce8",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #fef08a",
              }}
            >
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                ⚡ ¿Qué es el CAC?
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#78350f",
                  lineHeight: "1.4",
                }}
              >
                El CAC (Coeficiente de Adecuación de Costos) es un índice
                oficial que refleja la variación de costos en la construcción,
                permitiendo mantener el valor real de los presupuestos ante la
                inflación.
              </p>
            </div>

            {/* Ventajas del sistema */}
            <div
              style={{
                background: "#f0f9ff",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #bfdbfe",
              }}
            >
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#1e40af",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <CheckCircle2 size={14} />
                Ventajas de este Sistema
              </h4>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: "16px",
                  fontSize: "13px",
                  color: "#1e3a8a",
                  lineHeight: "1.5",
                }}
              >
                <li>Protege tu inversión contra la inflación</li>
                <li>Mantiene precios justos para ambas partes</li>
                <li>Actualización automática y transparente</li>
                <li>Basado en índices oficiales reconocidos</li>
              </ul>
            </div>

            {/* Comparación con presupuesto inicial */}
            <div
              style={{
                background: "#f8fafc",
                padding: "16px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#64748b",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <ArrowUpDown size={14} />
                Diferencia con Presupuesto Inicial
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "13px",
                  color: "#475569",
                  lineHeight: "1.4",
                }}
              >
                La diferencia entre ambos presupuestos muestra el efecto de la
                inflación y las variaciones del tipo de cambio desde la
                cotización original hasta hoy.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              background: "#1e293b",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#1e293b";
            }}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
// Modal para ver información de un presupuesto específico
function PresupuestoInfoModal({
  presupuesto,
  onClose,
  formatearMoneda,
  isMobile,
  monedaVista,
  convertirMonto,
  gastosAsignados,
  datosHonorarios,
  datosManoObra,
  proyectoSeleccionado,
}) {
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("T")[0].split("-");
    return `${day}/${month}/${year}`;
  };

  // Detectar si es honorarios o mano de obra
  const esHonorarios = presupuesto.nombre?.toLowerCase().includes("honorario");
  const esManoObra =
    presupuesto.nombre?.toLowerCase().includes("mano de obra") ||
    presupuesto.nombre?.toLowerCase().includes("mano de obra");

  let totalGastadoConvertido = 0;
  let porcentajeEjecutado = 0;

  if (esHonorarios && datosHonorarios) {
    // Para honorarios, usar datos de entradas
    totalGastadoConvertido = convertirMonto(
      datosHonorarios.total_pagado || 0,
      datosHonorarios.moneda || "ARS",
      monedaVista,
      proyectoSeleccionado?.tipo_cambio
    );
    porcentajeEjecutado = datosHonorarios.porcentaje_pagado || 0;
  } else if (esManoObra && datosManoObra) {
    // Para mano de obra, usar datos de entradas
    totalGastadoConvertido = convertirMonto(
      datosManoObra.total_pagado || 0,
      datosManoObra.moneda || "ARS",
      monedaVista,
      proyectoSeleccionado?.tipo_cambio
    );
    porcentajeEjecutado = datosManoObra.porcentaje_pagado || 0;
  } else {
    // Para presupuestos normales, usar gastos asignados
    totalGastadoConvertido = gastosAsignados.reduce((sum, gasto) => {
      return (
        sum +
        convertirMonto(
          gasto.monto,
          gasto.moneda,
          presupuesto.moneda,
          gasto.tipo_cambio
        )
      );
    }, 0);

    porcentajeEjecutado =
      (presupuesto.monto_actualizado || presupuesto.monto_presupuestado) > 0
        ? (totalGastadoConvertido /
            (presupuesto.monto_actualizado ||
              presupuesto.monto_presupuestado)) *
          100
        : 0;
  }

  // Calcular el monto presupuestado correcto para el saldo
  let montoPresupuestado = 0;
  if (esHonorarios && datosHonorarios) {
    montoPresupuestado = convertirMonto(
      datosHonorarios.monto_inicial || 0,
      datosHonorarios.moneda || "ARS",
      monedaVista,
      proyectoSeleccionado?.tipo_cambio
    );
  } else if (esManoObra && datosManoObra) {
    montoPresupuestado = convertirMonto(
      datosManoObra.monto_inicial || 0,
      datosManoObra.moneda || "ARS",
      monedaVista,
      proyectoSeleccionado?.tipo_cambio
    );
  } else {
    montoPresupuestado = convertirMonto(
      presupuesto.monto_actualizado || presupuesto.monto_presupuestado,
      presupuesto.moneda,
      monedaVista,
      presupuesto.tipo_cambio
    );
  }

  // Calcular saldo correcto
  const saldo = montoPresupuestado - totalGastadoConvertido;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        padding: isMobile ? "10px 10px 80px 10px" : "20px",
      }}
    >
      <div
        style={{
          background: "white",
          borderRadius: "20px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "800px",
          maxHeight: isMobile ? "calc(100vh - 160px)" : "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.3)",
          animation: "scaleIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: getVistaColor("presupuestos-iniciales").gradient,
            color: "white",
            padding: isMobile ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 4px 0",
                fontSize: isMobile ? "18px" : "20px",
                fontWeight: "700",
              }}
            >
              <Eye
                size={20}
                style={{ verticalAlign: "text-bottom", marginRight: "8px" }}
              />
              {presupuesto.nombre}
            </h2>
            <p
              style={{
                margin: 0,
                fontSize: "13px",
                opacity: 0.9,
              }}
            >
              {presupuesto.etapas_obra?.nombre} →{" "}
              {presupuesto.subetapas_obra?.nombre}
            </p>
          </div>

          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              borderRadius: "8px",
              padding: "8px",
              color: "white",
              cursor: "pointer",
              transition: "background 0.2s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255,255,255,0.2)")
            }
          >
            <X size={20} />
          </button>
        </div>

        {/* Contenido */}
        <div
          style={{
            flex: 1,
            padding: isMobile ? "20px" : "24px",
            overflowY: "auto",
          }}
        >
          <div style={{ display: "grid", gap: "24px" }}>
            {/* Información general */}
            <div
              style={{
                background: "#f8fafc",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #e2e8f0",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Info size={16} />
                Información del Presupuesto
              </h3>

              <div style={{ display: "grid", gap: "12px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Concepto:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#1e293b",
                      fontWeight: "600",
                    }}
                  >
                    {presupuesto.conceptos_salida?.nombre}
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Monto Original:
                  </span>
                  <span
                    style={{
                      fontSize: "16px",
                      color: "#3b82f6",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(
                      presupuesto.monto_presupuestado,
                      presupuesto.moneda
                    )}
                  </span>
                </div>

                {presupuesto.usa_cac && presupuesto.monto_actualizado && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Monto Actualizado:
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "16px",
                          color: "#10b981",
                          fontWeight: "700",
                        }}
                      >
                        {formatearMoneda(
                          presupuesto.monto_actualizado,
                          presupuesto.moneda
                        )}
                      </span>
                      <span
                        style={{
                          background: "#f0fdf4",
                          color: "#059669",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "10px",
                          fontWeight: "600",
                          border: "1px solid #bbf7d0",
                        }}
                      >
                        CAC
                      </span>
                    </div>
                  </div>
                )}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      color: "#64748b",
                      fontWeight: "500",
                    }}
                  >
                    Estado:
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      color:
                        presupuesto.estado === "cerrado"
                          ? "#059669"
                          : "#f59e0b",
                      fontWeight: "600",
                    }}
                  >
                    {presupuesto.estado === "cerrado" ? "Cerrado" : "Abierto"}
                  </span>
                </div>

                {presupuesto.descripcion && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr",
                      gap: "8px",
                      alignItems: "flex-start",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#64748b",
                        fontWeight: "500",
                      }}
                    >
                      Descripción:
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#1e293b",
                        lineHeight: "1.5",
                      }}
                    >
                      {presupuesto.descripcion}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Información financiera */}
            <div
              style={{
                background: "#f0fdf4",
                padding: "20px",
                borderRadius: "12px",
                border: "1px solid #bbf7d0",
              }}
            >
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Calculator size={16} />
                Resumen Financiero
                {esHonorarios &&
                  datosHonorarios &&
                  proyectoSeleccionado?.tipo_obra === "administracion" && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontWeight: "400",
                      }}
                    >
                      {proyectoSeleccionado.moneda_honorarios === "USD"
                        ? " (Presupuesto pactado en USD)"
                        : proyectoSeleccionado.moneda_honorarios === "ARS"
                        ? " (Presupuesto pactado en ARS)"
                        : ""}
                    </span>
                  )}
                {esManoObra &&
                  datosManoObra &&
                  proyectoSeleccionado?.tipo_obra === "administracion" && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#64748b",
                        fontWeight: "400",
                      }}
                    >
                      {proyectoSeleccionado.moneda_mano_obra === "USD"
                        ? " (Presupuesto pactado en USD)"
                        : proyectoSeleccionado.moneda_mano_obra === "ARS"
                        ? " (Presupuesto pactado en ARS)"
                        : ""}
                    </span>
                  )}
              </h3>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      color: "#059669",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Presupuestado
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      color: "#059669",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(montoPresupuestado, monedaVista)}
                  </p>
                </div>

                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      color: "#ef4444",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Ejecutado
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      color: "#ef4444",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(totalGastadoConvertido, monedaVista)}
                  </p>
                </div>

                <div style={{ textAlign: "center" }}>
                  <p
                    style={{
                      margin: "0 0 4px 0",
                      fontSize: "12px",
                      color: "#f59e0b",
                      fontWeight: "600",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    Saldo
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "18px",
                      color: "#f59e0b",
                      fontWeight: "700",
                    }}
                  >
                    {formatearMoneda(saldo, monedaVista)}
                  </p>
                </div>
              </div>

              {/* Barra de progreso */}
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#1e293b",
                    }}
                  >
                    Progreso
                  </span>
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: porcentajeEjecutado > 80 ? "#ef4444" : "#059669",
                    }}
                  >
                    {porcentajeEjecutado.toFixed(1)}%
                  </span>
                </div>
                <div
                  style={{
                    background: "#e5e7eb",
                    height: "12px",
                    borderRadius: "6px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      background:
                        porcentajeEjecutado > 100
                          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                          : porcentajeEjecutado > 80
                          ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
                          : "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      width: `${Math.min(porcentajeEjecutado, 100)}%`,
                      borderRadius: "6px",
                      transition: "width 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
                      position: "relative",
                    }}
                  >
                    {/* Efecto de brillo */}
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "50%",
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.3) 0%, transparent 100%)",
                        borderRadius: "6px 6px 0 0",
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Información CAC si aplica */}
              {presupuesto.usa_cac && (
                <div
                  style={{
                    background: "#ecfdf5",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #d1fae5",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#059669",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    ⚡ Actualización CAC Activa
                  </h4>
                  <div style={{ fontSize: "12px", color: "#065f46" }}>
                    {presupuesto.cac_creacion && (
                      <p style={{ margin: "0 0 4px 0" }}>
                        <strong>CAC Inicial:</strong>{" "}
                        {presupuesto.cac_creacion.toFixed(2)}
                      </p>
                    )}
                    {presupuesto.cac_actual && (
                      <p style={{ margin: "0 0 4px 0" }}>
                        <strong>CAC Actual:</strong>{" "}
                        {presupuesto.cac_actual.toFixed(2)}
                      </p>
                    )}
                    {presupuesto.observaciones_cac && (
                      <p style={{ margin: 0, fontStyle: "italic" }}>
                        {presupuesto.observaciones_cac}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            {/* Lista de gastos */}
            <div>
              <h3
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Receipt size={16} />
                Gastos Asignados ({gastosAsignados.length})
              </h3>

              {gastosAsignados.length === 0 ? (
                <div
                  style={{
                    background: "#f8fafc",
                    padding: "40px 20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    border: "2px dashed #cbd5e1",
                  }}
                >
                  <div
                    style={{
                      width: "48px",
                      height: "48px",
                      background: "#f1f5f9",
                      borderRadius: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}
                  >
                    <Package size={24} color="#64748b" />
                  </div>
                  <h4
                    style={{
                      margin: "0 0 8px 0",
                      color: "#1e293b",
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    No hay gastos asignados
                  </h4>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "14px",
                      color: "#64748b",
                    }}
                  >
                    Aún no se han registrado gastos para este presupuesto
                  </p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: "8px" }}>
                  {gastosAsignados.map((gasto) => (
                    <div
                      key={gasto.id}
                      style={{
                        background: "white",
                        border: "1px solid #e2e8f0",
                        borderRadius: "12px",
                        padding: "16px",
                        transition: "all 0.2s ease",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "8px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "14px",
                              fontWeight: "600",
                              color: "#1e293b",
                            }}
                          >
                            {gasto.conceptos_salida?.nombre}
                          </p>
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "12px",
                              color: "#64748b",
                            }}
                          >
                            {gasto.proveedores_servicios?.nombre}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              color: "#9ca3af",
                            }}
                          >
                            {formatDateForDisplay(gasto.fecha)}
                          </p>
                        </div>

                        <div
                          style={{
                            textAlign: "right",
                            marginLeft: "16px",
                          }}
                        >
                          <p
                            style={{
                              margin: "0 0 4px 0",
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#ef4444",
                            }}
                          >
                            {formatearMoneda(
                              convertirMonto(
                                gasto.monto,
                                gasto.moneda,
                                monedaVista,
                                gasto.tipo_cambio
                              ),
                              monedaVista
                            )}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "10px",
                              color: "#64748b",
                            }}
                          >
                            Original: {gasto.moneda} $
                            {parseFloat(gasto.monto).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {gasto.descripcion && (
                        <p
                          style={{
                            margin: "12px 0 0 0",
                            fontSize: "12px",
                            color: "#64748b",
                            fontStyle: "italic",
                            borderTop: "1px solid #f1f5f9",
                            paddingTop: "8px",
                          }}
                        >
                          {gasto.descripcion}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#f8fafc",
            padding: "16px 24px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              background: "#1e293b",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "#0f172a";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#1e293b";
            }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

export default PresupuestosIniciales;